import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AssessmentIcon from "@mui/icons-material/Assessment";
import EmailIcon from "@mui/icons-material/Email";
import HistoryIcon from "@mui/icons-material/History";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import { useTranslation } from "i18n-fe";
import { TableSkeleton, useActiveShow, RideStatusBadge, SignatureBox, ScoreEntry } from "common-fe";
// #1805 / bug #159 — activeSheet.completedAt is a genuine server instant
// (`new Date().toISOString()`, the sheet-completion timestamp), not an FVD
// Access stamp, so per the #1811 split it belongs on the show's real
// venueTimezone. Previously rendered with a bare `.toLocaleString()` (no
// timeZone at all), which showed the VIEWER's browser-local time -- the
// reported bug (Screenshot 2026-08-23 at 10.41.57 AM: "Completed
// 8/23/2026, 9:24:54 AM" was the reporter's own device time, not the
// venue's). RideDetail already has `useActiveShow()` available (see
// RidesList above), so this threads the show's real `venueTimezone`
// through `resolveVenueTimeZone`, same pattern #1813 used for
// announcer-fe's formatVenueClock -- resolveVenueTimeZone itself falls
// back to DEFAULT_VENUE_TIMEZONE (Pacific) when the show has none set yet,
// so AC5's no-venueTimezone fallback is unchanged.
import { resolveVenueTimeZone } from "common-fe/venueTime";
import { useRideStatuses } from "../hooks/useRideStatuses";
import { useSheetSignature } from "../hooks/useSheetSignature";
import { useResendScoreSheet } from "../hooks/useResendScoreSheet";
import { useScorePageLink } from "../hooks/useScorePageLink";
import { usePrintScoreSheet } from "../hooks/usePrintScoreSheet";
import { useScoreSheetStatus } from "../hooks/useScoreSheetStatus";
import { useShowRides } from "../hooks/useShowRides";
import { useSortedRows } from "../hooks/useSortedRows";
import { useCatalogDefs } from "../hooks/useCatalogDefs";
import {
  DERIVED_RIDE_STATUSES,
  RIDE_STATUSES,
  scoreStepFor,
  isPlaceholderEntityName,
  formatPercentage,
  formatPoints,
  missingJudgeSectionsForClass,
  // #1755 — same shared section-header map the scribe's ScoringForm uses,
  // so a freestyle/young-horse test groups identically on both surfaces.
  FREESTYLE_SECTION_LABEL_KEY,
  // #1756 - the error-of-course rule machinery, imported rather than
  // re-implemented here: the exact functions scribe-fe's ScoringForm calls,
  // so the same Nth error on the same sheet resolves to the same deduction
  // on either surface. That it did not was the defect (#1068 let an admin
  // type the amount, so the deduction depended on who entered it).
  resolveErrorTiers,
  errorTierForOccurrence,
  nextErrorOccurrence,
  retierErrorOccurrences,
  ordinal,
  ELIMINATION_REASONS,
  ERROR_AMOUNT_SOURCES,
  // #2015 — the platform's own coefficient-weighted subtotal functions
  // (score-common owns the arithmetic, #1628): reading these at display
  // time is the whole point of the card, so the movements/collective
  // subtotal rows below never reimplement a summing helper locally.
  weightedMovementTotal,
  weightedCollectiveTotal,
  // #2018 — the ONE shared #1342/#1343 position resolver: order + judge-
  // roster-id matching, so this file never re-derives a class's panel or
  // parses a judgeAssignments name itself (score-common's calculation.js).
  // Cited by symbol, not line: this branch's base and dev disagree on that
  // file's line numbers. round3 backs the local cleanSectionPct mirror
  // below (#998).
  resolveClassPanel,
  // #2410 — the same finality signals ride-status derivation uses, so the
  // "create missing score sheets" affordance's suppression gate (a
  // terminal ride) and expected-panel math never diverge from what
  // deriveRideStatus itself already computes elsewhere in this file.
  deriveRideStatus,
  expectedJudgeCountForClass,
  expectedSectionCountForClass,
  // #2423 — the single predicate (score-common's calculation.js) that tells
  // handleCompleteAndSign below whether THIS sheet's Test is the #1748
  // panel-consensus shape, so the panel-signing ceremony (#1751) only ever
  // runs for that shape and never for an ordinary sheet (AC3).
  isPanelConsensusTest,
  round3,
  // #2034 — the shared home for cleanSectionPct/sheetSection/isSectionJudged,
  // consolidated out of this file's own #2018 mirror and report-defs'
  // competitor-score-sheet.js (the original). See score-common's
  // utils/section-scoring.js doc comment for what stayed local (the
  // qualityJudging.topology class-field OR-fallback below) and why.
  cleanSectionPct,
  sheetSection as sharedSheetSection,
  isSectionJudged as sharedIsSectionJudged,
  // #2013 — the same cap score-common's zod schema and score-api's route
  // enforce on annotation text (packages/score-common/src/constants.js),
  // mirrored here so the input's own maxLength/counter never disagree with
  // what the server will actually accept.
  ANNOTATION_TEXT_MAX,
} from "score-common";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { formatClassStartTime, getRideTestName, formatVenueLocalDateTime } from "../util/classDisplay";
import { RIDE_VIEW_MODES, getRideViewMode, setRideViewMode, formatSimpleMovementText } from "../util/rideViewMode";
import { UTILITY_EVENT_TYPE_BY_KEY } from "../util/utilityEventTypes";
import { formatPlacing } from "../util/placingDisplay";
import { classifyTestFetchError, testDefAlertProps } from "../util/testFetchError";
import SheetStateChip, { deriveSheetState } from "../components/SheetStateChip";
import SheetSentChip from "../components/SheetSentChip";
import SheetAuditDialog from "../components/SheetAuditDialog";
import NumericOnlyScoreGrid from "../components/NumericOnlyScoreGrid";
import RideLink from "../components/RideLink";
import ClassLink from "../components/ClassLink";
import { useDocumentTitle } from "../hooks/useDocumentTitle";

// #508 — purpose-built ADMIN ride/scoresheet editor. Deliberately NOT a reuse
// of scribe-fe's ScoringForm (offline-first, per-judge locked session, #422
// outbox sync): this is a simpler online-only tool for score-admin to review
// every judge's sheet on a Ride, correct marks, force-complete/sign, force a
// Ride to FINAL, and scratch/un-scratch — all straight against score-api.

// #1756 - ELIMINATION_REASONS used to be hand-mirrored here from scribe-fe's
// ScoringForm (#208/#753). It now comes from score-common so the two menus
// cannot drift apart; the custom-reason fallback below is unchanged.

// #846 — report-api's admin-sheets.js resend route's error codes, mapped to
// curated, static i18n keys (never render a raw backend code/message; see
// common-fe's installServerErrorInterceptor — it stashes the real detail on
// err.response.data but rewrites err.message to a generic "Server Error").
const RESEND_ERROR_KEYS = {
  validation_error: "rideEditor.resendInvalidEmail",
  no_email: "rideEditor.resendNoEmail",
};

// #2413 — usePrintScoreSheet.js resolves a failure to one of these fixed
// "kinds" (never a raw error); same curated-static-key convention as
// RESEND_ERROR_KEYS above.
const PRINT_ERROR_KEYS = {
  timeout: "rideEditor.printReportTimeout",
  failed: "rideEditor.printReportError",
  network: "rideEditor.printReportError",
};

// #2013 FE-judge fold-in note 2 — same curated-static-key convention as
// RESEND_ERROR_KEYS above: never render a raw backend/axios message. Codes
// per score-api's addScoreSheetAnnotation (apps/score-api/src/handlers/
// scoresheet.js): text_required (400) and not_assigned (403); anything else
// (404, an unexpected 409 after the one retry, a network error) falls back
// to the generic key.
const ANNOTATION_ERROR_KEYS = {
  text_required: "rideEditor.annotationErrorTextRequired",
  not_assigned: "rideEditor.annotationErrorNotAssigned",
};

// score-api owns these on a ScoreSheet — never resend them on a save.
// #850 — judgeSignature is stripped here too now that RideEditor no longer
// captures a per-sheet drawing (card #733's show-wide model, adopted from
// scribe-fe's ScoringForm): handleCompleteAndSign below sends toBody() as-is
// and completeScoreSheet resolves the judge's stored show-wide pointer
// server-side (offloadSignatureOnComplete's USE-AS-IS branch,
// apps/score-api/src/lib/signatures.js). Sending an inline judgeSignature
// would instead hit the REPLACE branch and overwrite that judge's org-wide
// current signature — exactly the bug this card fixes.
const SERVER_MANAGED = [
  "id",
  "orgId",
  "createdAt",
  "updatedAt",
  "totalPoints",
  "maxPoints",
  "percentage",
  "signature",
  "judgeSignature",
];

// #1754 / equitation-class-sheet-design.md §11 — for a "judge-percentage"
// def (the class-grid layout, e.g. USEF DSE/DSM), there are no movements to
// derive a total from: the judge's entered percentage IS the score of
// record (DR133.1.j), so percentage is client-writable for this model ONLY.
// Every other def keeps the SERVER_MANAGED strip above (percentage is
// server-computed from marks there, and an admin-typed value would desync
// the sheet from its own arithmetic). Mirrors scribe-fe's
// CLIENT_WRITABLE_TOTAL_SCORING_MODEL / scoreSheetIsClientTotal
// (apps/scribe-fe/src/hooks/useScoreSheet.js, #1750).
const JUDGE_PERCENTAGE_SCORING_MODEL = "judge-percentage";
function isJudgePercentageTest(test) {
  return test?.scoringModel === JUDGE_PERCENTAGE_SCORING_MODEL;
}

// updateScoreSheet REPLACES the whole ScoreSheet server-side (no merge), so
// every save must resend the FULL sheet — INCLUDING completedAt when it's
// already set — or a previously-completed sheet silently reverts the Ride's
// result from FINAL to PARTIAL (see results.js buildRideResult). Only the
// keys score-api itself computes/manages are stripped. `test` is the
// resolved Test for this sheet (may be null while still loading) — passed
// so percentage can be let through for the one scoringModel that owns it
// client-side (see isJudgePercentageTest above).
function toBody(sheet, test) {
  const clientWritableTotal = isJudgePercentageTest(test);
  const body = {};
  for (const [k, v] of Object.entries(sheet)) {
    if (k === "percentage" && clientWritableTotal) {
      if (v !== undefined) body[k] = v;
      continue;
    }
    if (SERVER_MANAGED.includes(k)) continue;
    if (v !== undefined) body[k] = v;
  }
  return body;
}

// #2013 AC2 — annotation rendering helpers. `movementNumber` on a stored
// annotation may be null (a deliberate sheet-general note, score-common's
// scoreSheetAnnotationSchema) or a number that doesn't match any movement on
// THIS test (addScoreSheetAnnotation, score-api, validates it against
// nothing) -- neither case is rejected server-side. An annotation that only
// ever rendered by joining onto the movement rows drawn below would make
// both of those vanish silently, which is exactly the trap the judge who
// reviewed the backend flagged. So annotationsForMovement only ever returns
// an EXACT numeric match (rendered inline on that movement's row), and
// unmatchedAnnotations is everything else -- rendered in the general
// Annotations panel so nothing recorded on the sheet is ever invisible.
function annotationsForMovement(sheet, movementNumber) {
  return (sheet?.annotations ?? []).filter((a) => a.movementNumber === movementNumber);
}

// `renderedMovements` must be the SAME list the movements table actually
// draws (movementsForSheet's output), not test.movements unfiltered -- on a
// two-judge (young-horse) class movementsForSheet filters to one judge's
// section, and a movement outside that section never gets an inline row on
// this tab. Building the exclusion set from the full test would then also
// exclude it from this panel, so a quality-section annotation viewed from
// the C/technical tab would be invisible everywhere (found by the #2013 FE
// judge review).
function unmatchedAnnotations(sheet, renderedMovements) {
  const knownNumbers = new Set((renderedMovements ?? []).map((m) => m.movementNumber));
  return (sheet?.annotations ?? []).filter(
    (a) => a.movementNumber === null || a.movementNumber === undefined || !knownNumbers.has(a.movementNumber)
  );
}

// #1067/#1068 — the fields an admin can actually change on a CONFIRMED/SIGNED
// sheet through this screen: marks, their remarks, the collective marks, the
// general remarks, eliminations, and (#1068) errors-of-course/deductions.
// This is a plain allowlist mirror of score-api's draftFieldsSchema
// (apps/score-api/src/handlers/scoresheet.js, DRAFT_EXCLUDED_FIELDS) — the
// server independently enforces the same boundary (never
// rideId/judgeId/testId/judgeSignature/any computed or freeze-marker field),
// this is just what the FE ever bothers to send. #1068 deliberately leaves
// scribe-only workflow affordances (the review flag, the bell timer / entryTimeSeconds)
// out of this list — see docs/score/admin-scoresheet-editing.md §4.7.
// #1295 — the three annotation-override pairs (sheet-level
// annotatedPurpose/annotatedIntroduce, per-movement
// annotatedDescription/annotatedDirective, per-collective
// annotatedName/annotatedDirective) were removed from this editor: nothing
// downstream rendered them (see the card). The per-movement/per-collective
// pairs never had their own DRAFT_FIELD_KEYS entries (they rode inside the
// already-allowed movementScores/collectives containers below); with their
// only UI call sites gone, this editor no longer has any path that writes
// them.
const DRAFT_FIELD_KEYS = [
  "movementScores",
  "collectives",
  "generalRemarks",
  "eliminations",
  "errors",
];

// #1754 — the two class-grid-only draft fields, admitted ONLY for a
// judge-percentage sheet, same gate as isJudgePercentageTest above. Mirrors
// score-api's JUDGE_PERCENTAGE_DRAFT_FIELDS (apps/score-api/src/handlers/
// scoresheet.js) — the server independently enforces the same boundary,
// this is just what the FE ever bothers to send. Kept as a SEPARATE list
// rather than folded into DRAFT_FIELD_KEYS so an ordinary sheet's draft
// push can never carry them even if a future edit forgets to gate the
// caller — draftFieldKeysFor is the one place the two lists combine.
const JUDGE_PERCENTAGE_DRAFT_FIELD_KEYS = ["percentage", "observationMarks"];
function draftFieldKeysFor(test) {
  return isJudgePercentageTest(test)
    ? [...DRAFT_FIELD_KEYS, ...JUDGE_PERCENTAGE_DRAFT_FIELD_KEYS]
    : DRAFT_FIELD_KEYS;
}

// common-fe's installServerErrorInterceptor rewrites every failed API call's
// err.message to the hardcoded English "Server Error" string before it ever
// reaches this component (see scoreApi.js) - on EVERY rejection, including a
// no-response/offline one, not just an HTTP error with err.response. So
// `err.message` is never useful in any branch: `draftSyncError.message ||
// t(key)` would always pick the generic English string and the localized
// copy would be dead code. Always prefer the localized string.
function apiErrorMessage(err, t, key) {
  return t(key);
}

function personName(person) {
  if (!person) return null;
  return [person.firstName, person.lastName].filter(Boolean).join(" ") || null;
}

// judgeAssignments: { [position]: [judgeId, ...] } -> judgeId -> position.
// Display-only mirror of score-api's results.js invertJudgeAssignments.
function judgePosition(klass, judgeId) {
  const assignments = klass?.judgeAssignments;
  if (!assignments) return null;
  for (const [position, ids] of Object.entries(assignments)) {
    if ((ids || []).includes(judgeId)) return position;
  }
  return null;
}

// Arena display order for judge booths. In dressage this is ALWAYS
// E, H, C, M, B (left to right around the arena), and it is NOT
// score-common's JUDGE_POSITIONS (C, E, M, B, H), which is the printed
// sheet's signature-line order. packages/report-defs/src/score/final-class.js
// and results-summary-day.js already carry this same constant for the
// printed reports; this is the screen's copy of it.
const DISPLAY_POSITION_ORDER = ["E", "H", "C", "M", "B"];

// Score sheets arrive from listScoreSheets in the API's own (DynamoDB query)
// order, which is NOT stable: it can change between loads and after a sheet
// is written. Every consumer of `sheets` here renders columns/tabs in array
// order, so an unsorted array made the judge columns swap places, sometimes
// mid-class, in the paper-scoring grid. Sort once at ingestion so every
// consumer is fixed by one change. Unknown/unassigned positions sort after
// the five known booths, by sheet id, so the result is fully deterministic.
export function sortSheetsByDisplayPosition(klass, sheetList) {
  // Resolve a sheet's booth letter EXACTLY the way every render site in this
  // file and in NumericOnlyScoreGrid.jsx already does: judgeAssignments
  // first, then the sheet's own judgeId. That fallback is not a safety net
  // here, it is the normal case -- a ScoreSheet's judgeId IS the position
  // code (see missingPanelEntries' comment below, useScoreSheet.js, and the
  // rideEditorNumericGrid fixture), so a paper ride's sheets carry "B"/"C"
  // and resolve no position out of judgeAssignments at all. Ranking on
  // judgePosition() alone therefore scored EVERY sheet "unknown" and fell
  // through to the id tiebreak, which is alphabetical -- which is precisely
  // how "Judge B" kept rendering to the left of "Judge C".
  const rank = (s) => {
    const position = judgePosition(klass, s?.judgeId) || s?.judgeId || null;
    const idx = DISPLAY_POSITION_ORDER.indexOf(position);
    return idx === -1 ? DISPLAY_POSITION_ORDER.length : idx;
  };
  return [...(sheetList || [])].sort(
    (a, b) => rank(a) - rank(b) || String(a?.id ?? "").localeCompare(String(b?.id ?? ""))
  );
}

// #2018 — "J. Smith" from a score-judge roster row's own firstName/lastName
// (apps/score-api/src/handlers/daysheet.js:387 confirms those fields live on
// the JUDGE row itself, not a separate Person). No lastName (missing/foreign
// row) returns null rather than a bare initial, so AC5's "no stray initial"
// is enforced right here, not by every caller remembering to check.
function judgeInitialLastName(judgeRow) {
  const last = (judgeRow?.lastName || "").trim();
  if (!last) return null;
  const first = (judgeRow?.firstName || "").trim();
  return first ? `${first.charAt(0).toUpperCase()}. ${last}` : last;
}

// #2018 AC4 — a mapped composite position must show BOTH judges, never only
// the primary. Reads only what the #1342/#1343 shared resolver
// (resolveJudgeComposite -> resolveCompositeJudge) returned; this never
// re-derives compositeness or a panel guess locally. `resolved` is null when
// the judge id couldn't be resolved at all (AC5 — degrades to no names,
// never a blank/partial entry).
function judgeDisplayNames(resolved) {
  if (!resolved) return [];
  const rows = resolved.isComposite ? [resolved.primary, resolved.secondary] : [resolved.primary];
  return rows.map(judgeInitialLastName).filter(Boolean);
}

// #2018 — split a "First Last" display name (resolveClassPanel's judgeName,
// sourced straight from Class.judgeAssignments) into the {firstName,
// lastName} shape judgeInitialLastName already expects, so the ORDINARY
// (non-composite) case prints "J. Smith" straight from the panel with no
// roster row and no resolveJudgeComposite call at all — that call is only
// spent on genuine roster ids that might be composite (BLOCKING 1). A
// multi-word first/middle name keeps everything but the last token as
// firstName; only the first token's initial ever prints, so that grouping
// never changes the output.
function splitDisplayName(fullName) {
  const trimmed = (fullName || "").trim();
  if (!trimmed) return null;
  const parts = trimmed.split(/\s+/);
  const lastName = parts.pop();
  const firstName = parts.join(" ");
  return { firstName, lastName };
}

// #2018 — the name(s) to print for one panel position: BOTH judges for a
// mapped composite (AC4, resolveJudgeComposite says isComposite for the
// panel's resolved roster id), else the single name resolveClassPanel
// already carries, formatted first-initial + last (AC1). No panel entry
// (position unassigned/foreign to this class, AC5) or a judgeName with no
// last token returns [] — judgeInitialLastName's own null covers the
// latter, so there is exactly one place a stray initial could leak and it
// already refuses to produce one.
function judgeNamesForPanelEntry(panelEntry, judgeResolutions) {
  if (!panelEntry) return [];
  const resolved = panelEntry.judgeId ? judgeResolutions[panelEntry.judgeId] : null;
  if (resolved?.isComposite) return judgeDisplayNames(resolved);
  const name = judgeInitialLastName(splitDisplayName(panelEntry.judgeName));
  return name ? [name] : [];
}

// #998/#2018/#2034 — cleanSectionPct now lives in score-common
// (utils/section-scoring.js) as the ONE canonical copy, imported above. It
// used to be a byte-for-byte local mirror of report-defs' cleanSectionPct;
// see that module's doc comment for the full history.

// #2018/#2034 AC3 — which section a sheet ACTUALLY scored. The majority-vote
// logic itself now lives in score-common (sharedSheetSection); this file
// supplies its own movement list (tests?.[s.testId]?.movements — this admin
// view does not resolve movementOverride, unlike report-defs; see that
// module's doc comment) the way that function's doc comment expects.
function sheetSectionFromScores(test, sheet) {
  return sharedSheetSection(test?.movements, sheet);
}

// #2018/#2034 AC3 — is this ride the section-judged (young-horse two-judge)
// shape the printed scoresheet renders as ONE merged score column?
//
// This deliberately does NOT gate on Class.qualityJudging alone. report-defs
// hit exactly that bug and abandoned the equivalent class-field gate: its
// isSectionJudged was originally `expectedSectionCountForClass(cls) === 2`,
// i.e. Class.sectionJudgeAssignments, and the comment at
// competitor-score-sheet.js:459-483 records why that failed — that field is a
// SEPARATE, operator-entered assignment whose real job is gating which scribe
// booth scores which section, and "operators routinely leave it unset on a
// fully-scored class" (CL 357EF, the 7YO final). Class.qualityJudging is the
// same kind of operator-entered field (hand-set from the quality-judging panel
// in apps/scoreadmin-fe/src/components/ClassDetailDrawer.jsx:459-534, and
// apps/scribe-fe/src/mocks/classes.js:156-162 documents a real young-horse
// class that "deliberately leaves qualityJudging unset" while still being
// section-judged), so gating this on it alone would print the ERROR-DEDUCTED number
// here while the scoresheet printed the CLEAN one — precisely what AC3
// forbids.
//
// #2034 — the "both sections actually scored" half of this gate now lives in
// score-common (sharedIsSectionJudged); the class-field OR fallback stays
// here on purpose (see section-scoring.js's doc comment): report-defs ORs a
// DIFFERENT class field (expectedSectionCountForClass /
// Class.sectionJudgeAssignments), so unifying the two fallbacks would be a
// real behavior change, not a de-duplication.
function isSectionJudgedRide(klass, tests, sheets) {
  const list = sheets || [];
  const hasSections = list.some((s) => (tests?.[s.testId]?.movements ?? []).some((m) => m.section));
  if (!hasSections) return false;
  const scored = new Set(
    list.map((s) => sheetSectionFromScores(tests?.[s.testId], s)).filter(Boolean)
  );
  if (sharedIsSectionJudged(scored)) return true;
  return klass?.qualityJudging?.topology === "two-judge";
}

// #998/#2018 AC3 — the percentage to print for a sheet's own judge line.
// A section-judged (young-horse two-judge) sheet's STORED sheet.percentage
// already has that section's error of course subtracted (#878), but the
// printed scoresheet's per-judge line shows the CLEAN section percentage
// instead (competitor-score-sheet.js:641-660) — the error renders as its
// own separate step there, not folded into this number. Reprinting the
// deducted sheet.percentage here would show a number the scoresheet does
// not. Every other class shape (no section split) has no such override:
// sheet.percentage IS what prints.
//
// Which section this sheet is comes from its OWN movementScores first
// (sheetSectionFromScores, the report-defs signal); sectionForPosition is only
// a fallback for a sheet with no marks entered yet, where there is nothing to
// derive from.
function displaySheetPercentage(klass, tests, sheets, sheet) {
  if (!sheet) return null;
  if (!isSectionJudgedRide(klass, tests, sheets)) return sheet.percentage ?? null;
  const position = judgePosition(klass, sheet.judgeId) || sheet.judgeId || null;
  const section =
    sheetSectionFromScores(tests?.[sheet.testId], sheet) ?? sectionForPosition(klass, position);
  if (section === "technical") {
    return cleanSectionPct(sheet.technicalPoints, sheet.technicalMaxPoints) ?? sheet.percentage ?? null;
  }
  if (section === "quality") {
    return cleanSectionPct(sheet.qualityPoints, sheet.qualityMaxPoints) ?? sheet.percentage ?? null;
  }
  return sheet.percentage ?? null;
}

// #2033 — the pre-deduction (clean) percentage for the active sheet's error-
// of-course step, rendered between the #2018 judge summary and the #827 raw
// score line. Reuses displaySheetPercentage for a section-judged sheet
// (already clean, via technicalPoints/qualityPoints — the raw section
// subtotals report-defs' calculateScoreSheet never reduces by an error-of-
// course deduction, #987/#998), so this never re-derives a second, possibly
// disagreeing, clean figure for that shape.
//
// For every other shape, DO NOT try to re-derive the clean figure from
// sheet.totalPoints/maxPoints (an earlier version of this function did, and
// an Opus judge review of 3f75605c rejected it, #2033): a young-horse-average
// test scored on ONE sheet (both sections, the #502 shape — reachable even
// though isSectionJudgedRide is false for it, since sheetSectionFromScores'
// majority vote collapses a single both-sections sheet to one label) uses
// rawPercentage, the AVERAGE of the section percentages (calculation.js
// #502/#891), as its clean basis — not pct(totalPoints, maxPoints), a
// different, flat basis the two numbers cannot be reconciled against. Worse,
// a points-style error on such a sheet DOES reduce totalPoints while still
// populating errorPercentApplied (calculation.js #986's
// youngHorseErrorPointsPercent), so the old totalPoints-based fallback was
// asked to un-deduct an already-deducted total on exactly the case its own
// comment claimed could not reach it.
//
// Instead, reconstruct the clean figure from the two numbers
// calculateScoreSheet already persisted, by the same arithmetic the error-
// of-course step displays: percentage = clean - errorPercentApplied (both
// the plain and young-horse-average branches of calculateScoreSheet compute
// it exactly that way, calculation.js #878/#986), so
// clean = percentage + errorPercentApplied always reconstructs the correct
// pre-deduction figure regardless of test model or error style, with no
// need to know which basis (flat vs section-average) produced it.
function cleanPercentageBeforeError(klass, tests, sheets, sheet) {
  if (!sheet) return null;
  if (isSectionJudgedRide(klass, tests, sheets)) return displaySheetPercentage(klass, tests, sheets, sheet);
  if (typeof sheet.percentage !== "number" || typeof sheet.errorPercentApplied !== "number") return null;
  return round3(sheet.percentage + sheet.errorPercentApplied);
}

// #891 — a young-horse two-judge test splits into a technical section (scored
// at C) and a quality section (scored at the class's quality position, B/E).
// Map a judge position to the section that judge scores; null when the class
// isn't a two-judge topology (then every movement is shown/editable), OR when
// qualityJudging is unset — see sectionForSheet below, which is the function
// that actually decides what an admin sees. This is now only ONE signal it
// consults, kept for the "no marks yet AND class field is set" case.
function sectionForPosition(klass, position) {
  const qj = klass?.qualityJudging;
  if (qj?.topology !== "two-judge") return null;
  if (position && position === qj.qualityPosition) return "quality";
  if (position === "C") return "technical";
  return null;
}

// #2035 — which section a given judge's tab shows and may edit, on a
// section-judged (young-horse two-judge) ride. This used to be
// sectionForPosition alone, which reads ONLY Class.qualityJudging and returns
// null (⇒ every movement, both sections) whenever that operator-entered field
// is unset — exactly the shape the #2018/#2034 fix already found and worked
// around for the DISPLAYED PERCENTAGE (isSectionJudgedRide/
// displaySheetPercentage), but deliberately left alone here because which
// movements an admin may edit is a bigger behavioural change than a number.
// This is that follow-up.
//
// Decision (AC1), in priority order:
//   1. isSectionJudgedRide(klass, tests, sheets) first: if this ride is not
//      actually the two-judge shape at all (no sections on the test, or only
//      one section ever scored), every movement is shown, unchanged from
//      before.
//   2. Otherwise, THIS sheet's own scored movements decide its section
//      (sheetSectionFromScores, the same majority-vote signal report-defs'
//      printed sheet and the #2018 percentage already trust), independent of
//      any class field. This is what closes the gap #2035 was filed for: a
//      class with qualityJudging unset still has each sheet's OWN marks to
//      derive from, so step 1 having already proved the ride two-judge plus
//      this step's sheet-derived label is enough with no class field at all.
//   3. No marks yet on this sheet: fall back to the class field
//      (sectionForPosition). If that is also unusable (unset, or this
//      sheet's position is neither "C" nor the class's qualityPosition —
//      data drift, the same shape the AC5 judgeId:"Z" fixture in this file's
//      Cypress spec covers), the section is genuinely unknowable from here
//      and this returns null: every movement shows, same as pre-fix. An
//      unmarked sheet at an off-topology position getting POSITION-guessed
//      into one section would hide the other section's movements from an
//      admin who came to this corrective surface specifically because the
//      data is already wrong — worse than showing extra movements.
//
// Movements already scored OUTSIDE the derived section (e.g. a stray
// cross-section mark like CL 357EF cno 360's B sheet) stay HIDDEN from this
// tab, same as the printed scoresheet's own majority-vote treatment of that
// sheet: the stray mark is not deleted (this function only controls what
// RENDERS on this tab, never what's stored in movementScores), it is simply
// not part of what this judge edits going forward.
function sectionForSheet(test, klass, tests, sheets, sheet) {
  if (!isSectionJudgedRide(klass, tests, sheets)) return null;
  const derived = sheetSectionFromScores(test, sheet);
  if (derived) return derived;
  const position = judgePosition(klass, sheet?.judgeId) || sheet?.judgeId || null;
  return sectionForPosition(klass, position);
}

// The movements an admin sees on a given judge's tab: only that judge's
// section for a section-judged (young-horse two-judge) ride — see
// sectionForSheet's doc comment for how that section is now derived — the
// full list otherwise.
function movementsForSheet(test, klass, sheet, tests, sheets) {
  const all = test?.movements ?? [];
  const section = sectionForSheet(test, klass, tests, sheets, sheet);
  return section ? all.filter((m) => m.section === section) : all;
}

function findMovementScore(sheet, movementNumber) {
  return sheet?.movementScores?.find((m) => m.movementNumber === movementNumber)?.score;
}

// #2467 — a scored mark check, not merely "was a key ever pressed" — mirrors
// scribe-fe's own hasAllScoredMarks (ClassGridRow.jsx, #2184) over the same
// two containers (movementScores + collectives), so a null/empty-string/
// non-numeric leftover reads the same as never having been touched, not as
// "scored". (Judge D5 — the previous `=== undefined` check was laxer than
// that established predicate: it passed null, "", and any non-undefined
// string, and the collective cell writer stores its raw typed value while
// the movement writer Number()s it, so that gap was reachable here.)
function isScoredMark(score) {
  return typeof score === "number" && Number.isFinite(score);
}

// #2467 — confirm rules are unchanged from any other confirm path: every
// movement and collective on the sheet still needs a score, or an explicit
// override. This is a UI-local precondition to a call that IS finality on
// these sheets (confirmScoreSheetMarks stamps completedAt in the same write,
// #2407) — the server enforces none of this itself (see score-api's own
// "#2407 AC4 — a noSignatureNeeded sheet with a missing score and no
// override still confirms with the same math as any other sheet" test,
// which proves a bare confirm call happily stamps completedAt over a hole in
// movementScores) — so this rule binds this one screen only, not the API.
// Checked BEFORE the request is ever sent — a refused booth never reaches
// the API at all, which is what makes it safe to call from a sequential
// fan-out (confirmAllGridSheets below) without any risk of a half-applied
// server write.
//
// Judge D2 — "override" is wider than the sheet's own eliminations[]. Three
// RIDE-level facts also make a blank sheet correct, and are deliberately
// NOT recorded as a sheet elimination (a judge who never opened their sheet
// has no row to write one onto): ride.noShow (#1992), ride.excused (#2064,
// DR123.3), and ride.status SCRATCHED (#916/#2063 — WITHDRAWAL_ELIMINATION_
// REASON was removed from the elimination pick list specifically so an
// in-progress withdrawal goes through the dedicated Scratch action instead
// of a fabricated per-sheet cause). Precedent: scribe-fe's ClassCeremony.jsx
// carries a RULED note (Jaybill, 2026-09-02, #2225) that a missing-score
// gate must warn, never gate, on a scratched/withdrawn/eliminated/retired
// horse — gating on it would make an ordinary scratch unconfirmable on a
// kiosk with no escape hatch. Without these, any ride carrying one of these
// three facts would be refused forever, and would also push the operator to
// falsify a DR124 elimination cause for what is actually a DR123.3 outcome
// — the exact conflation #2063 untangled. #2225 rejected a plain
// ride.status filter partly because score-api's batched ride payload had no
// reliable status signal; RideEditor already loads the full `ride` object
// for this screen, so that objection does not transfer here.
function isSheetConfirmable(sheet, test, ride) {
  if ((sheet?.eliminations ?? []).length > 0) return true;
  if (ride?.noShow?.active) return true;
  if (ride?.excused?.active) return true;
  if (ride?.status === RIDE_STATUSES.SCRATCHED) return true;
  // Judge D4 — fail CLOSED, not open, when the test def failed to load
  // (loadDetail's getTest catches to null): a gate whose only job is
  // refusing must not silently wave every booth through because it has
  // nothing to check against.
  const movements = test?.movements;
  const collectives = test?.collectives;
  if (!Array.isArray(movements) || !Array.isArray(collectives)) return false;
  const missingMovement = movements.some((m) => !isScoredMark(findMovementScore(sheet, m.movementNumber)));
  if (missingMovement) return false;
  // Judge N4 — like sheetSectionFromScores above, this reads the Test's raw
  // movements, not the ride's movementOverride (#1767's
  // getRideMovementOverride) — consistent with this admin view's documented
  // non-resolution of that override (see this file's sheetSectionFromScores
  // doc comment).
  return !collectives.some((c) => !isScoredMark(sheet?.collectives?.[String(c.movementNumber)]?.score));
}

// #1068 — per-movement point deductions (#209/#922), previously read-only
// (buildDeductionItems above). Same lookup shape as findMovementScore.
function findMovementDeductions(sheet, movementNumber) {
  return sheet?.movementScores?.find((m) => m.movementNumber === movementNumber)?.deductions ?? [];
}

function findMovementRemarks(sheet, movementNumber) {
  return sheet?.movementScores?.find((m) => m.movementNumber === movementNumber)?.remarks ?? "";
}

// #2016 — a scored zero ("the judge actually gave a 0") is real, meaningful
// data and gets flagged; an unscored cell is simply pending data entry and
// must never be conflated with it (the same "0 is data, absence is not"
// lesson as #2015's weightedMovementTotal, and the standing "Place 0 is
// stored data, not a missing value" rule elsewhere in this app). Committed
// scores only ever reach here as a `z.number()` per score-common's
// movementScoreValue schema (packages/score-common/src/schemas/validation.js) —
// optional/nullable, never a numeric 0 standing in for "not scored". The
// only other shapes a cell can hold pre-commit are undefined/null (never
// scored) or "" (mid-edit, cleared by the admin), so a strict typeof+equality
// check never fires for "nothing entered yet".
function isScoredZero(value) {
  return typeof value === "number" && value === 0;
}

// #994 — points-vs-percent formatting shared by every deduction line, mirroring
// report-defs' competitor-score-sheet Errors/Deductions box (#719) so the same
// error reads the same way on the admin Ride view and the printed sheet. Only
// one of points/percent is ever set per the catalog errorRule / #922 movement
// deduction scheme.
function formatDeductionAmount(points, percent) {
  if (typeof points === "number") return `−${points} pt${points === 1 ? "" : "s"}`;
  if (typeof percent === "number") return `−${percent}%`;
  return "";
}

// #2012 AC3 / #2014 — the movements/collectives tables get a fixed width per
// column (rather than a column growing/shrinking with its content) so the
// numeric and remarks columns share one left edge down the whole sheet, in
// BOTH Simple and Detailed view (#2014 AC1/AC3) — Detailed differs only in
// what a cell renders (directive line included), never in column geometry.
// One shared spot so the later column-alignment cards (#2015 subtotal rows,
// #2016 highlight zeros, #2018 judge line) have a single seam to extend
// rather than scattered widths. `text` covers both the movement description
// column and the collectives NAME column (#2012 left it keyed `description`
// for both — #2014 renamed it since the key is shared).
//
// #2014 judge finding on #2012: a `width` on a TableCell is only a
// PREFERENCE under the default `table-layout: auto` — a cell whose
// min-content exceeds its share (a nowrap Chip is exactly that case) still
// widens the column, defeating alignment. Real alignment needs BOTH a
// <colgroup> (the one place `table-layout: fixed` actually reads column
// widths from, so every row — header or body, either table — is bound by
// the same geometry regardless of what any individual cell asks for) AND an
// overflow policy for content that doesn't fit: Typography/TextField cells
// wrap or scroll internally for free, but a Chip (the per-movement
// deduction chip) does not wrap on its own, so it gets an explicit
// max-width + ellipsis + Tooltip (see the deductions cell below) rather
// than being left to blow out its column and misalign everything after it.
const SIMPLE_MOVEMENT_COLUMN_WIDTHS = {
  number: 56,
  text: 320,
  score: 120,
  remarks: 220,
  deductions: 220,
};

// #1997 — the movement/collective remarks TextField previously rendered at
// MUI's default input font size (1rem), visibly larger than every other
// row-level cell in these tables, which is Typography variant="body2" (see
// the movement description / collective name cells above) or TableCell's
// own default body2 styling (the number/score cells, no explicit
// Typography wrapper). Spreading theme.typography.body2 onto the input
// itself, rather than picking one property to override by hand, is what
// keeps this from drifting again the next time body2 changes.
// #2044 — the field stays single-line (see the movement/collective remarks
// TextField comments below for why `multiline` is out of scope here), so a
// judge remark longer than the 220px column is hard-truncated mid-word with
// no visual cue and no way to read the rest without clicking in and
// arrowing across (measured 38-44% visible at #1997's HEAD). textOverflow:
// ellipsis turns that silent truncation into a visible one. Note this
// does NOT need (and setting doesn't achieve) overflow: hidden on the
// input: a text <input>'s own overflow-x computes to the browser-enforced
// `clip` no matter what's authored here (confirmed empirically — an
// authored `hidden` still reads back as `clip` via getComputedStyle), but
// CSS Overflow Module Level 3 defines text-overflow: ellipsis as applying
// whenever overflow is anything other than `visible`, and `clip` qualifies,
// so the ellipsis still renders. The Tooltip wrapping each TextField below
// (title = the full remark) is what actually restores readability without
// a click.
const remarksInputSx = (theme) => ({
  "& .MuiInputBase-input": {
    ...theme.typography.body2,
    textOverflow: "ellipsis",
  },
});

// #1996 — alternating (zebra) row backgrounds on the movements/collectives
// data rows so a scorer's eye doesn't lose the row while scanning across
// number/score/remarks/deductions. `action.hover` is MUI's own theme token
// for this: it already renders as a legible tint in EITHER theme with no
// light/dark branching of our own to get wrong, and this file already uses
// the same token for the section-header row above (bgcolor: "action.hover"
// at the freestyle/young-horse section header) — reusing it keeps every
// background tint on this sheet drawn from the one token instead of
// introducing a second, slightly-different grey.
//
// Checked before adding this: neither the movements TableRow nor the
// collectives TableRow carries any background today (grep for
// bgcolor/backgroundColor in this file turns up only the section-header row
// and nothing on the data rows themselves). Every row-level signal that DOES
// exist — the #2016 scored-zero Chip (its own error-colored icon+text,
// rendered on top), the #1067/#2015 changed-field warning Typography (its
// own warning.main text color) — draws its own foreground, never a row
// background, so there is nothing for this stripe to swallow or wash out:
// it is the only background these rows carry. Applied only to the odd data
// row (idx % 2 === 1) via the row's own map index, never to a section-header
// or subtotal row — those keep their own distinct treatment and are not
// reinterpreted as "just another stripe".
const zebraRowSx = (idx) => (idx % 2 === 1 ? { bgcolor: "action.hover" } : undefined);

// #2025 — the movements table stays a fixed 936px (#2014), wider than the
// ride editor's own available content width below roughly 1050-1100px
// logical window width (nav + padding eat ~110-150px of it). Below that,
// TableContainer's horizontal scroll is real and stays real: shrinking the
// remarks/deductions columns to fit would reopen the alignment work #2014
// just finished, and routing narrow windows to the Simple view buys
// nothing, since Simple and Detailed already share the identical
// MOVEMENT_TABLE_COLUMN_WIDTHS/COLLECTIVE_TABLE_COLUMN_WIDTHS (#2012/
// #2014) — only the CONTENT differs, never the widths. The decision
// recorded on #2025: keep the scroll, but pin the movement/collective
// NUMBER column to the container's left edge with position: sticky, so a
// scorer who has scrolled right to reach remarks/deductions never loses
// sight of which row they are on. The scroll stays; the disorientation
// doesn't. Both tables' number column gets this, same treatment #2014
// already gave their left edge.
//
// A sticky cell needs an OPAQUE background of its own: TableContainer
// scrolls the whole <table>, so other rows' cells slide underneath a
// sticky cell, and a transparent cell would show them bleeding through.
// background.default (not background.paper — this page renders directly
// on the app background, no Paper wrapper around either table) is used
// uniformly for every sticky number cell, header and body alike, rather
// than also reproducing zebraRowSx per row: computing an opaque blend of
// action.hover (a translucent MUI token) over background.default is
// avoidable complexity for one column that reads fine either way, so the
// zebra tint is deliberately NOT carried onto this one sticky column.
const stickyNumberColumnSx = {
  position: "sticky",
  left: 0,
  zIndex: 1,
  bgcolor: "background.default",
};

// #2014 — <colgroup>/<col> elements, one array per table shape, so the
// SAME width numbers apply to the movements table (5 columns) and the
// collectives table (4 columns, no deductions) without restating them.
// Rendered as plain <col> tags (not an MUI wrapper) since colgroup is a
// native table construct with no MUI equivalent.
const MOVEMENT_TABLE_COLUMN_WIDTHS = [
  SIMPLE_MOVEMENT_COLUMN_WIDTHS.number,
  SIMPLE_MOVEMENT_COLUMN_WIDTHS.text,
  SIMPLE_MOVEMENT_COLUMN_WIDTHS.score,
  SIMPLE_MOVEMENT_COLUMN_WIDTHS.remarks,
  SIMPLE_MOVEMENT_COLUMN_WIDTHS.deductions,
];
const COLLECTIVE_TABLE_COLUMN_WIDTHS = [
  SIMPLE_MOVEMENT_COLUMN_WIDTHS.number,
  SIMPLE_MOVEMENT_COLUMN_WIDTHS.text,
  SIMPLE_MOVEMENT_COLUMN_WIDTHS.score,
  SIMPLE_MOVEMENT_COLUMN_WIDTHS.remarks,
];

// #2014 — MUI's Table defaults to `width: 100%`, so it stretches to fill
// TableContainer. Under table-layout: fixed, a table WIDER than the sum of
// its <col> widths distributes the extra space by SCALING every column
// proportionally rather than honoring the declared pixel widths — and since
// the movements table (5 columns, wider sum) and collectives table (4
// columns, narrower sum) both stretch to the SAME container width, each
// gets a DIFFERENT scale factor, so their number/score/remarks columns
// silently drift apart even though the colgroups list the same numbers.
// Pinning each table's own `width` to the exact sum of its column widths
// stops it from stretching, so table-layout: fixed holds the declared
// pixel widths literally, in both tables, without decoupling.
const MOVEMENT_TABLE_WIDTH = MOVEMENT_TABLE_COLUMN_WIDTHS.reduce((a, b) => a + b, 0);
const COLLECTIVE_TABLE_WIDTH = COLLECTIVE_TABLE_COLUMN_WIDTHS.reduce((a, b) => a + b, 0);

// #1831 - occurrence spans both errors[] entries and fromError
// eliminations[] entries: an eliminate tier lands in eliminations, not
// errors (see nextRuleError below), so nextErrorOccurrence(errors) alone
// would undercount once one has fired. Mirrors scribe-fe ScoringForm's
// inline errorOccurrences formula.
function nextRuleOccurrence(errors, eliminations) {
  return (
    nextErrorOccurrence(errors) +
    (eliminations ?? []).filter((e) => e.fromError).length
  );
}

// #1756 - the errors[]/eliminations[] entry the RULE would produce for the
// next error of course on this sheet, mirroring scribe-fe ScoringForm's
// `recordError` off the same shared helpers. Deliberately the ONE place it
// is built: the dialog's preview and its Add button both call this, so
// what an admin is shown cannot disagree with what gets stored.
//
// #1831 - a tier's `eliminate` flag now auto-eliminates (USEF DR122.5.b.3,
// National tests: "the third time the competitor is eliminated" —
// docs/rules/usef/rulebook-dr-dressage-2026.pdf; the judge's discretion
// clause there governs only whether the rider may finish, not whether they
// are eliminated). This returns `{ eliminate: true, label }` for that case
// instead of an errors[] shape, and callers branch on `.eliminate` to add
// to eliminations[] instead of errors[]. #682 (2026-07-15) had this flag
// IGNORED for MVP; revisited here.
function nextRuleError(errors, eliminations, tiers) {
  const occurrence = nextRuleOccurrence(errors, eliminations);
  const tier = errorTierForOccurrence(tiers, occurrence) ?? {};
  const label = `${ordinal(occurrence)} error of course`;
  if (tier.eliminate) {
    return { eliminate: true, label };
  }
  const entry = { label, amountSource: ERROR_AMOUNT_SOURCES.RULE };
  if (tier.percent !== undefined) entry.percent = tier.percent;
  else entry.points = tier.points ?? 0;
  return entry;
}

// #1756 - one chip label for an errors[] entry, so the live chip and the
// struck-through draft-removed chip cannot describe it differently. A
// MANUAL entry says so: the stored `amountSource` is the audit record, but
// it is invisible on screen. Unmarked legacy entries read as rule-resolved.
function errorChipLabel(e, t) {
  const amount = formatDeductionAmount(e.points, e.percent);
  const base =
    e.movementNumber != null
      ? t("rideEditor.deductionItemWithMovement", {
          label: e.label,
          movementNumber: e.movementNumber,
          amount,
        })
      : t("rideEditor.deductionItem", { label: e.label, amount });
  return e.amountSource === ERROR_AMOUNT_SOURCES.MANUAL
    ? `${base} ${t("rideEditor.errorManualSuffix")}`
    : base;
}

// #994 — combine a sheet's errors-of-course (sheet.errors, #878/#922
// resolveErrorTiers) and its per-movement point/percent deductions
// (movementScores[].deductions, #209/#922) into one ordered list next to the
// raw-score line: errors first, then movement deductions in movement order —
// same order as report-defs' buildDeductionItems for card 993.
function buildDeductionItems(sheet) {
  const errorItems = (sheet?.errors ?? []).map((e) => ({
    id: e.id,
    label: e.label,
    movementNumber: e.movementNumber ?? null,
    amount: formatDeductionAmount(e.points, e.percent),
  }));
  const movementDeductionItems = (sheet?.movementScores ?? []).flatMap((m) =>
    (m.deductions ?? []).map((d) => ({
      id: d.id,
      label: d.reason,
      movementNumber: m.movementNumber,
      amount: formatDeductionAmount(d.points, d.percent),
    })),
  );
  return [...errorItems, ...movementDeductionItems];
}

export default function RideEditor() {
  const { rideId } = useParams();
  return rideId ? <RideDetail rideId={rideId} /> : <RidesList />;
}

// #1243 — the badge tiers in progress order, so the status column can sort
// numerically rather than alphabetically (which would scatter CONFIRMED
// between COMPLETED and FINAL instead of showing real scoring progress).
const RIDE_STATUS_ORDER = [
  DERIVED_RIDE_STATUSES.PENDING,
  // #768 — between PENDING and IN_PROGRESS, matching score-common's own
  // RIDE_STATUSES ordering (constants.js).
  DERIVED_RIDE_STATUSES.ENTERING_RING,
  DERIVED_RIDE_STATUSES.IN_PROGRESS,
  DERIVED_RIDE_STATUSES.CONFIRMED,
  DERIVED_RIDE_STATUSES.COMPLETED,
  DERIVED_RIDE_STATUSES.FINAL,
  DERIVED_RIDE_STATUSES.SCRATCHED,
];

function rideStatusSortRank(status) {
  const idx = RIDE_STATUS_ORDER.indexOf(status);
  return idx === -1 ? null : idx;
}

// #861 — filterable, progressively-loading list at /rides, replacing the
// class-Select + ride-Autocomplete drill-down (which also forced #845's
// hardcoded-`navigate("/rides")` back button; that's now just a normal
// pushed row link, so browser/in-app Back naturally pop to this list).
// Matches the ClassManager/PeopleManager/HorseManager filtered-list pattern:
// a search box over `useSortedRows`, one flat sortable table, rows linking
// out via the shared RideLink.
function RidesList() {
  const { t } = useTranslation("scoreadmin");
  useDocumentTitle(t("title.rides"));
  const { activeShowId, loading: loadingShow } = useActiveShow();
  // #1906 — useShowRides is always progressive now: the most recent day's
  // rides render immediately, the show's other days load in behind them
  // without blocking first paint.
  const {
    rides,
    loading: loadingRides,
    loadingMore,
    error,
  } = useShowRides(activeShowId);
  const [search, setSearch] = useState("");

  // #585 — drop FVD placeholder pseudo-rides ("***Break***") already seeded
  // before the reconcile-side filter landed. Kept separate from the search
  // filter below (rather than folded into one `filtered` memo) so the id set
  // driving the status batch fetch below doesn't churn on every keystroke.
  const base = useMemo(
    () =>
      rides.filter(
        (r) => !isPlaceholderEntityName(r.horseName) && !isPlaceholderEntityName(r.riderName),
      ),
    [rides],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return base;
    return base.filter((r) =>
      [r.competitorNumber, r.riderName, r.horseName, r.className].some(
        (v) => v != null && String(v).toLowerCase().includes(q),
      ),
    );
  }, [base, search]);

  // #1243 — the SAME server-derived status the detail view uses
  // (RideDetail's useRideStatuses below), batched over every visible ride so
  // the list column shows IN_PROGRESS/CONFIRMED/COMPLETED/FINAL instead of
  // just the raw stored PENDING/SCRATCHED. The hook's own wrapper chunks the
  // request so a big show's ride count doesn't blow up in one call.
  const rideIds = useMemo(() => base.map((r) => r.id), [base]);
  const { statuses: rideStatuses } = useRideStatuses(rideIds);

  // useSortedRows deliberately excludes `accessors` from its memo deps (every
  // pre-existing accessor is a pure function of the row, so it re-sorts only
  // on `rows`/`sortKey`/`sortDir` changing) — an accessor that reached into
  // `rideStatuses` directly would silently sort on STALE data whenever the
  // batch response landed after the sort key was already set (rows' identity
  // wouldn't have changed). Bake the resolved status onto each row instead,
  // in a memo keyed on `rideStatuses`, so a status landing IS a `rows` change
  // and the sort recomputes. A ride not yet resolved (still loading, or an id
  // the batch call didn't return) degrades to its raw stored status rather
  // than going blank.
  const withDisplayStatus = useMemo(
    () => filtered.map((r) => ({ ...r, _displayStatus: rideStatuses[r.id] ?? r.status })),
    [filtered, rideStatuses],
  );

  // #557 — sortable columns over the search-filtered rows; most-recent
  // running order (scheduledTime ascending) is the natural default.
  const { sorted, sortKey, sortDir, toggleSort } = useSortedRows(
    withDisplayStatus,
    {
      cno: (r) => r.competitorNumber,
      className: (r) => r.className,
      scheduledTime: (r) => r.scheduledTime,
      status: (r) => rideStatusSortRank(r._displayStatus),
    },
    "scheduledTime",
  );

  const loading = loadingShow || loadingRides;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom color="text.primary">
        {t("rideEditor.pickerTitle")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message || t("rideEditor.ridesLoadError")}
        </Alert>
      )}

      {!activeShowId && !loadingShow ? (
        <Typography color="text.secondary">{t("ring.noShows")}</Typography>
      ) : (
        <>
          <TextField
            size="small"
            fullWidth
            sx={{ mb: 2, maxWidth: 360 }}
            placeholder={t("rideEditor.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {loading ? (
            <TableSkeleton rows={6} columns={4} />
          ) : !error && filtered.length === 0 ? (
            <Typography color="text.secondary">{t("rideEditor.noRides")}</Typography>
          ) : error && filtered.length === 0 ? null : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {[
                      { key: "cno", labelKey: "rideEditor.columns.ride" },
                      { key: "className", labelKey: "rideEditor.columns.class" },
                      { key: "scheduledTime", labelKey: "rideEditor.columns.time" },
                      { key: "status", labelKey: "rideEditor.columns.status" },
                    ].map((col) => (
                      <TableCell key={col.key} sortDirection={sortKey === col.key ? sortDir : false}>
                        <TableSortLabel
                          active={sortKey === col.key}
                          direction={sortKey === col.key ? sortDir : "asc"}
                          onClick={() => toggleSort(col.key)}
                        >
                          {t(col.labelKey)}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sorted.map((ride) => (
                    <TableRow key={ride.id} hover>
                      <TableCell sx={{ color: "text.primary" }}>
                        <RideLink ride={ride} />
                      </TableCell>
                      <TableCell sx={{ color: "text.primary" }}>{ride.className || "—"}</TableCell>
                      <TableCell sx={{ color: "text.primary" }}>
                        {formatClassStartTime(ride.scheduledTime) || "—"}
                      </TableCell>
                      <TableCell>
                        <RideStatusBadge status={ride._displayStatus} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {loadingMore && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              {t("rideEditor.loadingMore")}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}

// AC2/3/4/5/6/7 — the ride's header/actions plus a per-judge ScoreSheet
// editor. Loaded straight from score-api (no offline/outbox layer).
function RideDetail({ rideId }) {
  const { t } = useTranslation("scoreadmin");
  const navigate = useNavigate();
  const location = useLocation();
  const scoreApi = useScoreApi();
  // #1805 / bug #159 — activeSheet.completedAt below needs the show's real
  // venueTimezone, not a fixed constant.
  const { activeShow } = useActiveShow();

  // #2012 — Simple/Detailed toggle for the movements/collectives tables
  // below. Simple (default, AC2) is fixed-width columns, movement text
  // only, no directive line; Detailed is today's unchanged rendering
  // (Detailed's own content is #1998-B, out of scope here). Persisted in
  // localStorage (AC5) via util/rideViewMode.js, seeded lazily so a stored
  // value doesn't flash Simple first on load.
  const [viewMode, setViewModeState] = useState(getRideViewMode);
  const isSimpleView = viewMode === RIDE_VIEW_MODES.SIMPLE;
  const handleViewModeChange = (_e, next) => {
    if (!next) return; // ToggleButtonGroup fires null when the active button is re-clicked.
    setViewModeState(next);
    setRideViewMode(next);
  };

  // Single server-derived badge status for this ride (#544/#546) — refreshed
  // after admin actions (scratch / force-final / sheet completion) below.
  const { statuses: rideStatuses, refresh: refreshStatus } = useRideStatuses([rideId]);

  // #1665 — the LIVE score-catalog table (GET /catalog, card #1662's
  // useCatalogDefs / IndexedDB cache), replacing the retired build-time
  // bundle as the source getRideTestName (classDisplay.js) resolves the
  // ride's test NAME against, below.
  const { catalogDefs } = useCatalogDefs();
  const catalogDefsById = useMemo(() => {
    const map = new Map();
    for (const def of catalogDefs) map.set(def.id, def);
    return map;
  }, [catalogDefs]);

  // #845 — "Back" must return to wherever the user actually came from (the
  // #863 class-detail drawer at /classes/:classId, or the generic /rides
  // picker), not a hardcoded `navigate("/rides")` PUSH: that stranded a
  // classes-drawer visitor on the unrelated global picker instead of their
  // class, and — because it pushed a fresh entry rather than popping one —
  // left the browser's own Back button one hop further from the list than
  // it should be. `location.key` is react-router's own signal for "is there
  // a real prior in-app entry to pop back to" ("default" on a bare/deep-link
  // load, a generated key once we've navigated at least once); only the
  // latter is safe to pop, so a bare deep link still falls back to the
  // generic picker as before.
  //
  // #995 — a duplicate-in-effect bug report (any entry path, not just the
  // class drawer) landed against this same behavior; confirmed the fix above
  // already covers it generically (every entry — class list, rider-list
  // drawer, /rides search — pushes a real history entry, and `navigate(-1)`
  // doesn't care which). Added direct rider-list-entry coverage in
  // people.cy.js since only the class-drawer and /rides-search paths had a
  // dedicated assertion before.
  const goBackToPicker = () => {
    if (location.key !== "default") {
      navigate(-1);
    } else {
      navigate("/rides");
    }
  };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ride, setRide] = useState(null);
  const [klass, setKlass] = useState(null);
  const [horse, setHorse] = useState(null);
  const [rider, setRider] = useState(null);
  const [result, setResult] = useState(null);
  const [tests, setTests] = useState({});
  // #2622 — getTest can fail for reasons other than "no such test": a 500,
  // a network drop, an expired token. Only a genuine 404 means the test
  // really is missing (`tests[id]` stays null, unchanged rendering below).
  // Any other failure is kept HERE, keyed by testId, so the render site can
  // tell "the lookup failed" apart from "there is no test def" instead of
  // collapsing both into the same rideEditor.noTest wording.
  const [testErrors, setTestErrors] = useState({});
  const [sheets, setSheets] = useState([]);
  const [activeSheetId, setActiveSheetId] = useState(null);
  // #2469 — the numeric-only grid (#2411) hard-disables editing for any
  // sheet whose state isn't OPEN, with no way to correct a CONFIRMED/SIGNED
  // sheet from the grid at all. Rather than porting the #1067 unlock/draft/
  // commit lifecycle into the grid itself (that machinery is written as
  // single-activeSheet state throughout this file, and NumericOnlyScoreGrid.jsx
  // is deliberately kept separate from it), a booth needing correction sends
  // the operator to the EXISTING tabbed editor below for just that one sheet
  // — same server-side unlock/draft/commit lifecycle, zero new code path.
  // Non-null while the operator is there for correction; null shows the grid
  // (when the panel is all numericOnly) as before. Reset on every ride load
  // (applyDetail) so navigating to a different ride never carries this over.
  const [correctingSheetId, setCorrectingSheetId] = useState(null);
  // #850 — show-wide judges roster for the ride's show (card #732), used to
  // gate Complete & Sign on `signaturePresent` and, since #2018, to resolve
  // the judge-summary panel below. Mirrors scribe-fe's ScoringForm
  // `showJudges`/`missingSignatureJudges` (#733).
  const [judges, setJudges] = useState([]);
  // #2018 — { [rosterJudgeId]: resolveJudgeComposite result | null }, fed by
  // the effect below. Keyed by the RESOLVED roster row id resolveClassPanel
  // matches out of Class.judgeAssignments, never by a sheet's own judgeId:
  // on production data that IS the arena position letter ("C"/"E"/…), not a
  // roster row id (docs/score/fvd-data-model.md:236) — resolveCompositeJudge
  // does a GetCommand on the JUDGE table keyed by id, so a position letter
  // 404s against it every time (the defect that made this feature render
  // nothing before the rework).
  const [judgeResolutions, setJudgeResolutions] = useState({});
  const [scratchLoading, setScratchLoading] = useState(false);
  const [scratchError, setScratchError] = useState(null);
  // Card #2064 — Excused (DR123.3's fourth term reason). Score Admin gets
  // the same action as the scribe kiosk, for correction after the fact
  // (2026-08-30 card answer: BOTH can stamp Excused).
  const [excusedLoading, setExcusedLoading] = useState(false);
  const [excusedError, setExcusedError] = useState(null);

  const [savingSheet, setSavingSheet] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // #2411 — the numeric-only grid's own per-booth save state. Deliberately
  // separate from savingSheet/saveError above (the tabbed editor's single
  // active-sheet Save state): the grid can have a save in flight for any
  // one of several booths at once, keyed by sheet id. Batching a save
  // across the whole panel behind one click is #2466 ("save fan-out"),
  // out of scope here — this is the SAME single-sheet PUT handleSave below
  // already makes, just invocable per column instead of only for
  // activeSheet.
  const [gridSavingSheetId, setGridSavingSheetId] = useState(null);
  const [gridSaveErrors, setGridSaveErrors] = useState({});
  // #2466 — the grid's whole-panel "Save" (fan-out across every booth).
  // gridSaveAllResult is null before any Save-All run, then
  // { failedPositions: [] } (all-succeed) or { failedPositions: ["C", ...] }
  // (partial failure) after one completes — the grid renders the summary
  // message off this, naming whichever booths are in that array.
  const [gridSavingAll, setGridSavingAll] = useState(false);
  const [gridSaveAllResult, setGridSaveAllResult] = useState(null);
  // #2467 — the grid's per-booth Confirm and its "Confirm All" fan-out.
  // gridConfirmErrors is keyed by sheetId, same shape as gridSaveErrors,
  // and is the single source of truth the failure banner is derived from
  // (NumericOnlyScoreGrid.jsx) — no separate stored "result" object, so an
  // individual Confirm retry that clears one booth's own entry also clears
  // it out of the page-level banner with no extra bookkeeping (the #2466
  // judge's stale-banner finding). gridConfirmAllRan is just "has a
  // Confirm All run finished at least once", so the banner has something
  // to gate "all clean" on before any run.
  const [gridConfirmingSheetId, setGridConfirmingSheetId] = useState(null);
  const [gridConfirmErrors, setGridConfirmErrors] = useState({});
  const [gridConfirmingAll, setGridConfirmingAll] = useState(false);
  const [gridConfirmAllRan, setGridConfirmAllRan] = useState(false);

  // #2410 — "create this ride's missing score sheets" fan-out. One button,
  // not a per-row action, so a single busy boolean is enough (unlike the
  // per-sheetId maps above). missingSheetCreateErrors is keyed by JUDGE
  // POSITION (the panel entry that failed, not a sheet id — a failed
  // create has no sheet id to key by) and is the sole source the failure
  // banner reads live off, same #2466 judge finding as gridConfirmErrors:
  // never a separate stored result object.
  const [creatingMissingSheets, setCreatingMissingSheets] = useState(false);
  const [missingSheetCreateErrors, setMissingSheetCreateErrors] = useState({});

  // #1754 — class-grid (judge-percentage) local typing buffers, same
  // in-progress-value problem scribe-fe's ClassGridRow hit and fixed
  // (F5 round-2 judge review): a controlled type="number" input firing
  // Number(e.target.value) on every keystroke reports e.target.value === ""
  // for an in-progress "72." (half-mark percentages), which would otherwise
  // route into the "clear the field" branch mid-keystroke. Parse+commit only
  // on blur; these hold the raw string while typing, for both sheet states
  // (OPEN direct-edit and CONFIRMED/SIGNED draft) — the commit functions
  // below decide which one a blur actually writes to.
  const [percentageDraft, setPercentageDraft] = useState(undefined);
  const [obsDrafts, setObsDrafts] = useState({});

  // #1755 continuation — driver decision (card comment): Save/Commit is a
  // hard block while any score cell is showing a strict-mode rejection, so
  // an admin can never commit the OLD (last-known-good) value while the UI
  // still shows that cell in error. Keyed by a stable per-cell id
  // ("movement-<n>" / "collective-<n>") so one cell's fix doesn't clear
  // another's error.
  const [invalidScores, setInvalidScores] = useState({});
  const markScoreValidity = useCallback(
    (id) => (isInvalid) => {
      setInvalidScores((prev) => {
        if (Boolean(prev[id]) === Boolean(isInvalid)) return prev;
        return { ...prev, [id]: Boolean(isInvalid) };
      });
    },
    [],
  );
  const hasInvalidScore = Object.values(invalidScores).some(Boolean);

  const [signOpen, setSignOpen] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [completeError, setCompleteError] = useState(null);

  const [elimAnchor, setElimAnchor] = useState(null);
  const [customReasonOpen, setCustomReasonOpen] = useState(false);
  const [customReasonValue, setCustomReasonValue] = useState("");

  // #1068 — errors-of-course / deductions are always a custom entry (label +
  // points-or-percent + optional movement), so one dialog rather than the
  // eliminations menu-of-presets pattern.
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  // #1756 - "rule" (deduction resolved from the tier schedule, the default
  // and the normal path) vs "manual" (admin types the amount, the escape
  // hatch #1068 made the ONLY path). Stored on the entry as `amountSource`.
  const [errorEntryMode, setErrorEntryMode] = useState(ERROR_AMOUNT_SOURCES.RULE);
  const [errorLabelValue, setErrorLabelValue] = useState("");
  const [errorAmountType, setErrorAmountType] = useState("points");
  const [errorAmountValue, setErrorAmountValue] = useState("");
  const [errorMovementValue, setErrorMovementValue] = useState("");

  // #1068 — per-movement deduction dialog: which movement it's for (null =
  // closed), a reason, and points (schema requires a strictly positive
  // points value, no percent option for this one — see movementScoreSchema).
  const [deductionDialogMovement, setDeductionDialogMovement] = useState(null);
  const [deductionReasonValue, setDeductionReasonValue] = useState("");
  const [deductionPointsValue, setDeductionPointsValue] = useState("");

  // #2013 — Detailed-view annotation dialog. `undefined` = closed; `null` =
  // open for a sheet-general note (movementNumber: null is a real, allowed
  // value on the schema, so it can't double as "closed" the way
  // deductionDialogMovement's `null` does above); a number = open, scoped to
  // that movement.
  const [annotationDialogMovement, setAnnotationDialogMovement] = useState(undefined);
  const [annotationTextValue, setAnnotationTextValue] = useState("");
  const [annotationSubmitting, setAnnotationSubmitting] = useState(false);
  const [annotationError, setAnnotationError] = useState(null);

  const openAnnotationDialog = (movementNumber) => {
    setAnnotationDialogMovement(movementNumber);
    setAnnotationTextValue("");
    setAnnotationError(null);
  };

  // #2013 AC3/AC4/AC6 — calls the dedicated POST /scoresheet/:id/annotations
  // route directly, NEVER the draft/commit path or a plain PUT: `annotations`
  // is in score-api's SERVER_ONLY_FIELDS, so a PUT/draft body can never write
  // it (see score-api/src/handlers/scoresheet.js), and this route has no
  // freeze check at all -- an annotation is a note, not a mark-affecting
  // correction, so it must work identically on an OPEN, CONFIRMED, or SIGNED
  // sheet (AC6). addedBy/addedAt are never sent -- the server stamps both
  // from request.user/now and ignores anything the client sends for them.
  // On a 409 concurrent_modification (someone else wrote the sheet between
  // load and this append), re-fetch the sheet once and retry the append
  // against the fresh version -- never an unbounded retry loop.
  //
  // #2013 FE-judge blocker 2 -- MERGE the server response into local state,
  // never replace the local sheet object wholesale. An OPEN sheet has no
  // draft buffer (that only exists for CONFIRMED/SIGNED): setMovementScore,
  // handleMovementScoreChange, setCollective etc. all write straight into
  // `sheets`, and Save is an explicit button. The annotation endpoint's
  // response is the server's PERSISTED sheet, so swapping the whole local
  // object in for it would silently discard whatever the admin had typed
  // but not yet saved. Only `annotations` (what this call actually changed)
  // and `updatedAt` (needed for the next optimistic-concurrency check) come
  // from the server response; every admin-editable field is left exactly as
  // the admin left it.
  //
  // The 409 path is the same call twice over: a 409 here only means someone
  // else appended an annotation (or otherwise wrote the sheet) between load
  // and this attempt, not that the CURRENT admin's in-progress marks are
  // wrong -- so the re-fetch is merged the same way before the retry, never
  // replacing the local scores/remarks/collectives/eliminations the admin
  // is mid-editing.
  const submitAnnotation = async () => {
    const text = annotationTextValue.trim();
    if (!activeSheet || !text) return;
    const movementNumber = annotationDialogMovement ?? null;
    setAnnotationSubmitting(true);
    setAnnotationError(null);
    const mergeServerAnnotations = (id, serverSheet) => {
      setSheets((prev) =>
        prev.map((s) =>
          s.id === id ? { ...s, annotations: serverSheet.annotations, updatedAt: serverSheet.updatedAt } : s,
        ),
      );
    };
    try {
      let updated;
      try {
        updated = unwrap(
          await scoreApi.scoreSheets.addScoreSheetAnnotation(activeSheet.id, { text, movementNumber }),
        );
      } catch (err) {
        if (err?.response?.status === 409) {
          const fresh = unwrap(await scoreApi.scoreSheets.getScoreSheet(activeSheet.id));
          mergeServerAnnotations(fresh.id, fresh);
          updated = unwrap(
            await scoreApi.scoreSheets.addScoreSheetAnnotation(fresh.id, { text, movementNumber }),
          );
        } else {
          throw err;
        }
      }
      mergeServerAnnotations(updated.id, updated);
      setAnnotationDialogMovement(undefined);
      setAnnotationTextValue("");
    } catch (err) {
      // #2013 FE-judge fold-in note 2 — err.message is essentially always
      // truthy (common-fe's installServerErrorInterceptor rewrites it to a
      // generic "Server Error" string rather than clearing it), so
      // `err.message || t(...)` was dead-code fallback that always rendered
      // the raw axios/server text. Map by code instead, same as
      // resendErrorMessage above.
      setAnnotationError(t(ANNOTATION_ERROR_KEYS[err?.response?.data?.code] || "rideEditor.annotationError"));
    } finally {
      setAnnotationSubmitting(false);
    }
  };

  const [auditOpen, setAuditOpen] = useState(false);

  // #1067 — the unlock/draft/commit in-place edit lifecycle (replaces the old
  // #587 comment-only unlock and #898/#901 reopen, which deleted the judge
  // signature — see docs/score/admin-scoresheet-editing.md). A CONFIRMED or
  // SIGNED sheet is edited directly: no manual "unlock" button, the FIRST
  // field edit silently calls POST .../unlock (fixed, non-user-facing reason
  // — the meaningful reason capture happens once, at commit) if the sheet
  // isn't already in the editing state, then every change is upserted to the
  // server-persisted draft (PUT .../draft) so it survives a reload, a
  // different device, or a different admin picking up the same sheet.
  // draftFields is the current draft's `fields` diff (server-shaped); null
  // means "not applicable / not loaded yet" (OPEN sheets never have one).
  const [draftFields, setDraftFields] = useState(null);
  const [draftLoading, setDraftLoading] = useState(false);
  const [draftSyncError, setDraftSyncError] = useState(null);
  const [discarding, setDiscarding] = useState(false);

  const [commitOpen, setCommitOpen] = useState(false);
  const [commitReasonDraft, setCommitReasonDraft] = useState("");
  const [commitResend, setCommitResend] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [commitError, setCommitError] = useState(null);
  const [commitConflict, setCommitConflict] = useState(false);
  const [resendAfterCommitError, setResendAfterCommitError] = useState(false);
  // #1069 — §4.8 conditional re-place: the commit response's
  // `placingImpact` ({classId, showId, checked, changed} | {..., error}).
  // Rendered as a banner only when `changed` or `error` is true; a
  // changed:false (or missing) impact means the class stayed placed and
  // deliberately shows nothing — that's the entire point of the card.
  const [placingImpactNotice, setPlacingImpactNotice] = useState(null);

  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);

  // #846 — per-ride "Resend score sheet": an editable-email modal, prepopulated
  // from the rider's on-file address but overridable for a one-time send (the
  // override is never written back onto the rider's record — see
  // useResendScoreSheet). Ad-hoc/one-off replacement for the removed
  // en-masse email button (#822 now auto-sends on class Place).
  const { resend, sending: resending } = useResendScoreSheet();
  const [resendOpen, setResendOpen] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [resendError, setResendError] = useState(null);
  const [resendSuccess, setResendSuccess] = useState(false);

  // #918 — "was this rider actually emailed their sheet" indicator. Fetched
  // independently of loadDetail below (report-api, not score-api) and
  // re-fetched after a resend so the indicator reflects the CONFIRMED result
  // of that send, not just that a resend was dispatched (the resend route
  // awaits the actual send server-side before returning, so by the time
  // resend() resolves the status is already settled for the force/existing
  // case — a first-ever request instead lands GENERATING until its worker
  // finishes, which the chip honestly shows as pending, not sent).
  const { fetchStatus: fetchSheetSentStatus } = useScoreSheetStatus();
  const [sheetSentStatus, setSheetSentStatus] = useState(null);
  const refreshSheetSentStatus = useCallback(async () => {
    try {
      const data = await fetchSheetSentStatus(rideId);
      setSheetSentStatus(data);
    } catch {
      // Non-fatal — the indicator just stays at its last known state.
    }
  }, [fetchSheetSentStatus, rideId]);
  useEffect(() => {
    refreshSheetSentStatus();
  }, [refreshSheetSentStatus]);

  // #755 — ride-level BLOOD/LAMENESS utility flags (card #139's cross-scribe
  // welfare signal, persisted on the Ride, never a Sheet — see
  // UTILITY_EVENT_TYPES in score-common). Previously invisible on this page;
  // this is a plain fetch-on-load, unlike scribe-fe's
  // useRideUtilityEvents (poll + offline outbox) — this admin tool is
  // online-only and doesn't need cross-tab live sync, so a page reload is
  // enough to pick up a flag raised after load.
  const [utilityEvents, setUtilityEvents] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = unwrap(await scoreApi.rides.listRideUtilityEvents(rideId)) || [];
        if (!cancelled) setUtilityEvents(rows);
      } catch {
        // Non-fatal — this is a supplementary welfare-flag strip, not core
        // ride data the rest of the page depends on; just stays empty.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scoreApi, rideId]);

  // #947 — "Open score page": opens this rider's permanent score-sheet page
  // (the same one the Resend button emails) in a new tab, without sending
  // any email. See useScorePageLink for why this is a separate call from
  // resend above rather than reusing its response.
  const { getLink: getScorePageLink, loading: scorePageLinkLoading } = useScorePageLink();
  const [scorePageLinkError, setScorePageLinkError] = useState(null);
  const handleOpenScorePage = useCallback(async () => {
    setScorePageLinkError(null);
    try {
      const url = await getScorePageLink(rideId);
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setScorePageLinkError(err);
    }
  }, [getScorePageLink, rideId]);

  // #928 — a two-judge-section class (young-horse/Children T/Q) missing a
  // section's judge assignment blocks the score-sheet report; this dialog
  // offers an override that resends the run with overrideIncompleteJudges,
  // which stamps the rendered sheet instead of throwing (report-defs). Single-
  // section classes always resolve to [] (unaffected).
  const [judgesBlockOpen, setJudgesBlockOpen] = useState(false);

  // #2413 — print score sheet stays on the ride editor: run+poll the report
  // in place (usePrintScoreSheet.js) instead of navigating to /reports. The
  // override dialog below now calls the same `printScoreSheet` with
  // overrideIncompleteJudges:true rather than resending a deep link.
  const { print: printScoreSheet, busy: printScoreSheetBusy, errorKind: printScoreSheetErrorKind } =
    usePrintScoreSheet();

  // Fetch everything the screen needs for one Ride: the Ride itself, its
  // multi-judge result (FINAL/PARTIAL + composite, card #506), the Class
  // (for judge-position labels and #850's judgeIds gate below), horse/rider
  // display names, every ScoreSheet, the distinct Tests those sheets are
  // scored against, and the show's judge roster (#732, GET /judge?showId=)
  // for the show-wide signature gate.
  const loadDetail = useCallback(async () => {
    const rideData = unwrap(await scoreApi.rides.getRide(rideId));
    const [resultData, klassData, horseData, riderData, sheetList, judgeList] = await Promise.all([
      scoreApi.results.getRideResult(rideId).then(unwrap).catch(() => null),
      rideData.classId
        ? scoreApi.classes.getClass(rideData.classId).then(unwrap).catch(() => null)
        : Promise.resolve(null),
      rideData.horseId
        ? scoreApi.horses.getHorse(rideData.horseId).then(unwrap).catch(() => null)
        : Promise.resolve(null),
      rideData.riderId
        ? scoreApi.people.getPerson(rideData.riderId).then(unwrap).catch(() => null)
        : Promise.resolve(null),
      scoreApi.scoreSheets.listScoreSheets(rideId).then(unwrap).catch(() => []),
      rideData.showId
        ? scoreApi.judges.listJudges(rideData.showId).then(unwrap).catch(() => [])
        : Promise.resolve([]),
    ]);

    const testIds = [...new Set(sheetList.map((s) => s.testId).filter(Boolean))];
    // #2622 — a getTest failure used to collapse into `[id, null]` no
    // matter why it failed, so a 500/network-drop/expired-token read the
    // same as "no such test" at the render site below. Only a genuine 404
    // means the test really is missing; keep every other failure so the
    // render site can tell them apart (common-fe's
    // installServerErrorInterceptor already logs the real status/detail to
    // the console on every rejected call, see scoreApi.js — AC4 needs no
    // extra logging here).
    const testEntries = await Promise.all(
      testIds.map(async (id) => {
        try {
          return [id, unwrap(await scoreApi.tests.getTest(id)), null];
        } catch (err) {
          return [id, null, classifyTestFetchError(err)];
        }
      })
    );

    return {
      ride: rideData,
      result: resultData,
      klass: klassData,
      horse: horseData,
      rider: riderData,
      tests: Object.fromEntries(testEntries.map(([id, test]) => [id, test])),
      testErrors: Object.fromEntries(
        testEntries.filter(([, , err]) => err).map(([id, , err]) => [id, err])
      ),
      sheets: sheetList,
      judges: judgeList,
    };
  }, [scoreApi, rideId]);

  const applyDetail = useCallback((detail) => {
    setRide(detail.ride);
    setResult(detail.result);
    setKlass(detail.klass);
    setHorse(detail.horse);
    setRider(detail.rider);
    setTests(detail.tests);
    setTestErrors(detail.testErrors);
    const orderedSheets = sortSheetsByDisplayPosition(detail.klass, detail.sheets);
    setSheets(orderedSheets);
    setJudges(detail.judges);
    setActiveSheetId((prev) =>
      prev && orderedSheets.some((s) => s.id === prev) ? prev : orderedSheets[0]?.id ?? null
    );
    setCorrectingSheetId(null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const detail = await loadDetail();
        if (cancelled) return;
        applyDetail(detail);
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadDetail, applyDetail]);

  // #2018 — the class panel: position order + each position's judge name
  // AND, where it matches a roster row, that row's id (resolveClassPanel in
  // score-common's calculation.js). This is what the judge-summary list
  // below reads for names, and it needs no per-judge API call for the
  // ordinary case — only a genuinely composite roster id (below) does.
  const judgePanel = useMemo(() => resolveClassPanel(klass, judges), [klass, judges]);

  // #2410 — the panel POSITIONS this ride's class expects a sheet for that
  // don't have one yet. Derived from judgePanel (resolveClassPanel — the
  // same signal finality/deriveRideStatus reads below), never from
  // "whatever sheets happen to exist": AC3's whole point is that a
  // two-judge class with one sheet reports exactly one missing, regardless
  // of which position that one sheet happens to be. A ScoreSheet's judgeId
  // IS the position code (useScoreSheet.js / rideEditorNumericGrid
  // fixture), so a plain Set-membership check is enough here.
  const missingPanelEntries = useMemo(() => {
    const present = new Set(sheets.map((s) => s.judgeId).filter(Boolean));
    return judgePanel.filter((p) => !present.has(p.position));
  }, [judgePanel, sheets]);

  // #2018 — resolve every PANEL position's matched roster judgeId through
  // the ONE shared #1342/#1343 expander (GET /judge/:id/resolve), never a
  // local composite guess (AC4). Only ids resolveClassPanel actually
  // matched to a roster row are ever requested (BLOCKING 1: a sheet's own
  // judgeId is the arena position letter on production data, not a roster
  // id, and 404s against this endpoint). A resolve failure (data drift)
  // degrades that one id to `null` instead of failing the whole ride view —
  // judgeNamesForPanelEntry renders nothing for it (AC5).
  //
  // Keyed on a sorted, deduped join of those ids (BLOCKING 3) rather than on
  // `judgePanel`/`sheets` directly: `sheets` gets a brand-new array from
  // every keystroke in a score field (setSheets((prev) => prev.map(...)) at
  // the movement/collective/field handlers below), which previously refired
  // this effect ~90 times for a 30-movement/3-judge sheet. The panel's
  // resolved ids only actually change when klass or judges themselves
  // change, so this key is stable across scoring keystrokes.
  const judgeIdKey = useMemo(
    () => [...new Set(judgePanel.map((p) => p.judgeId).filter(Boolean))].sort().join(","),
    [judgePanel],
  );
  useEffect(() => {
    const ids = judgeIdKey ? judgeIdKey.split(",") : [];
    if (ids.length === 0) {
      setJudgeResolutions({});
      return;
    }
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        ids.map(async (id) => {
          try {
            return [id, unwrap(await scoreApi.judges.resolveJudgeComposite(id))];
          } catch {
            return [id, null];
          }
        }),
      );
      if (!cancelled) setJudgeResolutions(Object.fromEntries(entries));
    })();
    return () => {
      cancelled = true;
    };
  }, [judgeIdKey, scoreApi]);

  const activeSheet = sheets.find((s) => s.id === activeSheetId) ?? null;
  const sheetState = deriveSheetState(activeSheet);
  // #994 — errors of course / deductions shown next to the raw-score line, so
  // an admin can see where points were lost without opening the audit trail
  // or cross-referencing the scribe sheet. eliminated mirrors calculateScoreSheet
  // (score-common/calculation.js #878): any recorded elimination reason ⇒
  // eliminated, same condition the Eliminations chips below already use.
  const eliminated = (activeSheet?.eliminations ?? []).length > 0;
  const deductionItems = useMemo(() => buildDeductionItems(activeSheet), [activeSheet]);
  // #2033 — the error-of-course step rendered between the #2018 judge summary
  // (clean) and the #827 raw-score line (net): only when a deduction actually
  // came off THIS sheet's percentage. errorPercentApplied is 0/absent for a
  // clean sheet (AC3 unaffected) and for an eliminated sheet's percentage is
  // forced to 0 by calculateScoreSheet with nothing left to explain, so the
  // eliminated guard here matches report-defs' rideEliminated null-out
  // (competitor-score-sheet.js #987) rather than printing a meaningless step
  // on top of the ELIMINATED banner below.
  const activeErrorPct = !eliminated ? round3(activeSheet?.errorPercentApplied || 0) : 0;
  const activeCleanPct = useMemo(
    () => (activeErrorPct > 0 ? cleanPercentageBeforeError(klass, tests, sheets, activeSheet) : null),
    [activeErrorPct, klass, tests, sheets, activeSheet],
  );
  // #1756 - the ride's error-of-course tier schedule, resolved with the same
  // inputs and the same shared function scribe-fe's ScoringForm uses (#773):
  // the CLASS's admin-picked errorDeductionScheme when it has one, otherwise
  // the catalog test's own errorRule (#683/#922). Declared up here, not
  // beside `test` below, because the remove handlers re-tier against it. An
  // EMPTY array is meaningful, not missing data: #1300's errorRule.unit
  // "none" (materiale has no course to deviate from) ⇒ no error concept.
  const errorTiers = useMemo(
    () =>
      resolveErrorTiers({
        errorDeductionScheme: klass?.errorDeductionScheme,
        test: activeSheet ? tests[activeSheet.testId] : null,
      }),
    [klass?.errorDeductionScheme, tests, activeSheet],
  );
  const hasErrorConcept = errorTiers.length > 0;
  // #735 — bottom-of-sheet signature box. Only fetched once the sheet is
  // SIGNED (completedAt set); the show-wide per-judge signature (#731) is
  // immutable at that point, so this is a one-shot fetch, not polled.
  // #2412 — a noSignatureNeeded (paper-scoring) sheet has no signature to
  // fetch: read straight off the sheet, never re-derived from the class
  // (#2406), same rule as scribe-fe's ScoringForm.
  const { signature: sheetSignature, loading: signatureLoading } = useSheetSignature(
    activeSheet?.id,
    { enabled: Boolean(activeSheet?.completedAt) && !activeSheet?.noSignatureNeeded },
  );
  // #850 — mirrors scribe-fe ScoringForm's missingSignatureJudges/
  // hasMissingSignature (#733): class-wide gate, not per-sheet. klass.judgeIds
  // is the deduped set of judge PERSON ids assigned to this class (#730);
  // Complete & Sign is blocked while any of them lacks a show-wide signature
  // on file, same as scribe, rather than trying to resolve which single judge
  // this one sheet belongs to (that resolution is server-side-only — see
  // resolveJudgeIdentity's #818 warning in score-api's signatures.js).
  const missingSignatureJudges = useMemo(() => {
    const judgeIds = klass?.judgeIds ?? [];
    if (judgeIds.length === 0) return [];
    const byId = new Map(judges.map((j) => [j.id, j]));
    return judgeIds.filter((id) => !byId.get(id)?.signaturePresent);
  }, [klass?.judgeIds, judges]);
  // #2412 — a noSignatureNeeded (paper-scoring) sheet's judges never carry a
  // show-wide e-signature on file (that's the whole premise of a paper
  // class), so this class-wide gate must never apply to one: it would
  // permanently disable Complete & Sign for a sheet the server itself
  // requires no signature to finalize (#2407). In practice a noSignatureNeeded
  // sheet is also always numericOnly (#2406, stamped together from the same
  // paperEntry flag) and so renders via the NumericOnlyScoreGrid branch
  // below, never reaching this gate at all -- this guard covers the one
  // edge case where a ride's sheets are momentarily mixed (some created
  // before the class's paperEntry flag flipped) and a noSignatureNeeded
  // sheet still lands in this tabbed view.
  const hasMissingSignature = missingSignatureJudges.length > 0 && !activeSheet?.noSignatureNeeded;
  // #928 — [] for a single-section class (qualityJudging unset/"technical-only");
  // one entry per unassigned section for a two-judge class.
  const missingJudgeSections = useMemo(() => missingJudgeSectionsForClass(klass), [klass]);
  // #1067 — a CONFIRMED/SIGNED sheet is edited directly (§4.1): fetch the
  // server-persisted draft (if any) whenever the active sheet changes, so a
  // reload / a different admin / a different device picks up exactly what's
  // pending. A 404 means "editing state but nothing saved to the draft yet"
  // (or no editing session at all) — both read as "no pending edits" here.
  // OPEN sheets never have a draft; this flow doesn't apply to them.
  useEffect(() => {
    if (!activeSheet || sheetState === "OPEN") {
      setDraftFields(null);
      setDraftSyncError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setDraftLoading(true);
        setDraftSyncError(null);
        const data = unwrap(await scoreApi.scoreSheets.getScoreSheetDraft(activeSheet.id));
        if (!cancelled) setDraftFields(data?.fields ?? {});
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setDraftFields({});
        } else {
          setDraftSyncError(err);
        }
      } finally {
        if (!cancelled) setDraftLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // sheetState is derived from activeSheet, included for clarity — the
    // effect only needs to re-run when the SHEET changes, not on every local
    // patch to draftFields itself (that would refetch what we just wrote).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoreApi, activeSheet?.id, sheetState]);

  // The live, editable projection: committed sheet + whatever's pending in
  // the draft on top of it. Only meaningful once draftFields has loaded
  // (non-null); OPEN sheets render straight from activeSheet as before.
  const displaySheet = draftFields ? { ...activeSheet, ...draftFields } : activeSheet;
  // #1831 — the errors[]/eliminations[] entry the RULE would produce for
  // the NEXT error of course on this sheet, computed once so the dialog's
  // preview and its Add button agree (nextRuleError above). Must come after
  // displaySheet (draft-aware) is declared above, not before.
  const ruleOutcome = nextRuleError(displaySheet?.errors, displaySheet?.eliminations, errorTiers);
  // A key is only ever written into draftFields by the push helpers below
  // when its value actually differs from the committed sheet (see
  // pushDraftFields callers), so "key present" IS "this field changed".
  const hasUnsavedEdits = Boolean(draftFields && Object.keys(draftFields).length > 0);
  // While the initial draft fetch is in flight, draftFields is still null
  // (or a leftover from the previous tab) — hold the fields read-only for
  // that brief window rather than let an admin start typing over data we
  // haven't confirmed is current yet (another admin/device may have a
  // pending edit already in flight).
  const draftFieldsBusy = sheetState !== "OPEN" && (draftLoading || draftFields === null);

  // AC5 — navigating away mid-edit prompts a confirm dialog. The draft is
  // already server-persisted (so nothing is actually lost), but the admin
  // still gets a deliberate confirm before leaving an uncommitted correction
  // behind. beforeunload covers a tab close/reload/URL-bar navigation (the
  // browser's own native prompt — no custom text is allowed there); the
  // in-app "Back to picker" button below gets the real MUI confirm dialog.
  useEffect(() => {
    if (!hasUnsavedEdits) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedEdits]);

  const handleBackClick = () => {
    if (hasUnsavedEdits) {
      setLeaveConfirmOpen(true);
      return;
    }
    goBackToPicker();
  };

  const setActiveSheetField = (patch) => {
    setSheets((prev) => prev.map((s) => (s.id === activeSheetId ? { ...s, ...patch } : s)));
  };

  const setMovementScore = (movementNumber, patch) => {
    setSheets((prev) =>
      prev.map((s) => {
        if (s.id !== activeSheetId) return s;
        const existing = s.movementScores ?? [];
        const idx = existing.findIndex((m) => m.movementNumber === movementNumber);
        const next =
          idx >= 0
            ? existing.map((m, i) => (i === idx ? { ...m, ...patch, movementNumber } : m))
            : [...existing, { movementNumber, ...patch }];
        return { ...s, movementScores: next };
      })
    );
  };

  // "—" clears a movement's score by dropping the entry entirely — a
  // movementScore with no score wouldn't round-trip through score-common's
  // schema (score is required there), so this is cleaner than storing
  // score: undefined.
  const handleMovementScoreChange = (movementNumber, value) => {
    if (value === "") {
      setSheets((prev) =>
        prev.map((s) =>
          s.id !== activeSheetId
            ? s
            : {
                ...s,
                movementScores: (s.movementScores ?? []).filter(
                  (m) => m.movementNumber !== movementNumber
                ),
              }
        )
      );
    } else {
      setMovementScore(movementNumber, { score: Number(value) });
    }
  };

  const setCollective = (key, patch) => {
    setSheets((prev) =>
      prev.map((s) => {
        if (s.id !== activeSheetId) return s;
        const prevEntry = s.collectives?.[key] ?? {};
        return { ...s, collectives: { ...(s.collectives ?? {}), [key]: { ...prevEntry, ...patch } } };
      })
    );
  };

  // #1831 — `extra` carries fromError:true (and optionally movementNumber)
  // for an eliminate-tier error-of-course occurrence; every existing call
  // site passes only `reason`, unaffected.
  const addElimination = (reason, extra = {}) => {
    setActiveSheetField({
      eliminations: [
        ...(activeSheet?.eliminations ?? []),
        { id: crypto.randomUUID(), reason, ...extra },
      ],
    });
  };

  // #1831 AC4 — a 3rd-error elimination is removable like a manual one, but
  // since it came off the same occurrence schedule as errors[], removing it
  // must also re-tier whatever errors[] entries remain against that
  // schedule. Uses retierErrorOccurrences (not retierRuleErrors) so the
  // OTHER direction is covered too: if other fromError eliminations remain
  // after this removal, their occurrence may also shift and need
  // re-deriving, same as removeError below.
  const removeElimination = (id) => {
    const target = (activeSheet?.eliminations ?? []).find((e) => e.id === id);
    const nextEliminationsRaw = (activeSheet?.eliminations ?? []).filter((e) => e.id !== id);
    if (target?.fromError) {
      const { errors, eliminations } = retierErrorOccurrences(
        activeSheet?.errors ?? [],
        nextEliminationsRaw,
        errorTiers,
      );
      setActiveSheetField({ errors, eliminations });
    } else {
      setActiveSheetField({ eliminations: nextEliminationsRaw });
    }
  };

  // #1068 — sheet-level errors-of-course / deductions (score-common's
  // `errors` field, #994's buildDeductionItems already renders these
  // read-only; this is the editable counterpart). entry is
  // { label, points|percent, movementNumber }.
  const addError = (entry) => {
    setActiveSheetField({
      errors: [...(activeSheet?.errors ?? []), { id: crypto.randomUUID(), ...entry }],
    });
  };

  // #1756/#752 - removing an error doesn't just drop it. Every remaining
  // rule-resolved error's occurrence shifts down by one, so a former 2nd
  // error becomes the 1st and must drop to the 1st-tier deduction. This is
  // scribe-fe's semantics, via the same shared re-tier path; manual
  // overrides pass through untouched.
  // #1831 (mirror of AC4) — a fromError elimination is part of the SAME
  // occurrence schedule, so removing an ordinary error can knock a
  // previously-eliminating 3rd occurrence back down to a non-eliminating
  // tier. retierErrorOccurrences re-derives BOTH arrays together instead of
  // retiering errors[] alone and leaving a stale elimination in place.
  const removeError = (id) => {
    const nextErrorsRaw = (activeSheet?.errors ?? []).filter((e) => e.id !== id);
    const { errors, eliminations } = retierErrorOccurrences(
      nextErrorsRaw,
      activeSheet?.eliminations ?? [],
      errorTiers,
    );
    setActiveSheetField({ errors, eliminations });
  };

  // #1068 — per-movement point deductions (#209/#922, movementScores[].
  // deductions), previously read-only via buildDeductionItems. movementScores
  // is already an allowed draft key, so this reuses setMovementScore's
  // existing patch-merge, same as a remarks edit does.
  const addMovementDeduction = (movementNumber, entry) => {
    const current = (activeSheet?.movementScores ?? []).find((m) => m.movementNumber === movementNumber);
    setMovementScore(movementNumber, {
      deductions: [...(current?.deductions ?? []), { id: crypto.randomUUID(), ...entry }],
    });
  };

  const removeMovementDeduction = (movementNumber, id) => {
    const current = (activeSheet?.movementScores ?? []).find((m) => m.movementNumber === movementNumber);
    setMovementScore(movementNumber, {
      deductions: (current?.deductions ?? []).filter((d) => d.id !== id),
    });
  };

  // #1067 — draft-path mutators for a CONFIRMED/SIGNED sheet. Each one
  // computes the field's next FULL value (draft PUT is a full-replace per
  // field, mirroring the direct-edit path above), stores it optimistically in
  // draftFields for instant UI feedback, and schedules the server push below.
  // A field is only ever added to draftFields when its value actually differs
  // from the committed sheet, so "key present in draftFields" stays a valid
  // "this field changed" check for the row indicators.
  const draftPushTimer = useRef(null);

  useEffect(
    () => () => {
      if (draftPushTimer.current) clearTimeout(draftPushTimer.current);
    },
    [],
  );

  // Ensure the sheet is in the editing state (idempotent — a second call, or
  // a second admin, just gets the sheet back as-is per #1064), then upsert
  // the draft. A fixed, non-user-facing reason: the admin never types a
  // reason to START editing, only to COMMIT (§4.4) — that's the one
  // meaningful reason capture this flow keeps.
  const pushDraftFields = (fields, { immediate = false } = {}) => {
    if (draftPushTimer.current) {
      clearTimeout(draftPushTimer.current);
      draftPushTimer.current = null;
    }
    const run = async () => {
      if (!activeSheet) return;
      // Nothing changed and the sheet isn't already in the editing state:
      // this is a no-op field blur (an admin merely clicked into a comment
      // box and back out, or the debounce fired after a change that already
      // reverted to the committed value). Skip unlock/PUT entirely — the
      // ordinary read path must never unlock a sheet on its own.
      if (Object.keys(fields).length === 0 && !activeSheet.editingStartedAt) return;
      try {
        setDraftSyncError(null);
        if (!activeSheet.editingStartedAt) {
          const unlockedSheet = unwrap(
            await scoreApi.scoreSheets.unlockScoreSheet(activeSheet.id, {
              reason: t("rideEditor.autoUnlockReason"),
            }),
          );
          setSheets((prev) => prev.map((s) => (s.id === unlockedSheet.id ? unlockedSheet : s)));
        }
        // Defense in depth: only ever send the allowed draft keys, even
        // though every caller already constructs `fields` from that same
        // set. #1754 — draftFieldKeysFor(test) widens this to
        // percentage/observationMarks ONLY when `test` (closed over from
        // the render this handler was created in — see the module-level
        // comment on toBody for why that's safe) is a judge-percentage
        // sheet; score-api's own draft schema enforces the same boundary
        // independently, so this is defense in depth, not the real gate.
        const scopedFields = {};
        for (const key of draftFieldKeysFor(test)) {
          if (key in fields) scopedFields[key] = fields[key];
        }
        await scoreApi.scoreSheets.putScoreSheetDraft(activeSheet.id, { fields: scopedFields });
      } catch (err) {
        setDraftSyncError(err);
      }
    };
    if (immediate) {
      run();
    } else {
      draftPushTimer.current = setTimeout(run, 400);
    }
  };

  function fieldValueEqual(key, a, b) {
    const STRING_FIELDS = ["generalRemarks"];
    // #1754 — observationMarks is a keyed object like collectives, not an
    // array; percentage is a nullable scalar (see score-common's
    // scoreSheetSchema.percentage), so its "no committed value" default is
    // `null`, not `[]` — matching `place`'s existing nullable pattern
    // rather than falling into the array default every other numeric-ish
    // field here uses.
    const OBJECT_FIELDS = ["collectives", "observationMarks"];
    if (key === "percentage") return JSON.stringify(a) === JSON.stringify(b ?? null);
    return JSON.stringify(a) === JSON.stringify(b ?? (STRING_FIELDS.includes(key) ? "" : OBJECT_FIELDS.includes(key) ? {} : []));
  }

  // Drops a field out of the draft entirely once it matches the committed
  // sheet again (e.g. an admin edits a remark then edits it back) — keeps
  // hasUnsavedEdits and the per-row indicators honest.
  function withDraftField(key, nextValue) {
    const next = { ...draftFields };
    if (fieldValueEqual(key, nextValue, activeSheet?.[key])) {
      delete next[key];
    } else {
      next[key] = nextValue;
    }
    return next;
  }

  const setDraftMovementScore = (movementNumber, patch, opts) => {
    const working = displaySheet?.movementScores ?? [];
    const idx = working.findIndex((m) => m.movementNumber === movementNumber);
    const nextEntry = idx >= 0 ? { ...working[idx], ...patch, movementNumber } : { movementNumber, ...patch };
    const nextMovementScores =
      idx >= 0 ? working.map((m, i) => (i === idx ? nextEntry : m)) : [...working, nextEntry];
    const next = withDraftField("movementScores", nextMovementScores);
    setDraftFields(next);
    pushDraftFields(next, opts);
  };

  const handleDraftMovementScoreChange = (movementNumber, value) => {
    if (value === "") {
      const nextMovementScores = (displaySheet?.movementScores ?? []).filter(
        (m) => m.movementNumber !== movementNumber,
      );
      const next = withDraftField("movementScores", nextMovementScores);
      setDraftFields(next);
      pushDraftFields(next, { immediate: true });
    } else {
      setDraftMovementScore(movementNumber, { score: Number(value) }, { immediate: true });
    }
  };

  const setDraftCollective = (key, patch, opts) => {
    const prevEntry = displaySheet?.collectives?.[key] ?? {};
    const nextCollectives = { ...(displaySheet?.collectives ?? {}), [key]: { ...prevEntry, ...patch } };
    const next = withDraftField("collectives", nextCollectives);
    setDraftFields(next);
    pushDraftFields(next, opts);
  };

  const setDraftGeneralRemarks = (value) => {
    const next = withDraftField("generalRemarks", value);
    setDraftFields(next);
    pushDraftFields(next);
  };

  // #1754 — class-grid draft mutators (judge-percentage sheets only; see
  // isJudgePercentageTest/JUDGE_PERCENTAGE_DRAFT_FIELD_KEYS above). `value`
  // is already the field's next FULL value (a number or `null` to clear —
  // see score-common's scoreSheetSchema.percentage), matching the direct
  // OPEN-state mutators below rather than a raw string. Pushed immediately
  // (not debounced) since both callers commit on blur, not on every
  // keystroke — same convention setDraftMovementScore's immediate option
  // uses for its own blur-triggered callers.
  const setDraftPercentage = (value) => {
    const next = withDraftField("percentage", value);
    setDraftFields(next);
    pushDraftFields(next, { immediate: true });
  };

  const setDraftObservationMarks = (nextMarks) => {
    const next = withDraftField("observationMarks", nextMarks);
    setDraftFields(next);
    pushDraftFields(next, { immediate: true });
  };

  // #1754 — commits the buffered percentageDraft on blur, to EITHER the
  // OPEN-state direct edit (setActiveSheetField, applied on the next Save
  // click) or the CONFIRMED/SIGNED draft path (setDraftPercentage), same
  // branch every other field in this file makes on sheetState. Empty string
  // commits `null` (score-common's scoreSheetSchema.percentage is nullable
  // for exactly this — an omitted PUT/draft field means "unchanged", so
  // clearing needs an explicit value to send), matching commitPlace's
  // established clear-with-null contract in scribe-fe's ClassGridRow.
  const commitPercentage = (raw) => {
    const next = raw === "" ? null : Number(raw);
    if (raw !== "" && Number.isNaN(next)) {
      setPercentageDraft(undefined);
      return;
    }
    if (sheetState === "OPEN") {
      setActiveSheetField({ percentage: next });
    } else {
      setDraftPercentage(next);
    }
    setPercentageDraft(undefined);
  };

  // #1754 — same branch, for one observation cell. Deletes the key on
  // clear rather than storing `undefined` (observationMarks is a
  // z.record(z.string(), z.number()) — see scoreSheetSchema — an explicit
  // undefined value wouldn't round-trip through JSON, exactly like a
  // cleared movement score drops its entry entirely, see
  // handleMovementScoreChange above).
  const commitObservationMark = (key, raw) => {
    const marks = { ...(displaySheet?.observationMarks ?? {}) };
    if (raw === "") {
      delete marks[key];
    } else {
      const num = Number(raw);
      if (Number.isNaN(num)) {
        setObsDrafts((prev) => {
          const rest = { ...prev };
          delete rest[key];
          return rest;
        });
        return;
      }
      marks[key] = num;
    }
    if (sheetState === "OPEN") {
      setActiveSheetField({ observationMarks: marks });
    } else {
      setDraftObservationMarks(marks);
    }
    setObsDrafts((prev) => {
      const rest = { ...prev };
      delete rest[key];
      return rest;
    });
  };

  // #1831 — draft-path counterpart of addElimination above (`extra` carries
  // fromError:true for an eliminate-tier occurrence).
  const addDraftElimination = (reason, extra = {}) => {
    const nextEliminations = [
      ...(displaySheet?.eliminations ?? []),
      { id: crypto.randomUUID(), reason, ...extra },
    ];
    const next = withDraftField("eliminations", nextEliminations);
    setDraftFields(next);
    pushDraftFields(next, { immediate: true });
  };

  // #1831 AC4 — draft-path counterpart of removeElimination above: removing
  // a fromError elimination re-tiers the remaining errors[] too, via the
  // same combined-occurrence function removeError below uses.
  const removeDraftElimination = (id) => {
    const target = (displaySheet?.eliminations ?? []).find((e) => e.id === id);
    const nextEliminationsRaw = (displaySheet?.eliminations ?? []).filter((e) => e.id !== id);
    const next = withDraftField("eliminations", nextEliminationsRaw);
    if (target?.fromError) {
      const { errors, eliminations } = retierErrorOccurrences(
        displaySheet?.errors ?? [],
        nextEliminationsRaw,
        errorTiers,
      );
      next.errors = errors;
      next.eliminations = eliminations;
    }
    setDraftFields(next);
    pushDraftFields(next, { immediate: true });
  };

  // #1068 — draft-path counterparts of addError/removeError above.
  const addDraftError = (entry) => {
    const nextErrors = [...(displaySheet?.errors ?? []), { id: crypto.randomUUID(), ...entry }];
    const next = withDraftField("errors", nextErrors);
    setDraftFields(next);
    pushDraftFields(next, { immediate: true });
  };

  // #1756 - same re-tier as removeError above, on the draft path.
  // #1831 (mirror of AC4) — removeError's combined-occurrence re-tier
  // (retierErrorOccurrences), so a stale fromError elimination doesn't
  // survive dropping below the eliminate tier here either.
  const removeDraftError = (id) => {
    const nextErrorsRaw = (displaySheet?.errors ?? []).filter((e) => e.id !== id);
    const { errors, eliminations } = retierErrorOccurrences(
      nextErrorsRaw,
      displaySheet?.eliminations ?? [],
      errorTiers,
    );
    const next = withDraftField("errors", errors);
    next.eliminations = eliminations;
    setDraftFields(next);
    pushDraftFields(next, { immediate: true });
  };

  // #1068 — draft-path counterparts of addMovementDeduction/
  // removeMovementDeduction above, reusing setDraftMovementScore's existing
  // patch-merge (same as a draft remarks edit).
  const addDraftMovementDeduction = (movementNumber, entry) => {
    const current = (displaySheet?.movementScores ?? []).find((m) => m.movementNumber === movementNumber);
    setDraftMovementScore(
      movementNumber,
      { deductions: [...(current?.deductions ?? []), { id: crypto.randomUUID(), ...entry }] },
      { immediate: true },
    );
  };

  const removeDraftMovementDeduction = (movementNumber, id) => {
    const current = (displaySheet?.movementScores ?? []).find((m) => m.movementNumber === movementNumber);
    setDraftMovementScore(
      movementNumber,
      { deductions: (current?.deductions ?? []).filter((d) => d.id !== id) },
      { immediate: true },
    );
  };

  // Abandon the pending edits with no changes applied — the committed sheet
  // was never touched, so there's nothing to roll back on it either.
  const handleDiscardDraft = async () => {
    if (!activeSheet) return;
    // Same reasoning as confirmCommit below: a pending debounced draft PUT
    // landing AFTER discard clears the editing state would 409
    // (sheet_not_editing) and raise a bogus draftSyncError banner on an
    // otherwise-successful discard — cancel it first.
    if (draftPushTimer.current) {
      clearTimeout(draftPushTimer.current);
      draftPushTimer.current = null;
    }
    try {
      setDiscarding(true);
      setDraftSyncError(null);
      const restored = unwrap(await scoreApi.scoreSheets.discardScoreSheetDraft(activeSheet.id));
      setSheets((prev) => prev.map((s) => (s.id === restored.id ? restored : s)));
      setDraftFields({});
    } catch (err) {
      setDraftSyncError(err);
    } finally {
      setDiscarding(false);
    }
  };

  // AC4/AC3 — commit: apply the draft, re-establish releasable status, no
  // fresh signature. A short reason is captured (the one thing the audit
  // trail never drops); the resend checkbox (default off) is a SEPARATE call
  // to the existing #846 resend path after a successful commit, since
  // commitScoreSheetEdit's request body is reason-only.
  const openCommitDialog = () => {
    setPlacingImpactNotice(null);
    setCommitReasonDraft("");
    setCommitResend(false);
    setCommitError(null);
    setCommitConflict(false);
    setResendAfterCommitError(false);
    setCommitOpen(true);
  };

  const confirmCommit = async () => {
    // #1755 continuation — driver decision B: same defensive gate as Save
    // and Complete & Sign. The dialog can only be opened from the
    // Commit-changes button, which is already disabled while hasInvalidScore
    // (below), so this is belt-and-braces, not a reachable path today — kept
    // for consistency with the other two write paths rather than leaving it
    // as the one asymmetric case.
    if (!activeSheet || !commitReasonDraft.trim() || hasInvalidScore) return;
    // A pending debounced draft PUT landing AFTER commit clears the editing
    // state would 409 (sheet_not_editing) and raise a bogus draftSyncError
    // banner on an otherwise-successful commit — cancel it first.
    if (draftPushTimer.current) {
      clearTimeout(draftPushTimer.current);
      draftPushTimer.current = null;
    }
    try {
      setCommitting(true);
      setCommitError(null);
      setCommitConflict(false);
      setResendAfterCommitError(false);
      // A commit is valid even with zero pending edits (a no-op session is
      // still an audited open->commit cycle, §4.4) — ensure the sheet is in
      // the editing state first, same as any other field edit would.
      if (!activeSheet.editingStartedAt) {
        unwrap(
          await scoreApi.scoreSheets.unlockScoreSheet(activeSheet.id, {
            reason: t("rideEditor.autoUnlockReason"),
          }),
        );
      }
      const committed = unwrap(
        await scoreApi.scoreSheets.commitScoreSheetEdit(activeSheet.id, {
          reason: commitReasonDraft.trim(),
        }),
      );
      // #1069 — §4.8: surface the class placing-impact signal the commit
      // just reported. Rendered conditionally below (changed or error
      // only) — a changed:false impact intentionally renders nothing.
      setPlacingImpactNotice(committed?.placingImpact ?? null);
      if (commitResend && rider?.email) {
        try {
          await resend(rideId, rider.email);
        } catch {
          setResendAfterCommitError(true);
        }
      }
      setCommitOpen(false);
      setDraftFields({});
      applyDetail(await loadDetail());
      refreshStatus();
    } catch (err) {
      if (err?.response?.data?.code === "concurrent_modification") {
        setCommitConflict(true);
      }
      setCommitError(err);
    } finally {
      setCommitting(false);
    }
  };

  const reloadAfterConflict = async () => {
    setCommitOpen(false);
    applyDetail(await loadDetail());
  };

  // #2411 AC6 — numericOnly is read straight off each stamped-at-create
  // sheet (see #2406's "stamped once, kept forever" rule), never re-derived
  // from klass/the class's paperEntry flag. A ride with zero sheets has
  // nothing to switch on and keeps the existing "no sheets yet" alert.
  const allNumericOnly = sheets.length > 0 && sheets.every((s) => s.numericOnly === true);

  // #2411 — per-booth direct-edit patch for the numeric-only grid, keyed by
  // an explicit sheetId. This is NEW code (not a change to
  // setMovementScore/setCollective/setActiveSheetField above, which stay
  // hardcoded to the single activeSheetId the tabbed editor keeps): the
  // grid needs several booths editable at once, which those single-sheet
  // functions were never built for, and the card's own note says not to
  // refactor them.
  const patchGridSheet = (sheetId, patch) => {
    setSheets((prev) => prev.map((s) => (s.id === sheetId ? { ...s, ...patch } : s)));
  };

  // #2411 — the grid's per-booth Save, scoped to one sheet id instead of
  // handleSave's activeSheet. Otherwise identical: same toBody(sheet, test)
  // shape, same updateScoreSheet call through the generated score-common
  // client, same "merge only the saved sheet back in" so an unsaved edit on
  // another booth's column survives. See the gridSavingSheetId/
  // gridSaveErrors doc comment above for why this has its own state.
  //
  // #2466 — returns the saved sheet on success or null on failure, so
  // saveAllGridSheets (below) can fan this out across every booth and
  // collect which ones failed (a plain `!ok` check still works against
  // either a truthy sheet object or null), and #2467's confirmGridSheet can
  // gate against the freshly-PERSISTED sheet it gets back rather than
  // re-reading stale local state.
  const saveGridSheet = async (sheetId) => {
    const sheet = sheets.find((s) => s.id === sheetId);
    if (!sheet) return null;
    setGridSavingSheetId(sheetId);
    setGridSaveErrors((prev) => ({ ...prev, [sheetId]: null }));
    try {
      const updated = unwrap(await scoreApi.scoreSheets.updateScoreSheet(sheet.id, toBody(sheet, test)));
      setSheets((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
      return updated;
    } catch (err) {
      setGridSaveErrors((prev) => ({ ...prev, [sheetId]: err }));
      return null;
    } finally {
      setGridSavingSheetId(null);
    }
  };

  // #2466 — the grid's own "Save" action: fan out saveGridSheet across every
  // OPEN booth on the panel (a CONFIRMED/SIGNED sheet has no Save button and
  // nothing pending, same filter the per-booth Save row already renders
  // against). Sequential, not Promise.all: saveGridSheet already isolates
  // each booth's own try/catch, so nothing about correctness needs
  // concurrency, and running one at a time means gridSavingSheetId (a single
  // id, not a set) still lights up the right column while it's this
  // action's turn. AC2/AC3 — the result names every booth that failed by
  // its judge position, and a booth that succeeded keeps the sheet
  // saveGridSheet already merged back into `sheets` above; nothing here
  // touches a booth's error slot unless that booth's own call rejected.
  const saveAllGridSheets = async () => {
    const openSheets = sheets.filter((s) => deriveSheetState(s) === "OPEN");
    if (openSheets.length === 0) return;
    setGridSavingAll(true);
    setGridSaveAllResult(null);
    const failedPositions = [];
    for (const s of openSheets) {
      const ok = await saveGridSheet(s.id);
      if (!ok) {
        const position = judgePosition(klass, s.judgeId) || s.judgeId;
        failedPositions.push(t("rideEditor.judgeLabel", { position }));
      }
    }
    setGridSaveAllResult({ failedPositions });
    setGridSavingAll(false);
  };

  // #2467 — the grid's per-booth Confirm, scoped to one sheet id (same
  // shape as saveGridSheet above: goes through the generated score-common
  // client, merges only the confirmed sheet back into `sheets` so a
  // sibling booth's unsaved edit survives). Returns a boolean for the same
  // reason saveGridSheet does — confirmAllGridSheets below fans this out
  // and finds out which booths failed, without changing what an
  // individual Confirm click itself does.
  //
  // Judge D1 (BLOCKING) — grid edits (patchGridSheet) only ever touch local
  // `sheets` state; nothing reaches the server until Save. The original cut
  // of this function ran isSheetConfirmable against that in-memory sheet
  // and then confirmed straight away, so freshly-typed UNSAVED marks passed
  // the gate while the row confirmScoreSheetMarks actually freezes (the
  // DynamoDB one) still had the old hole in it — score-common's
  // calculateScoreSheet counts a missing movement as 0 toward totalPoints
  // but still counts it toward maxPoints, so the frozen result is
  // plausible-but-too-low, not blank, and undetectable downstream on a
  // sheet type with no un-confirm route. Fix: always save first (mirrors
  // handleCompleteAndSign's updateScoreSheet-then-confirm order below, the
  // in-file precedent for this exact sequencing), and gate + confirm
  // against the PERSISTED response, never the stale local `sheet`. A save
  // failure blocks confirm outright — there is nothing safe to gate.
  // AC2 — isSheetConfirmable is still checked BEFORE the confirm call: a
  // non-confirmable booth's save can succeed but its confirm is refused
  // locally and the confirm-marks API is never called for it. AC5 —
  // completedAt (and whether this sheet goes FINAL) is whatever
  // confirmScoreSheetMarks itself stamps; nothing here adds a second
  // finality rule.
  const confirmGridSheet = async (sheetId) => {
    if (!sheets.some((s) => s.id === sheetId)) return false;
    setGridConfirmErrors((prev) => ({ ...prev, [sheetId]: null }));
    const saved = await saveGridSheet(sheetId);
    if (!saved) {
      setGridConfirmErrors((prev) => ({ ...prev, [sheetId]: { reason: "save_failed" } }));
      return false;
    }
    if (!isSheetConfirmable(saved, test, ride)) {
      setGridConfirmErrors((prev) => ({ ...prev, [sheetId]: { reason: "missing_score" } }));
      return false;
    }
    setGridConfirmingSheetId(sheetId);
    try {
      const confirmed = unwrap(await scoreApi.scoreSheets.confirmScoreSheetMarks(saved.id));
      setSheets((prev) => prev.map((s) => (s.id === confirmed.id ? confirmed : s)));
      // A confirm on a noSignatureNeeded sheet can take the RIDE to FINAL
      // in this one call — refresh the header's RideStatusBadge so it
      // doesn't sit stale until some unrelated re-render.
      refreshStatus();
      return true;
    } catch (err) {
      setGridConfirmErrors((prev) => ({ ...prev, [sheetId]: { reason: "request_failed", err } }));
      return false;
    } finally {
      setGridConfirmingSheetId(null);
    }
  };

  // #2467 — confirm-all: fan out confirmGridSheet across every OPEN booth,
  // sequential for the same reason saveAllGridSheets above is (each
  // booth's own try/catch already isolates it; gridConfirmingSheetId is a
  // single id, not a set, so it lights up the right column in turn). AC3 —
  // a booth that fails is left OPEN (confirmGridSheet never touches
  // `sheets` for it); a booth that succeeds is already merged back in by
  // confirmGridSheet itself, so nothing here can undo or overwrite a
  // sibling's state. Unlike saveAllGridSheets, no failedPositions array is
  // stored here — the grid derives its failure list live off
  // gridConfirmErrors on every render (see that state's doc comment
  // above), so this only needs to flip the "a run happened" flag.
  // Judge N2 — that live derivation genuinely avoids #2466's stale-banner
  // bug (a later individual retry that clears one booth's own entry drops
  // it from this banner for free), but it is not scoped to one run: after a
  // Confirm All finishes clean, a LATER individual booth's own Confirm
  // failing on that same page view still lights the page-level banner too,
  // since both read off the one shared gridConfirmErrors map. Acceptable —
  // it never UNDER-reports (a real failure is still visible either way).
  const confirmAllGridSheets = async () => {
    const openSheets = sheets.filter((s) => deriveSheetState(s) === "OPEN");
    if (openSheets.length === 0) return;
    setGridConfirmingAll(true);
    for (const s of openSheets) {
      await confirmGridSheet(s.id);
    }
    setGridConfirmAllRan(true);
    setGridConfirmingAll(false);
  };

  // #2410 — create every score sheet this ride's class panel expects but
  // doesn't have yet (missingPanelEntries above). Sequential, not
  // Promise.all, same reasoning as saveAllGridSheets/confirmAllGridSheets:
  // nothing about correctness needs concurrency, and it keeps each
  // position's own try/catch isolated. testId mirrors whatever this ride's
  // OTHER sheets already carry when one exists, falling back to the ride's
  // own testId (ride.testId — the authoritative per-ride test, see
  // getRideTestName's comment below) when this is the very first sheet on
  // the ride. numericOnly/noSignatureNeeded are deliberately NOT sent:
  // score-api's createScoreSheet handler (#2406) always derives and stamps
  // both itself from the class's paperEntry flag and ignores whatever a
  // client sends for them, so there is nothing for this client to mirror.
  //
  // #2409 duplicate guard: a 409 duplicate_score_sheet (another client, or
  // a scribe's tablet, won the create race for this exact position) is
  // ADOPTED via getScoreSheet, never surfaced as a failure — the sheet
  // this button wanted to exist now does. A real failure is recorded by
  // judge POSITION (not sheet id — a failed create has no sheet id) into
  // missingSheetCreateErrors; the banner below derives its message live
  // off that map every render, never a stored result object (the #2466
  // judge's stale-banner finding).
  const createMissingScoreSheets = async () => {
    if (missingPanelEntries.length === 0) return;
    const createTestId = sheets[0]?.testId ?? ride?.testId ?? null;
    if (!createTestId) return;
    setCreatingMissingSheets(true);
    setMissingSheetCreateErrors({});
    const errors = {};
    const createdSheets = [];
    for (const entry of missingPanelEntries) {
      try {
        const created = unwrap(
          await scoreApi.scoreSheets.createScoreSheet({
            rideId,
            judgeId: entry.position,
            testId: createTestId,
            // #2590 — this is an admin backfilling a missing panel
            // position, not a scribe starting to score live: this ride is
            // not necessarily what any ring is actually running right now.
            // score-api's createScoreSheet gates recordRingScoringActivity
            // on this flag (see its #2590 comment) so this create never
            // moves which ride the scoreboard shows as current.
            skipRingState: true,
          })
        );
        createdSheets.push(created);
      } catch (err) {
        if (err?.response?.status === 409 && err?.response?.data?.code === "duplicate_score_sheet") {
          const existingSheetId = err.response.data?.data?.existingSheetId;
          try {
            createdSheets.push(unwrap(await scoreApi.scoreSheets.getScoreSheet(existingSheetId)));
          } catch (adoptErr) {
            errors[entry.position] = adoptErr;
          }
        } else {
          errors[entry.position] = err;
        }
      }
    }
    if (createdSheets.length > 0) {
      setSheets((prev) => {
        const byId = new Map(prev.map((s) => [s.id, s]));
        for (const s of createdSheets) byId.set(s.id, s);
        return sortSheetsByDisplayPosition(klass, [...byId.values()]);
      });
      setActiveSheetId((prev) => prev ?? createdSheets[0]?.id ?? null);

      // #2589 — a paper ride's very first sheets are created right here, on
      // demand, never fetched by loadDetail: that effect only builds `tests`
      // from whatever sheets already existed at page load (see #915's
      // comment above `test` below), and a ride with zero sheets loads with
      // `tests` empty. NumericOnlyScoreGrid reads `test?.movements` straight
      // off that map (RideEditor.jsx:2805, NumericOnlyScoreGrid.jsx:140), so
      // a testId this map has never seen renders zero movement rows with no
      // error at all — exactly the reported symptom. Fetch and merge in
      // whatever test defs this create batch introduced.
      const missingTestIds = [
        ...new Set(createdSheets.map((s) => s.testId).filter(Boolean)),
      ].filter((id) => !(id in tests));
      if (missingTestIds.length > 0) {
        // #2622 — same 404-vs-anything-else split as loadDetail above: a
        // non-404 getTest failure here must not render as "missing test"
        // either.
        const fetchedTests = await Promise.all(
          missingTestIds.map(async (id) => {
            try {
              return [id, unwrap(await scoreApi.tests.getTest(id)), null];
            } catch (err) {
              return [id, null, classifyTestFetchError(err)];
            }
          })
        );
        setTests((prev) => ({
          ...prev,
          ...Object.fromEntries(fetchedTests.map(([id, test]) => [id, test])),
        }));
        const newTestErrors = Object.fromEntries(
          fetchedTests.filter(([, , err]) => err).map(([id, , err]) => [id, err])
        );
        if (Object.keys(newTestErrors).length > 0) {
          setTestErrors((prev) => ({ ...prev, ...newTestErrors }));
        }
      }
    }
    setMissingSheetCreateErrors(errors);
    setCreatingMissingSheets(false);
  };

  // AC3 — Save persists the FULL active sheet (toBody keeps completedAt so a
  // completed sheet's edit never reverts the Ride to PARTIAL). Only the saved
  // sheet is updated locally, so unsaved edits on other judges' tabs survive.
  // #1067 — this is the OPEN-sheet path only now: a CONFIRMED/SIGNED sheet
  // is edited through the unlock/draft/commit lifecycle above instead (Save
  // and Complete & Sign only render for sheetState === "OPEN" below).
  const handleSave = async () => {
    // #1755 continuation — driver decision (card comment): block Save while
    // any score cell is showing a strict-mode rejection, so the model can
    // never commit the stale (last-known-good) value while the UI still
    // shows that cell in error. Belt-and-braces with the Save button's own
    // `disabled` below, since a click can in principle race a state update.
    if (!activeSheet || hasInvalidScore) return;
    try {
      setSavingSheet(true);
      setSaveError(null);
      const updated = unwrap(
        await scoreApi.scoreSheets.updateScoreSheet(activeSheet.id, toBody(activeSheet, test)),
      );
      setSheets((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
    } catch (err) {
      setSaveError(err);
    } finally {
      setSavingSheet(false);
    }
  };

  // #846 — prepopulate from the rider's on-file email every time the dialog
  // opens, so a previous edit/send doesn't linger into the next one.
  const openResendDialog = () => {
    setResendEmail(rider?.email || "");
    setResendError(null);
    setResendSuccess(false);
    setResendOpen(true);
  };

  const resendErrorMessage = (err) =>
    t(RESEND_ERROR_KEYS[err?.response?.data?.code] || "rideEditor.resendError");

  const confirmResend = async () => {
    const email = resendEmail.trim();
    if (!email) return;
    try {
      setResendError(null);
      await resend(rideId, email);
      setResendSuccess(true);
      // #918 — the resend route awaits the send server-side, so the status
      // is already settled by the time this resolves; refresh the indicator
      // to reflect what actually happened (may still show pending for a
      // brand-new request whose render hasn't finished).
      refreshSheetSentStatus();
    } catch (err) {
      setResendError(err);
    }
  };

  const openSignDialog = () => {
    setCompleteError(null);
    setSignOpen(true);
  };

  // #850 — no per-sheet signature to gather anymore (card #733's show-wide
  // model, mirroring scribe-fe's ScoringForm finalize()). Complete & Sign is
  // already blocked by `hasMissingSignature` above while any class judge
  // lacks a show-wide signature on file, so by the time this fires
  // completeScoreSheet can simply adopt the judge's stored pointer
  // server-side (offloadSignatureOnComplete's USE-AS-IS branch). Still saves
  // the sheet body first — toBody() strips judgeSignature (SERVER_MANAGED),
  // so this never sends an inline signature and never hits the REPLACE
  // branch that would overwrite the judge's org-wide current pointer.
  const handleCompleteAndSign = async () => {
    // #1755 continuation — driver decision B (card comment): block this the
    // same as Save/commit. This is the irreversible path (it freezes and
    // signs the sheet), so writing the stale toBody() while a cell still
    // shows a strict-mode rejection is the worst version of the bug the
    // driver called out. Belt-and-braces with the button's own `disabled`.
    if (!activeSheet || hasMissingSignature || hasInvalidScore) return;
    try {
      setCompleting(true);
      setCompleteError(null);
      await scoreApi.scoreSheets.updateScoreSheet(activeSheet.id, toBody(activeSheet, test));
      // #2423 — a panel-consensus Test's completeScoreSheet is gated on
      // signatures[] (isPanelFullySigned, #1748), which only #1751's
      // POST /scoresheet/:id/panel-signature ever writes. scoreadmin never
      // ran that ceremony, so every panel-consensus sheet 409'd here
      // forever (panel_not_fully_signed) no matter how many times Complete
      // & Sign was clicked. Run it now, resolving each seat's roster judge
      // id from the SAME judgePanel this component already computes for the
      // #2018 judge-summary panel (resolveClassPanel), never a local guess.
      if (isPanelConsensusTest(test)) {
        // signScoreSheetPanel 409s (marks_not_confirmed) until the sheet's
        // marks are confirmed; confirmScoreSheetMarks is idempotent ONLY in
        // the sense that a repeat call on an already-confirmed sheet is a
        // no-op 200 -- that is what makes it safe to call unconditionally
        // here rather than trying to infer whether a PRIOR, partially-failed
        // Complete & Sign attempt already confirmed it. It is NOT reversible:
        // there is no un-confirm route anywhere in the API, and the Complete
        // & Sign button renders for any sheet with !completedAt, including a
        // still-OPEN one, so for an open sheet this call is a genuine
        // one-way freeze. If it lands and a later step in this block (a
        // panel-signature call, or completeScoreSheet itself) then fails,
        // the sheet is left stuck CONFIRMED with no code path back to OPEN.
        const confirmed = unwrap(await scoreApi.scoreSheets.confirmScoreSheetMarks(activeSheet.id));
        // AC2 — only sign seats missing from signatures[], and never
        // duplicate an already-signed judge: build the still-missing set
        // from the sheet's OWN signatures[] (post-confirm, so it reflects
        // any earlier partial signing), not from a locally-guessed list.
        const signedJudgeIds = new Set(
          (confirmed.signatures ?? []).map((s) => s?.judgeId).filter(Boolean),
        );
        const unsignedJudgeIds = [
          ...new Set(judgePanel.map((p) => p.judgeId).filter((id) => id && !signedJudgeIds.has(id))),
        ];
        for (const judgeId of unsignedJudgeIds) {
          // eslint-disable-next-line no-await-in-loop -- each seat's signature
          // must land before the next is requested: signScoreSheetPanel
          // merges into signatures[] by judgeId (mergePanelSignature), and
          // firing these concurrently would race the same PutCommand.
          await scoreApi.scoreSheets.signScoreSheetPanel(activeSheet.id, { judgeId });
        }
      }
      await scoreApi.scoreSheets.completeScoreSheet(activeSheet.id);
      setSignOpen(false);
      applyDetail(await loadDetail());
      refreshStatus();
    } catch (err) {
      setCompleteError(err);
    } finally {
      setCompleting(false);
    }
  };

  // AC6 — scratch stamps preScratchStatus server-side; un-scratch sends that
  // status back to revert (falling back to PENDING if it was never set).
  const handleScratchToggle = async () => {
    try {
      setScratchLoading(true);
      setScratchError(null);
      const nextStatus =
        ride.status === RIDE_STATUSES.SCRATCHED
          ? ride.preScratchStatus || RIDE_STATUSES.PENDING
          : RIDE_STATUSES.SCRATCHED;
      await scoreApi.rides.setRideStatus(rideId, { status: nextStatus });
      applyDetail(await loadDetail());
      refreshStatus();
    } catch (err) {
      setScratchError(err);
    } finally {
      setScratchLoading(false);
    }
  };

  // Card #2064 — mirrors handleScratchToggle's shape: toggles between
  // markRideExcused and undoRideExcused based on the currently-loaded
  // ride's own ride.excused.active (a ride-level field returned directly
  // by getRide, same as ride.noShow), then reloads detail + refreshes the
  // derived status so the zeroed/out-of-placing result shows immediately.
  const handleExcusedToggle = async () => {
    try {
      setExcusedLoading(true);
      setExcusedError(null);
      if (ride.excused?.active) {
        await scoreApi.rides.undoRideExcused(rideId);
      } else {
        await scoreApi.rides.markRideExcused(rideId);
      }
      applyDetail(await loadDetail());
      refreshStatus();
    } catch (err) {
      setExcusedError(err);
    } finally {
      setExcusedLoading(false);
    }
  };

  // #867 — tab title: once the ride has loaded, reflect its CNO/rider (same
  // degrade-gracefully-to-ride-number-then-"unnamed" order as `rideHeading`
  // below, minus the horse — CNO/rider alone is enough to tell tabs apart);
  // the plain "Rides" title beforehand, so the tab never shows "undefined".
  useDocumentTitle(
    ride
      ? t("title.ride", {
          who:
            [ride.competitorNumber, personName(rider) || ride.riderName]
              .filter((v) => v != null && v !== "")
              .join(" - ") ||
            (ride.rideNumber != null
              ? t("rideEditor.title", { number: ride.rideNumber })
              : t("rideEditor.unnamedRide")),
        })
      : t("title.rides"),
  );

  // AC8 — MD3 loading convention: shaped skeleton for the initial region.
  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <TableSkeleton rows={6} columns={3} />
      </Box>
    );
  }

  if (error || !ride) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error?.message || t("rideEditor.loadError")}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={goBackToPicker}
          data-testid="back-to-picker-button"
        >
          {t("rideEditor.backToPicker")}
        </Button>
      </Box>
    );
  }

  const test = activeSheet ? tests[activeSheet.testId] : null;
  // #2622 — set only when the getTest lookup for the active sheet's test
  // actually failed (non-404); undefined for a genuine "no such test" 404,
  // so the warning below keeps rendering rideEditor.noTest for that case.
  const testFetchError = activeSheet ? testErrors[activeSheet.testId] : null;
  const testDefAlert = testDefAlertProps(testFetchError);

  // #915 — the test NAME is resolved off ride.testId against the bundled
  // catalog directly (not the DDB `tests` map above, which requires an
  // active sheet and is what the "join misses" bug report was about).
  // Per-ride, never per-class: a ToC class can carry a different testId on
  // each ride.
  const rideTestName = getRideTestName(ride, catalogDefsById);

  // #576 — heading reads "[CNO] - [Rider] on [Horse]" (e.g. "142 - Jane Smith
  // on Beau"), degrading gracefully as fields drop out; falls back to the old
  // "Ride #N" only when CNO, rider and horse are all missing. #903 — a ride
  // can ALSO lack rideNumber in that same worst case (FVD never assigned it
  // a running-order position — see RideLink.jsx's comment), so guard against
  // rendering the literal "Ride #undefined": fall back one step further to
  // the generic "Unnamed ride" label rather than ever showing "undefined".
  const rideHeading = () => {
    const cno = ride.competitorNumber;
    const riderName = personName(rider) || ride.riderName;
    const horseName = horse?.name || ride.horseName;
    if (cno == null && !riderName && !horseName) {
      return ride.rideNumber != null
        ? t("rideEditor.title", { number: ride.rideNumber })
        : t("rideEditor.unnamedRide");
    }
    const left = [cno, riderName].filter((v) => v != null && v !== "").join(" - ");
    if (!horseName) return left;
    return left ? `${left} ${t("rideEditor.onHorse")} ${horseName}` : horseName;
  };

  // #864 — the rider/horse half of the subheading, joined separately from the
  // class part below (which is now a ClassLink and can't be flattened into a
  // single joined string).
  const subheadingRest = [personName(rider) || ride.riderName, horse?.name || ride.horseName]
    .filter(Boolean)
    .join(" · ");

  return (
    <Box sx={{ p: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={handleBackClick}
        sx={{ mb: 1 }}
        color="inherit"
        data-testid="back-to-picker-button"
      >
        {t("rideEditor.backToPicker")}
      </Button>

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
        <Typography variant="h4" color="text.primary">
          {rideHeading()}
        </Typography>
        <RideStatusBadge status={rideStatuses[rideId]} />
      </Stack>

      {/* #755 — welfare/utility flags (BLOOD/LAMENESS) raised by a scribe or
          judge for this ride. Silent when there are none (AC: absent flags
          don't clutter), matching the deductions-list precedent below. */}
      {utilityEvents.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{ mb: 1 }}
          data-testid="utility-events-list"
        >
          {utilityEvents.map((e) => {
            const cfg = UTILITY_EVENT_TYPE_BY_KEY[e.type];
            const time = e.raisedAt
              ? new Date(e.raisedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
              : null;
            return (
              <Chip
                key={e.id}
                data-testid="utility-event-chip"
                color={cfg?.severity ?? "warning"}
                label={
                  time
                    ? t("rideEditor.utilityEventChip", { label: cfg?.label ?? e.type, time })
                    : cfg?.label ?? e.type
                }
              />
            );
          })}
        </Stack>
      )}

      <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
        {/* #864 — shared ClassLink; klass is the full class doc, so this now
            shows classNumberLabel() (previously className-only, unlinked). */}
        <ClassLink classObj={klass} classId={ride.classId} fallback={ride.classId} />
        {subheadingRest && ` · ${subheadingRest}`}
      </Typography>

      {rideTestName && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} data-testid="ride-test-name">
          {t("rideEditor.test")}: {rideTestName}
        </Typography>
      )}

      {result && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} data-testid="ride-placing-line">
          {t("rideEditor.composite")}: {formatPercentage(result?.composite)}
          {" · "}
          {t("rideEditor.placing")}: {formatPlacing(t, result.placing)}
        </Typography>
      )}

      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        <Button variant="outlined" color="error" loading={scratchLoading} onClick={handleScratchToggle}>
          {ride.status === RIDE_STATUSES.SCRATCHED ? t("rideEditor.unscratch") : t("rideEditor.scratch")}
        </Button>
        {/* Card #2064 — Excused (DR123.3's fourth term reason), same shape
            as the Scratch toggle just above. */}
        <Button
          variant="outlined"
          color="error"
          loading={excusedLoading}
          onClick={handleExcusedToggle}
          data-testid="excuse-ride-button"
        >
          {ride.excused?.active ? t("rideEditor.unexcuseRide") : t("rideEditor.excuseRide")}
        </Button>
        {/* #2413 — runs + polls the score-sheet report in place (usePrintScoreSheet.js)
            instead of navigating to /reports; the tab opens synchronously in
            that hook, right here in the click, so a popup blocker doesn't eat
            it (report generation itself is necessarily async).
            #595 — hidden when the ride has no sheets (nothing to report on).
            #928 — a two-judge-section class missing a section judge blocks
            straight through to the dialog instead of printing immediately;
            the "print anyway" override runs the same in-place flow with
            overrideIncompleteJudges:true below. */}
        {sheets.length > 0 && (
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<AssessmentIcon />}
            data-testid="score-sheet-report-button"
            loading={printScoreSheetBusy}
            onClick={() =>
              missingJudgeSections.length > 0
                ? setJudgesBlockOpen(true)
                : printScoreSheet({ showId: ride.showId, rideId })
            }
          >
            {t("rideEditor.scoreSheetReport")}
          </Button>
        )}
        {/* #846 — gated the same as the report button above (a scoresheet
            exists to send) AND a resolved rider to attach the send to (no
            riderId means no rider identity for report-api's create-or-reuse
            row to key on). */}
        {sheets.length > 0 && ride.riderId && (
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<EmailIcon />}
            data-testid="resend-score-sheet-button"
            onClick={openResendDialog}
          >
            {t("rideEditor.resendScoreSheet")}
          </Button>
        )}
        {/* #947 — same gate as Resend above (a scoresheet + resolved rider),
            but opens the rider's permanent score page directly instead of
            emailing it. */}
        {sheets.length > 0 && ride.riderId && (
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<OpenInNewIcon />}
            loading={scorePageLinkLoading}
            data-testid="score-page-link-button"
            onClick={handleOpenScorePage}
          >
            {t("rideEditor.scorePageLink")}
          </Button>
        )}
        {/* #918 — proof/indicator that the rider was actually emailed this
            score sheet (not just that a send was attempted). Same gate as
            Resend/score-page-link — nothing to show without a resolved rider
            to have sent to. */}
        {sheets.length > 0 && ride.riderId && (
          <SheetSentChip statusData={sheetSentStatus} />
        )}
      </Stack>
      {scorePageLinkError && (
        <Alert severity="error" sx={{ mb: 1 }} data-testid="score-page-link-error">
          {t("rideEditor.scorePageLinkError")}
        </Alert>
      )}
      {/* #2413 AC4 — a render failure or a poll timeout clears the busy state
          and surfaces here; usePrintScoreSheet.js never returns silently to
          idle. */}
      {printScoreSheetErrorKind && (
        <Alert severity="error" sx={{ mb: 1 }} data-testid="print-score-sheet-error">
          {t(PRINT_ERROR_KEYS[printScoreSheetErrorKind] || "rideEditor.printReportError")}
        </Alert>
      )}
      {scratchError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {scratchError.message}
        </Alert>
      )}
      {excusedError && (
        <Alert severity="error" sx={{ mb: 1 }} data-testid="excuse-ride-error">
          {t("rideEditor.excuseError")}
        </Alert>
      )}

      {/* #2410 — "create this ride's missing score sheets". deriveRideStatus
          is a SUPPRESSION GATE here, not the missing-set source (that's
          missingPanelEntries above, off the class panel): a terminal ride
          (FINAL/SCRATCHED) never offers to create more sheets, even if the
          panel math says a position is missing. Rendered OUTSIDE the
          empty/numeric-grid/tabbed ternary below (AC3 — a two-judge class
          with one existing sheet must still show this, and that case falls
          into the tabbed branch, not the empty one) so it appears once
          regardless of which of the three sub-views is active. */}
      {(() => {
        const expectedJudgeCount = expectedJudgeCountForClass(klass);
        const sectionJudge = expectedSectionCountForClass(klass) != null;
        const consensus = isPanelConsensusTest(test);
        const derivedRideStatus = deriveRideStatus(ride, sheets, expectedJudgeCount, {
          sectionJudge,
          consensus,
        });
        const canCreateMissingSheets =
          missingPanelEntries.length > 0 &&
          derivedRideStatus !== DERIVED_RIDE_STATUSES.FINAL &&
          derivedRideStatus !== DERIVED_RIDE_STATUSES.SCRATCHED;
        if (!canCreateMissingSheets) return null;
        const failedPositions = Object.keys(missingSheetCreateErrors);
        return (
          <Stack spacing={1} sx={{ mt: 2, mb: 1 }}>
            <Box>
              <Button
                variant="contained"
                data-testid="create-missing-sheets-button"
                loading={creatingMissingSheets}
                onClick={createMissingScoreSheets}
              >
                {t("rideEditor.createMissingSheets", {
                  positions: missingPanelEntries.map((p) => p.position).join(", "),
                })}
              </Button>
            </Box>
            {failedPositions.length > 0 && (
              <Alert severity="error" data-testid="create-missing-sheets-error">
                {t("rideEditor.createMissingSheetsError", {
                  positions: failedPositions.join(", "),
                })}
              </Alert>
            )}
          </Stack>
        );
      })()}

      {sheets.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          {t("rideEditor.noSheets")}
        </Alert>
      ) : allNumericOnly && !correctingSheetId ? (
        // #2411 — GRID RENDER ONLY: one page, one score column per booth,
        // instead of the Tabs+activeSheet editor below. AC3 — this branch
        // is entered ONLY when every sheet is numericOnly, so a ride whose
        // sheets are NOT numericOnly always falls through to the unchanged
        // tabbed `else` below. #2469 — also falls through, for THIS render
        // only, while correctingSheetId is set (an operator asked to
        // correct one CONFIRMED/SIGNED booth from the grid).
        <NumericOnlyScoreGrid
          test={test}
          klass={klass}
          sheets={sheets}
          errorTiers={errorTiers}
          onPatchSheet={patchGridSheet}
          onSaveSheet={saveGridSheet}
          savingSheetId={gridSavingSheetId}
          saveErrors={gridSaveErrors}
          onSaveAll={saveAllGridSheets}
          savingAll={gridSavingAll}
          saveAllResult={gridSaveAllResult}
          onConfirmSheet={confirmGridSheet}
          confirmingSheetId={gridConfirmingSheetId}
          confirmErrors={gridConfirmErrors}
          onConfirmAll={confirmAllGridSheets}
          confirmingAll={gridConfirmingAll}
          confirmAllRan={gridConfirmAllRan}
          onCorrectSheet={(sheetId) => {
            setActiveSheetId(sheetId);
            setCorrectingSheetId(sheetId);
          }}
        />
      ) : (
        <>
          {/* #2469 — only shown when the operator arrived here via the
              grid's per-booth Correct action (never for a non-numericOnly
              ride, which has no grid to go back to). Returns to the grid;
              the tabs below stay fully usable meanwhile, including
              switching to a DIFFERENT booth, same as this editor always
              allowed. */}
          {allNumericOnly && (
            <Button
              size="small"
              color="inherit"
              data-testid="back-to-grid-button"
              onClick={() => setCorrectingSheetId(null)}
              sx={{ mb: 1 }}
            >
              {t("rideEditor.numericGrid.backToGrid")}
            </Button>
          )}
          {/* #581 — textColor="inherit" so the selected judge tab uses
              text.primary instead of the dim primary colour (light-grey on
              black, unreadable in dark mode). */}
          <Tabs
            value={activeSheetId ?? false}
            onChange={(_e, v) => setActiveSheetId(v)}
            textColor="inherit"
            indicatorColor="secondary"
            sx={{ mb: 2, mt: 2 }}
          >
            {sheets.map((s) => {
              const position = judgePosition(klass, s.judgeId);
              const label = t("rideEditor.judgeLabel", { position: position || s.judgeId });
              return <Tab key={s.id} value={s.id} label={s.completedAt ? `${label} ✓` : label} />;
            })}
          </Tabs>

          {/* #2018 — below the Judge line: each sheet's position shows that
              position's first-initial + last name, and that judge's own
              calculated percentage. Deliberately NOT a movements-table
              column or a wider MOVEMENT_TABLE_WIDTH (see #2025, which
              decided to keep the fixed 936px table and its horizontal
              scroll below ~1100px, not to widen it) — this list sits
              above the table and costs it no width.
              AC4: a mapped composite position's names come straight from
              judgeNamesForPanelEntry, which lists BOTH judges whenever
              resolveJudgeComposite says isComposite, never only the primary.

              AC2 layout: the percentage sits IMMEDIATELY beside its "Judge X"
              label, not pushed to the row's far right. The first cut used
              justifyContent="space-between"; the AC6 Chrome screenshots
              showed why that is wrong here — this Stack is full editor width,
              so the number ended up ~1000px from the label it belongs to and
              read as an unrelated figure floating at the edge rather than as
              that judge's score.

              The "Judge X" label does repeat the tab strip's own label
              directly above it. That is deliberate, not an accidental
              duplicate: the tabs are a SELECTOR for the one active sheet
              (only its label is highlighted), while this list is a summary of
              EVERY judge on the ride at once, so each row still has to say
              which position it is about. On a single-judge ride the two
              coincide and read as a repeat; on the multi-judge rides this
              list exists for, dropping the label would leave anonymous
              name/percentage rows. */}
          <Stack spacing={1} sx={{ mb: 2 }} data-testid="judge-summary-list">
            {sheets.map((s) => {
              // A sheet's own judgeId IS the arena position on production
              // data (docs/score/fvd-data-model.md:236); a data-drift sheet
              // with none falls back to "?" so the label interpolates a
              // visible placeholder instead of the literal word "undefined".
              const positionLabel = s.judgeId || "?";
              const panelEntry = judgePanel.find((p) => p.position === s.judgeId) ?? null;
              const names = judgeNamesForPanelEntry(panelEntry, judgeResolutions);
              const percentage = displaySheetPercentage(klass, tests, sheets, s);
              return (
                <Box key={s.id} data-testid="judge-summary-row">
                  <Stack direction="row" alignItems="baseline" spacing={1.5}>
                    <Typography
                      variant="subtitle2"
                      color="text.primary"
                      data-testid="judge-summary-label"
                    >
                      {t("rideEditor.judgeLabel", { position: positionLabel })}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      color="text.primary"
                      data-testid="judge-summary-percentage"
                    >
                      {formatPercentage(percentage)}
                    </Typography>
                  </Stack>
                  {/* AC5 — no panel entry (position unassigned/foreign to
                      this class) or no name resolved renders nothing here
                      rather than a blank line or a stray initial. */}
                  {names.length > 0 && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      data-testid="judge-summary-name"
                    >
                      {names.join(" / ")}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Stack>

          {activeSheet && (
            <Box>
              {/* #587/#1067 — legible at-a-glance state, and the entry point
                  to the audit trail (not secret — any admin can open it). A
                  CONFIRMED/SIGNED sheet has no separate unlock/reopen button
                  any more (§4.1) — editing a field starts the edit session
                  transparently; "Discard changes" is the only extra control.
                  It renders on the EDITING SESSION (activeSheet.editingStartedAt),
                  not on hasUnsavedEdits — an admin who merely clicked into a
                  field and back out (no actual change, but the sheet is still
                  in the editing state per #1064's unlock) needs a way out
                  that isn't a full Commit. */}
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
                <SheetStateChip state={sheetState} />
                <Button
                  size="small"
                  startIcon={<HistoryIcon />}
                  color="inherit"
                  onClick={() => setAuditOpen(true)}
                >
                  {t("rideEditor.viewAudit")}
                </Button>
                {sheetState !== "OPEN" && Boolean(activeSheet.editingStartedAt) && (
                  <Button
                    size="small"
                    color="inherit"
                    loading={discarding}
                    disabled={discarding}
                    data-testid="discard-draft-button"
                    onClick={handleDiscardDraft}
                  >
                    {t("rideEditor.discardChanges")}
                  </Button>
                )}
              </Stack>

              {/* AC2 — top-of-sheet banner: the sheet has unsaved edits
                  pending in the server-persisted draft. */}
              {sheetState !== "OPEN" && hasUnsavedEdits && (
                <Alert severity="warning" sx={{ mb: 2 }} data-testid="unsaved-edit-banner">
                  {t("rideEditor.unsavedEditBanner")}
                </Alert>
              )}
              {draftSyncError && (
                <Alert severity="error" sx={{ mb: 2 }} data-testid="draft-sync-error">
                  {apiErrorMessage(draftSyncError, t, "rideEditor.draftSyncError")}
                </Alert>
              )}

              {/* #1069 — §4.8: after a commit, the class's placing order may
                  have moved. `changed` unplaces the class server-side and
                  needs an explicit admin re-place (never auto); `error`
                  means the check itself couldn't run, so we can't say
                  either way — a distinct, softer notice, not silence and
                  not the same alarm as a confirmed change. changed:false
                  (or no placingImpact at all) renders nothing at all. */}
              {placingImpactNotice?.changed && (
                <Alert
                  severity="warning"
                  sx={{ mb: 2 }}
                  data-testid="placing-impact-changed-banner"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() =>
                        navigate(
                          `/placings?classId=${encodeURIComponent(placingImpactNotice.classId)}` +
                            (placingImpactNotice.showId
                              ? `&showId=${encodeURIComponent(placingImpactNotice.showId)}`
                              : ""),
                        )
                      }
                    >
                      {t("rideEditor.placingImpactChangedAction")}
                    </Button>
                  }
                >
                  {t("rideEditor.placingImpactChangedBanner")}
                </Alert>
              )}
              {placingImpactNotice?.checked === false && placingImpactNotice?.error && (
                <Alert severity="info" sx={{ mb: 2 }} data-testid="placing-impact-error-banner">
                  {t("rideEditor.placingImpactErrorBanner")}
                </Alert>
              )}

              {/* #2033 — the error-of-course step: renders only when a
                  deduction came off THIS sheet's percentage (activeErrorPct
                  > 0), below the #2018 clean judge summary above. A clean
                  ride renders neither line (AC3, unchanged from today).
                  Mirrors report-defs' competitor-score-sheet.js
                  singleSheet/sectionJudged step ("Score: {clean}% / Error of
                  course: {-Y%}") so the admin screen and the printed
                  scoresheet explain the same gap the same way (AC5). #2677 —
                  the #827 raw-score line that used to sit directly below
                  this step moved to the bottom of the sheet, below the
                  movements/collectives tables; see its new home for why. */}
              {activeCleanPct !== null && (
                <Stack spacing={0} sx={{ mb: 1 }} data-testid="error-of-course-step">
                  <Typography variant="body2" color="text.secondary" data-testid="error-of-course-score">
                    {t("rideEditor.errorOfCourseScore", { percentage: formatPercentage(activeCleanPct) })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" data-testid="error-of-course-deduction">
                    {t("rideEditor.errorOfCourseDeduction", {
                      amount: formatDeductionAmount(null, activeErrorPct),
                    })}
                  </Typography>
                </Stack>
              )}

              {/* #994 — error(s) of course / deduction(s). Silent (AC3) when
                  the sheet has neither — no empty box, no "Deductions: none"
                  line. #2677 — no longer rendered next to the raw-score line
                  (which moved to the bottom of the sheet); this list stays
                  here, directly under the clean/error-of-course summary it
                  explains. */}
              {(deductionItems.length > 0 || eliminated) && (
                <Stack spacing={0.25} sx={{ mb: 2 }} data-testid="deductions-list">
                  {deductionItems.map((item, idx) => (
                    <Typography
                      key={item.id ?? idx}
                      data-testid="deduction-item"
                      variant="body2"
                      color="text.secondary"
                    >
                      {item.movementNumber != null
                        ? t("rideEditor.deductionItemWithMovement", {
                            label: item.label,
                            movementNumber: item.movementNumber,
                            amount: item.amount,
                          })
                        : t("rideEditor.deductionItem", {
                            label: item.label,
                            amount: item.amount,
                          })}
                    </Typography>
                  ))}
                  {eliminated && (
                    <Typography
                      data-testid="ride-eliminated"
                      variant="body2"
                      color="error.main"
                      sx={{ fontWeight: 600 }}
                    >
                      {t("rideEditor.eliminatedLabel")}
                    </Typography>
                  )}
                </Stack>
              )}


              {test ? (
                <>
                  {/* #1754 / equitation-class-sheet-design.md §11 — a
                      "judge-percentage" def (sheetLayout: "class-grid", e.g.
                      the USEF DSE/DSM equitation sheets) has no movements
                      section at all (test.sections is []), so the ordinary
                      grid below rendered empty for these defs. This is the
                      single-ride admin counterpart of scribe-fe's
                      ClassGridRow (apps/scribe-fe/src/components/
                      ClassGridRow.jsx, #1750/#1752): the same six
                      observation cells, and the judge's entered Total
                      (percentage) as a real editable field instead of the
                      read-only raw-score line elsewhere on this sheet (which
                      never shows here — totalPoints/maxPoints are never set
                      for this model).
                      generalRemarks below (the sheet's Additional Tests
                      notes column) already renders for every layout
                      unchanged, so it isn't repeated here. Deliberately NOT
                      a second class grid (card #1754 SCOPE) — one ride's
                      cells, editable through the exact same OPEN/draft
                      branch every other field in this file uses. */}
                  {test.sheetLayout === "class-grid" ? (
                    <Box sx={{ mb: 3 }} data-testid="class-grid-sheet-fields">
                      <Typography variant="h6" color="text.primary" gutterBottom>
                        {t("rideEditor.observationsTitle")}
                      </Typography>
                      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                        {(test.observations ?? []).map((obs) => (
                          <TextField
                            key={obs.key}
                            size="small"
                            type="number"
                            label={`${obs.group} ${obs.label}`}
                            disabled={sheetState !== "OPEN" && draftFieldsBusy}
                            inputProps={{
                              "data-testid": `class-grid-observation-${obs.key}`,
                              style: { width: 72 },
                            }}
                            value={
                              obs.key in obsDrafts
                                ? obsDrafts[obs.key]
                                : displaySheet?.observationMarks?.[obs.key] ?? ""
                            }
                            onChange={(e) =>
                              setObsDrafts((prev) => ({ ...prev, [obs.key]: e.target.value }))
                            }
                            onBlur={(e) => commitObservationMark(obs.key, e.target.value)}
                          />
                        ))}
                      </Stack>
                      <TextField
                        label={t("rideEditor.totalLabel")}
                        type="number"
                        sx={{ mb: 1, maxWidth: 180 }}
                        disabled={sheetState !== "OPEN" && draftFieldsBusy}
                        inputProps={{ "data-testid": "class-grid-percentage-input" }}
                        value={percentageDraft !== undefined ? percentageDraft : displaySheet?.percentage ?? ""}
                        onChange={(e) => setPercentageDraft(e.target.value)}
                        onBlur={(e) => commitPercentage(e.target.value)}
                      />
                      {sheetState !== "OPEN" && draftFields && "percentage" in draftFields && (
                        <Typography
                          variant="caption"
                          color="warning.main"
                          display="block"
                          sx={{ mt: -0.5, mb: 1 }}
                          data-testid="changed-field-indicator"
                        >
                          {t("rideEditor.changedFieldWas", {
                            value:
                              activeSheet.percentage != null
                                ? activeSheet.percentage
                                : t("rideEditor.changedFieldWasBlank"),
                          })}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="h6" color="text.primary" gutterBottom sx={{ mb: 0 }}>
                      {t("rideEditor.movementsTitle")}
                    </Typography>
                    {/* #2012 AC1/AC2 — Simple/Detailed toggle, Simple by
                        default (RIDE_VIEW_MODES.SIMPLE, seeded from
                        localStorage above). */}
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={viewMode}
                      onChange={handleViewModeChange}
                      data-testid="ride-view-mode-toggle"
                    >
                      <ToggleButton value={RIDE_VIEW_MODES.SIMPLE} data-testid="ride-view-mode-simple">
                        {t("rideEditor.viewModeSimple")}
                      </ToggleButton>
                      <ToggleButton value={RIDE_VIEW_MODES.DETAILED} data-testid="ride-view-mode-detailed">
                        {t("rideEditor.viewModeDetailed")}
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>
                  <TableContainer sx={{ mb: 3 }}>
                    <Table
                      size="small"
                      sx={{ tableLayout: "fixed", width: MOVEMENT_TABLE_WIDTH }}
                      data-testid="movements-table"
                    >
                      {/* #2014 — colgroup is the ONE place table-layout:
                          fixed reads column widths from; per-cell widths
                          below are gone, this is the single source now. */}
                      <colgroup>
                        {MOVEMENT_TABLE_COLUMN_WIDTHS.map((w, i) => (
                          <col key={i} style={{ width: w }} />
                        ))}
                      </colgroup>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={stickyNumberColumnSx} data-testid="movements-table-header-number">
                            {t("rideEditor.columns.number")}
                          </TableCell>
                          <TableCell>{t("rideEditor.columns.description")}</TableCell>
                          <TableCell>{t("rideEditor.score")}</TableCell>
                          <TableCell>{t("rideEditor.remarks")}</TableCell>
                          <TableCell>{t("rideEditor.columns.deductions")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {movementsForSheet(test, klass, activeSheet, tests, sheets).map((m, idx, arr) => {
                          // #1755 AC8 — group freestyle/young-horse movements
                          // under a Technical/Artistic/Quality section header
                          // at each section boundary, using the SAME static
                          // i18n key map the scribe's ScoringForm renders
                          // (FREESTYLE_SECTION_LABEL_KEY, score-common). A
                          // standard test carries no `section` -> no headers.
                          const sectionLabelKey = m.section
                            ? FREESTYLE_SECTION_LABEL_KEY[m.section]
                            : null;
                          const showSectionHeader =
                            sectionLabelKey && m.section !== arr[idx - 1]?.section;
                          return (
                          <Fragment key={m.movementNumber}>
                            {showSectionHeader && (
                              <TableRow data-testid={`section-header-${m.section}`}>
                                <TableCell
                                  colSpan={5}
                                  sx={{ fontWeight: 700, bgcolor: "action.hover", color: "text.primary" }}
                                >
                                  {t(sectionLabelKey)}
                                </TableCell>
                              </TableRow>
                            )}
                          <TableRow data-testid={`movement-row-${m.movementNumber}`} sx={zebraRowSx(idx)}>
                            <TableCell
                              data-testid={`movement-number-cell-${m.movementNumber}`}
                              sx={stickyNumberColumnSx}
                            >
                              {m.movementNumber}
                            </TableCell>
                            <TableCell>
                              {/* #2012 AC3/AC4 — Simple shows the movement
                                  text only (no directive line), pipe-joined
                                  segments rendered inline with a spaced
                                  middle dot rather than the raw "|" the
                                  catalog stores multi-line text as (see
                                  util/rideViewMode.js). Detailed keeps
                                  today's unchanged description+directive
                                  rendering. */}
                              <Typography variant="body2" data-testid={`movement-description-${m.movementNumber}`}>
                                {isSimpleView ? formatSimpleMovementText(m.description) : m.description}
                              </Typography>
                              {!isSimpleView && m.directive && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  data-testid={`movement-directive-${m.movementNumber}`}
                                >
                                  {m.directive}
                                </Typography>
                              )}
                              {/* #2013 AC2/AC5 — annotations scoped to THIS
                                  movement (exact numeric match only, see
                                  annotationsForMovement above), Detailed-only
                                  so Simple hides them without losing the
                                  underlying data (AC5). */}
                              {!isSimpleView &&
                                annotationsForMovement(displaySheet, m.movementNumber).map((a) => (
                                  <Box
                                    key={a.id}
                                    data-testid={`annotation-${a.id}`}
                                    sx={{ mt: 0.5, p: 0.5, bgcolor: "action.hover", borderRadius: 1 }}
                                  >
                                    <Typography variant="body2" color="text.primary">
                                      {a.text}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {t("rideEditor.annotationMeta", {
                                        who: a.addedBy || "—",
                                        when: formatVenueLocalDateTime(a.addedAt) || "—",
                                      })}
                                    </Typography>
                                  </Box>
                                ))}
                              {!isSimpleView && (
                                <Button
                                  size="small"
                                  data-testid={`add-annotation-${m.movementNumber}`}
                                  onClick={() => openAnnotationDialog(m.movementNumber)}
                                >
                                  {t("rideEditor.addAnnotation")}
                                </Button>
                              )}
                            </TableCell>
                            {(() => {
                              // #1067 — a CONFIRMED/SIGNED sheet reads/writes
                              // through displaySheet (committed + pending
                              // draft) via the draft mutators; OPEN keeps the
                              // original direct-edit path unchanged.
                              const draftMode = sheetState !== "OPEN";
                              const committedScore = findMovementScore(activeSheet, m.movementNumber);
                              const workingScore = findMovementScore(displaySheet, m.movementNumber);
                              const committedRemarks = findMovementRemarks(activeSheet, m.movementNumber);
                              const workingRemarks = findMovementRemarks(displaySheet, m.movementNumber);
                              const movementDraftChanged = draftMode && draftFields?.movementScores;
                              const scoreChanged = movementDraftChanged && committedScore !== workingScore;
                              const remarksChanged = movementDraftChanged && committedRemarks !== workingRemarks;
                              return (
                                <>
                                  <TableCell data-testid={`movement-score-cell-${m.movementNumber}`}>
                                    {/* #1755 AC1/AC3/AC4/AC5/AC6/AC7 — typed
                                        numeric field (no Select/dropdown),
                                        snapping to this mark's own step on
                                        blur, showing a display-only ×N badge
                                        for a coefficient>1 movement, and
                                        rejecting an out-of-range/unparseable
                                        value visibly instead of silently
                                        coercing it (strict). Shared with the
                                        scribe's ScoringForm via common-fe. */}
                                    <ScoreEntry
                                      value={workingScore}
                                      disabled={draftMode && draftFieldsBusy}
                                      coefficient={m.coefficient ?? 1}
                                      step={scoreStepFor(test, m.section)}
                                      strict
                                      invalidLabel={t("rideEditor.scoreInvalid")}
                                      onValidityChange={markScoreValidity(
                                        `movement-${m.movementNumber}`,
                                      )}
                                      onChange={(score) => {
                                        const raw = score === undefined ? "" : score;
                                        draftMode
                                          ? handleDraftMovementScoreChange(m.movementNumber, raw)
                                          : handleMovementScoreChange(m.movementNumber, raw);
                                      }}
                                    />
                                    {/* AC2 — changed-but-uncommitted field indicator: old value -> new. */}
                                    {scoreChanged && (
                                      <Typography
                                        variant="caption"
                                        color="warning.main"
                                        display="block"
                                        data-testid="changed-field-indicator"
                                      >
                                        {t("rideEditor.changedFieldWas", {
                                          value: committedScore ?? "—",
                                        })}
                                      </Typography>
                                    )}
                                    {/* #2016 AC1/AC3/AC4 — a scored zero gets
                                        an icon + text-labeled Chip, not a
                                        color-only cue (AC4), and reads
                                        workingScore (displaySheet) so it
                                        never disagrees with the number shown
                                        above it. isScoredZero excludes
                                        undefined/null/"" so an unscored cell
                                        is never flagged (AC3). maxWidth
                                        matches the #2014 deductions-chip
                                        fix, same fixed-column-width column. */}
                                    {isScoredZero(workingScore) && (
                                      <Tooltip title={t("rideEditor.scoredZero")}>
                                        <Chip
                                          size="small"
                                          icon={<ReportProblemIcon fontSize="small" />}
                                          label={t("rideEditor.scoredZero")}
                                          color="error"
                                          variant="outlined"
                                          sx={{ mt: 0.5, maxWidth: "100%" }}
                                          data-testid={`movement-zero-highlight-${m.movementNumber}`}
                                        />
                                      </Tooltip>
                                    )}
                                  </TableCell>
                                  <TableCell data-testid={`movement-remarks-cell-${m.movementNumber}`}>
                                    {/* #1997 — sx matches the row's own
                                        body2 typography (see remarksInputSx
                                        above). Stays single-line (no
                                        `multiline`): MUI's multiline mode
                                        always renders a second, aria-hidden
                                        shadow <textarea> for autosize
                                        (TextareaAutosize, unconditional on
                                        `multiline`), which this suite's
                                        widespread `"input[type=text],
                                        textarea").last()`/`.first()` pattern
                                        cannot distinguish from the real one
                                        (proven: it broke ~15 unrelated
                                        ride-editor.cy.js tests when tried).
                                        Fixing that is a real, wider design
                                        change, not this small card's scope.
                                        #2044 — instead, the Tooltip below
                                        surfaces the full remark on hover
                                        (title="" is a no-op, so an empty
                                        remark shows nothing), matching the
                                        textOverflow: ellipsis added to
                                        remarksInputSx above. */}
                                    <Tooltip title={workingRemarks}>
                                      <TextField
                                        size="small"
                                        fullWidth
                                        sx={remarksInputSx}
                                        disabled={draftMode && draftFieldsBusy}
                                        value={workingRemarks}
                                        onChange={(e) =>
                                          draftMode
                                            ? setDraftMovementScore(m.movementNumber, { remarks: e.target.value })
                                            : setMovementScore(m.movementNumber, { remarks: e.target.value })
                                        }
                                        onBlur={() => draftMode && pushDraftFields(draftFields, { immediate: true })}
                                      />
                                    </Tooltip>
                                    {remarksChanged && (
                                      <Typography
                                        variant="caption"
                                        color="warning.main"
                                        display="block"
                                        data-testid="changed-field-indicator"
                                      >
                                        {t("rideEditor.changedFieldWas", {
                                          value: committedRemarks || t("rideEditor.changedFieldWasBlank"),
                                        })}
                                      </Typography>
                                    )}
                                  </TableCell>
                                  {/* #1068 — movementScores[].deductions
                                      (#209/#922), previously read-only via
                                      buildDeductionItems above. Same
                                      add/remove/struck-through-on-removal
                                      pattern as the sheet-level Errors /
                                      Deductions section below. */}
                                  <TableCell data-testid={`movement-deductions-cell-${m.movementNumber}`}>
                                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
                                      {findMovementDeductions(displaySheet, m.movementNumber).map((d) => {
                                        const label = `${d.reason}: ${formatDeductionAmount(d.points)}`;
                                        return (
                                          // #2014 — the known trap from #2012's judge: a Chip's
                                          // label is nowrap and won't shrink on its own, so an
                                          // admin-typed reason long enough to exceed the fixed
                                          // deductions column would blow the column out under
                                          // table-layout: fixed (visually overflowing instead of
                                          // widening it). maxWidth caps the Chip at the column's
                                          // available width so MUI's built-in label ellipsis takes
                                          // over instead of overflowing; the Tooltip keeps the full
                                          // reason readable on hover rather than silently lost.
                                          <Tooltip key={d.id} title={label}>
                                            <Chip
                                              size="small"
                                              label={label}
                                              color="error"
                                              variant="outlined"
                                              sx={{ maxWidth: "100%" }}
                                              onDelete={() =>
                                                draftMode
                                                  ? removeDraftMovementDeduction(m.movementNumber, d.id)
                                                  : removeMovementDeduction(m.movementNumber, d.id)
                                              }
                                            />
                                          </Tooltip>
                                        );
                                      })}
                                      {draftMode &&
                                        draftFields?.movementScores &&
                                        findMovementDeductions(activeSheet, m.movementNumber)
                                          .filter(
                                            (d) =>
                                              !findMovementDeductions(displaySheet, m.movementNumber).some(
                                                (w) => w.id === d.id,
                                              ),
                                          )
                                          .map((d) => (
                                            <Tooltip key={d.id} title={`${d.reason}: ${formatDeductionAmount(d.points)}`}>
                                              <Chip
                                                size="small"
                                                label={`${d.reason}: ${formatDeductionAmount(d.points)}`}
                                                color="default"
                                                variant="outlined"
                                                data-testid="changed-field-indicator"
                                                sx={{
                                                  maxWidth: "100%",
                                                  textDecoration: "line-through",
                                                  color: "text.secondary",
                                                }}
                                              />
                                            </Tooltip>
                                          ))}
                                      <IconButton
                                        size="small"
                                        data-testid={`add-movement-deduction-${m.movementNumber}`}
                                        disabled={draftMode && draftFieldsBusy}
                                        onClick={() => {
                                          setDeductionReasonValue("");
                                          setDeductionPointsValue("");
                                          setDeductionDialogMovement(m.movementNumber);
                                        }}
                                      >
                                        <AddIcon fontSize="small" />
                                      </IconButton>
                                    </Stack>
                                  </TableCell>
                                </>
                              );
                            })()}
                          </TableRow>
                          </Fragment>
                          );
                        })}
                        {/* #2015 AC1/AC3 — subtotal row directly after the
                            last test movement, reading score-common's own
                            weightedMovementTotal against the sheet as
                            actually displayed (displaySheet: draft-merged
                            when editing, matching every cell above it) so
                            a partially-scored sheet's subtotal is simply
                            the sum of what's entered so far (AC6) — never a
                            local re-sum, and "subtotal" (not "Total", which
                            is reserved for the sheet's own frozen total
                            above) so it never reads as final. */}
                        <TableRow data-testid="movements-subtotal-row">
                          <TableCell colSpan={2}>
                            <Typography variant="body2" color="text.primary" sx={{ fontWeight: 700 }}>
                              {t("rideEditor.movementsSubtotal")}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              data-testid="movements-subtotal-value"
                              variant="body2"
                              color="text.primary"
                              sx={{ fontWeight: 700 }}
                            >
                              {formatPoints(
                                weightedMovementTotal(displaySheet, test),
                                test,
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell colSpan={2} />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* #2013 AC2 — a sheet-general note (movementNumber: null)
                      or an annotation whose movementNumber doesn't match any
                      rendered movement above (score-api validates neither)
                      would otherwise be invisible. Shown here, Detailed-only,
                      so every annotation on the sheet is visible SOMEWHERE
                      (AC5: hidden, not lost, in Simple). */}
                  {!isSimpleView && (
                    <Box sx={{ mb: 3 }} data-testid="sheet-annotations-panel">
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="h6" color="text.primary">
                          {t("rideEditor.annotationsTitle")}
                        </Typography>
                        <Button
                          size="small"
                          data-testid="add-general-annotation"
                          onClick={() => openAnnotationDialog(null)}
                        >
                          {t("rideEditor.addGeneralAnnotation")}
                        </Button>
                      </Stack>
                      {unmatchedAnnotations(displaySheet, movementsForSheet(test, klass, activeSheet, tests, sheets)).length >
                      0 ? (
                        <Stack spacing={1}>
                          {unmatchedAnnotations(displaySheet, movementsForSheet(test, klass, activeSheet, tests, sheets)).map((a) => (
                            <Box
                              key={a.id}
                              data-testid={`annotation-${a.id}`}
                              sx={{ p: 1, bgcolor: "action.hover", borderRadius: 1 }}
                            >
                              <Typography variant="body2" color="text.primary">
                                {a.movementNumber != null
                                  ? t("rideEditor.annotationScopedLabel", { movementNumber: a.movementNumber })
                                  : t("rideEditor.annotationGeneralLabel")}
                                {": "}
                                {a.text}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {t("rideEditor.annotationMeta", {
                                  who: a.addedBy || "—",
                                  when: formatVenueLocalDateTime(a.addedAt) || "—",
                                })}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {t("rideEditor.noAnnotations")}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {test.collectives?.length > 0 && (
                    <>
                      <Typography variant="h6" color="text.primary" gutterBottom>
                        {test.collectivesLabel || t("rideEditor.collectivesTitle")}
                      </Typography>
                      <TableContainer sx={{ mb: 3 }}>
                        <Table
                          size="small"
                          sx={{ tableLayout: "fixed", width: COLLECTIVE_TABLE_WIDTH }}
                          data-testid="collectives-table"
                        >
                          {/* #2014 — same colgroup approach as the movements
                              table above, same width numbers (minus the
                              deductions column collectives doesn't have) so
                              the number/text/score/remarks columns share one
                              left edge with the movements table above it. */}
                          <colgroup>
                            {COLLECTIVE_TABLE_COLUMN_WIDTHS.map((w, i) => (
                              <col key={i} style={{ width: w }} />
                            ))}
                          </colgroup>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={stickyNumberColumnSx} data-testid="collectives-table-header-number">
                                {t("rideEditor.columns.number")}
                              </TableCell>
                              <TableCell>{t("rideEditor.columns.name")}</TableCell>
                              <TableCell>{t("rideEditor.score")}</TableCell>
                              <TableCell>{t("rideEditor.remarks")}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {test.collectives.map((c, idx) => {
                              const key = String(c.movementNumber);
                              const draftMode = sheetState !== "OPEN";
                              const committedEntry = activeSheet.collectives?.[key] ?? {};
                              const entry = displaySheet.collectives?.[key] ?? {};
                              const collectiveDraftChanged = draftMode && draftFields?.collectives;
                              const scoreChanged =
                                collectiveDraftChanged && committedEntry.score !== entry.score;
                              const remarksChanged =
                                collectiveDraftChanged &&
                                (committedEntry.remarks ?? "") !== (entry.remarks ?? "");
                              const onCollectiveChange = draftMode ? setDraftCollective : setCollective;
                              return (
                                <TableRow key={key} data-testid={`collective-row-${c.movementNumber}`} sx={zebraRowSx(idx)}>
                                  <TableCell
                                    data-testid={`collective-number-cell-${c.movementNumber}`}
                                    sx={stickyNumberColumnSx}
                                  >
                                    {c.movementNumber}
                                  </TableCell>
                                  <TableCell>
                                    {/* #2012 AC3/AC4 — same Simple/Detailed
                                        treatment as the movements table
                                        above: no directive line, pipe-joined
                                        text rendered with a spaced middle
                                        dot rather than a raw "|". */}
                                    <Typography
                                      variant="body2"
                                      data-testid={`collective-name-${c.movementNumber}`}
                                    >
                                      {isSimpleView ? formatSimpleMovementText(c.name) : c.name}
                                    </Typography>
                                    {!isSimpleView && c.directive && (
                                      <Typography variant="caption" color="text.secondary">
                                        {c.directive}
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell data-testid={`collective-score-cell-${c.movementNumber}`}>
                                    {/* Collectives are ONE object server-side (#697) — a remark
                                        edit inside it is still a `collectives` mutation. #1067 —
                                        on a CONFIRMED/SIGNED sheet this now goes through the draft
                                        path, same as a movement mark. #1755 AC2/AC6/AC7 — same
                                        typed field + coefficient badge as movement marks. */}
                                    <ScoreEntry
                                      value={entry.score}
                                      disabled={draftMode && draftFieldsBusy}
                                      coefficient={c.coefficient ?? 1}
                                      step={scoreStepFor(test)}
                                      strict
                                      invalidLabel={t("rideEditor.scoreInvalid")}
                                      onValidityChange={markScoreValidity(`collective-${key}`)}
                                      onChange={(score) => onCollectiveChange(key, { score })}
                                    />
                                    {scoreChanged && (
                                      <Typography
                                        variant="caption"
                                        color="warning.main"
                                        display="block"
                                        data-testid="changed-field-indicator"
                                      >
                                        {t("rideEditor.changedFieldWas", {
                                          value: committedEntry.score ?? "—",
                                        })}
                                      </Typography>
                                    )}
                                    {/* #2016 AC2/AC3/AC4 — same treatment as
                                        the movement score cell above, reading
                                        entry.score off displaySheet. */}
                                    {isScoredZero(entry.score) && (
                                      <Tooltip title={t("rideEditor.scoredZero")}>
                                        <Chip
                                          size="small"
                                          icon={<ReportProblemIcon fontSize="small" />}
                                          label={t("rideEditor.scoredZero")}
                                          color="error"
                                          variant="outlined"
                                          sx={{ mt: 0.5, maxWidth: "100%" }}
                                          data-testid={`collective-zero-highlight-${c.movementNumber}`}
                                        />
                                      </Tooltip>
                                    )}
                                  </TableCell>
                                  <TableCell data-testid={`collective-remarks-cell-${c.movementNumber}`}>
                                    {/* #1997 — same body2 sx as the
                                        movement remarks cell above. #2044 —
                                        same hover Tooltip + ellipsis fix
                                        too, see the movement remarks cell's
                                        comment above for why. */}
                                    <Tooltip title={entry.remarks ?? ""}>
                                      <TextField
                                        size="small"
                                        fullWidth
                                        sx={remarksInputSx}
                                        disabled={draftMode && draftFieldsBusy}
                                        value={entry.remarks ?? ""}
                                        onChange={(e) => onCollectiveChange(key, { remarks: e.target.value })}
                                        onBlur={() =>
                                          draftMode && pushDraftFields(draftFields, { immediate: true })
                                        }
                                      />
                                    </Tooltip>
                                    {remarksChanged && (
                                      <Typography
                                        variant="caption"
                                        color="warning.main"
                                        display="block"
                                        data-testid="changed-field-indicator"
                                      >
                                        {t("rideEditor.changedFieldWas", {
                                          value: committedEntry.remarks || t("rideEditor.changedFieldWasBlank"),
                                        })}
                                      </Typography>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                            {/* #2015 AC2/AC3 — same subtotal treatment as
                                the movements table above, reading
                                weightedCollectiveTotal (already exported by
                                score-common for exactly this "compute the
                                same number live" use — see its own doc
                                comment) against displaySheet. */}
                            <TableRow data-testid="collectives-subtotal-row">
                              <TableCell colSpan={2}>
                                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 700 }}>
                                  {t("rideEditor.collectivesSubtotal")}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  data-testid="collectives-subtotal-value"
                                  variant="body2"
                                  color="text.primary"
                                  sx={{ fontWeight: 700 }}
                                >
                                  {formatPoints(
                                    weightedCollectiveTotal(displaySheet, test),
                                    test,
                                  )}
                                </Typography>
                              </TableCell>
                              <TableCell />
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                    </>
                  )}
                </>
              ) : (
                // #2622 — testDefAlert (testDefAlertProps above) picks a
                // distinct message/severity for a real getTest failure
                // instead of always blaming a missing test def.
                <Alert severity={testDefAlert.severity} sx={{ mb: 2 }} data-testid={testDefAlert.testId}>
                  {t(testDefAlert.key, testDefAlert.values)}
                </Alert>
              )}

              {/* #827 — raw score (earned/max points) for this judge's sheet.
                  Server-computed at complete time, so only shown once the
                  sheet has a total. text.primary keeps it legible in dark
                  mode. #1081 — both points and max go through formatPoints
                  (passed this judge's `test`) so the test-specific possible-
                  max (always a whole multiple of SCORE_MAX, e.g. 350)
                  renders with the SAME decimal count as the earned score —
                  one decimal for a standard test ("233.5 / 350.0"), two for
                  a freestyle (scoringModel "freestyle-two-section", e.g.
                  "111.22 / 200.00") — never a bare "350" that reads like a
                  stuck default. #2677 — moved here, to the BOTTOM of the
                  sheet (below the movements/collectives tables), from its
                  old spot above the movements table. Evin's report: a
                  scorer works top-to-bottom and finishes at the bottom,
                  which is where he expected the final number and where his
                  arrow pointed — putting the answer above the question is
                  why he never saw it. Decision recorded on #2677: this is
                  the ONLY raw-score line on this branch now (moved, not
                  duplicated) — the error-of-course step and deductions list
                  above stay where they are, unchanged. */}
              {typeof activeSheet.totalPoints === "number" && (
                <Typography
                  data-testid="raw-score"
                  variant="body2"
                  color="text.primary"
                  sx={{ mb: 2, fontWeight: 600 }}
                >
                  {t("rideEditor.rawScore", {
                    points: formatPoints(activeSheet.totalPoints, test),
                    max: formatPoints(activeSheet.maxPoints, test),
                    percentage: formatPercentage(activeSheet.percentage),
                  })}
                </Typography>
              )}

              {/* #1067 — CONFIRMED/SIGNED reads/writes generalRemarks through
                  the draft path; onBlur flushes immediately (rather than
                  waiting on the debounce) so a quick tab-away still
                  persists right away. */}
              <TextField
                label={t("rideEditor.generalRemarks")}
                multiline
                minRows={2}
                fullWidth
                sx={{ mb: 2 }}
                disabled={sheetState !== "OPEN" && draftFieldsBusy}
                inputProps={{ "data-testid": "general-remarks-input" }}
                value={displaySheet?.generalRemarks ?? ""}
                onChange={(e) =>
                  sheetState === "OPEN"
                    ? setActiveSheetField({ generalRemarks: e.target.value })
                    : setDraftGeneralRemarks(e.target.value)
                }
                onBlur={() =>
                  sheetState !== "OPEN" && pushDraftFields(draftFields, { immediate: true })
                }
              />
              {sheetState !== "OPEN" &&
                draftFields &&
                "generalRemarks" in draftFields && (
                  <Typography
                    variant="caption"
                    color="warning.main"
                    display="block"
                    sx={{ mt: -1.5, mb: 1.5 }}
                    data-testid="changed-field-indicator"
                  >
                    {t("rideEditor.changedFieldWas", {
                      value: activeSheet.generalRemarks || t("rideEditor.changedFieldWasBlank"),
                    })}
                  </Typography>
                )}

              <Typography variant="subtitle2" color="text.primary" gutterBottom>
                {t("rideEditor.errorsTitle")}
              </Typography>
              {/* #1068 — sheet-level errors-of-course / deductions, editable
                  the same way eliminations are: a Chip list with add/remove,
                  a struck-through Chip for a draft-removed entry, and the
                  warning-color "changed" convention used everywhere else in
                  this draft. #994's read-only summary above (deductionItems)
                  is untouched — it always reflects the committed sheet. */}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                {(displaySheet?.errors ?? []).map((e) => (
                  <Chip
                    key={e.id}
                    label={errorChipLabel(e, t)}
                    data-testid="error-chip"
                    color="error"
                    variant="outlined"
                    onDelete={() => (sheetState === "OPEN" ? removeError(e.id) : removeDraftError(e.id))}
                  />
                ))}
                {sheetState !== "OPEN" &&
                  draftFields?.errors &&
                  (activeSheet.errors ?? [])
                    .filter((e) => !(displaySheet.errors ?? []).some((w) => w.id === e.id))
                    .map((e) => (
                      <Chip
                        key={e.id}
                        label={errorChipLabel(e, t)}
                        color="default"
                        variant="outlined"
                        data-testid="changed-field-indicator"
                        sx={{ textDecoration: "line-through", color: "text.secondary" }}
                      />
                    ))}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  data-testid="add-error-button"
                  onClick={() => {
                    // #1756 - by rule unless this test has no error-of-course
                    // schedule at all (#1300 errorRule.unit "none"), in which
                    // case manual is the only thing left to offer.
                    setErrorEntryMode(
                      hasErrorConcept ? ERROR_AMOUNT_SOURCES.RULE : ERROR_AMOUNT_SOURCES.MANUAL,
                    );
                    setErrorLabelValue("");
                    setErrorAmountType("points");
                    setErrorAmountValue("");
                    setErrorMovementValue("");
                    setErrorDialogOpen(true);
                  }}
                >
                  {t("rideEditor.addError")}
                </Button>
              </Stack>

              {/* #2054 (part C, workstream scoring-truth) — Evin's item 8:
                  "Change the word 'Eliminations' to 'Term Reason'... There
                  can only be one of these per ride, so it should not be
                  plural." Label only: the chips below still list this
                  SHEET's own elimination CAUSES (DR124 grounds — Errors of
                  course, Blood, etc, docs/score/term-reason-vocabulary.md
                  §1a), a deliberately distinct, still-plural-capable axis
                  from the ride-level singular ride.terminationReason #2053
                  added (that field has no FE display surface yet — wiring
                  it up is #2058's reporting-path scope, not this card's). */}
              <Typography
                variant="subtitle2"
                color="text.primary"
                gutterBottom
                data-testid="term-reason-heading"
              >
                {t("rideEditor.termReason")}
              </Typography>
              {/* #1067 — eliminations edit through the draft path on a
                  CONFIRMED/SIGNED sheet; a removed elimination is shown
                  struck through as its own changed-field indicator rather
                  than just vanishing. */}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                {(displaySheet?.eliminations ?? []).map((e) => (
                  <Chip
                    key={e.id}
                    label={e.reason}
                    color="error"
                    variant="outlined"
                    onDelete={() =>
                      sheetState === "OPEN" ? removeElimination(e.id) : removeDraftElimination(e.id)
                    }
                  />
                ))}
                {sheetState !== "OPEN" &&
                  draftFields?.eliminations &&
                  (activeSheet.eliminations ?? [])
                    .filter((e) => !(displaySheet.eliminations ?? []).some((w) => w.id === e.id))
                    .map((e) => (
                      <Chip
                        key={e.id}
                        label={e.reason}
                        color="default"
                        variant="outlined"
                        data-testid="changed-field-indicator"
                        sx={{ textDecoration: "line-through", color: "text.secondary" }}
                      />
                    ))}
                <Button size="small" startIcon={<AddIcon />} onClick={(e) => setElimAnchor(e.currentTarget)}>
                  {t("rideEditor.addElimination")}
                </Button>
              </Stack>
              <Menu anchorEl={elimAnchor} open={Boolean(elimAnchor)} onClose={() => setElimAnchor(null)}>
                {/* #1992 — "No Show" is not a member of ELIMINATION_REASONS at
                    all (see the constant's own comment in score-common): it's
                    reachable only through scribe-fe's presiding-judge (C)
                    action, which waterfalls it onto the whole ride and raises
                    an alert on every panel sheet — neither of which a plain
                    per-sheet elimination added here does. */}
                {ELIMINATION_REASONS.map((reason) => (
                  <MenuItem
                    key={reason}
                    onClick={() => {
                      sheetState === "OPEN" ? addElimination(reason) : addDraftElimination(reason);
                      setElimAnchor(null);
                    }}
                  >
                    {reason}
                  </MenuItem>
                ))}
                <MenuItem
                  onClick={() => {
                    setElimAnchor(null);
                    setCustomReasonValue("");
                    setCustomReasonOpen(true);
                  }}
                >
                  {t("rideEditor.customReason")}
                </MenuItem>
              </Menu>

              {activeSheet.completedAt && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t("rideEditor.completedAt", {
                    date: new Date(activeSheet.completedAt).toLocaleString([], {
                      timeZone: resolveVenueTimeZone(activeShow?.venueTimezone),
                    }),
                  })}
                </Typography>
              )}

              {saveError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {saveError.message}
                </Alert>
              )}

              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                {/* #1067 — an OPEN sheet keeps the ordinary scoring Save.
                    A CONFIRMED/SIGNED sheet's marks/comments are corrected
                    through the ONE "Commit changes" action instead (§4.4) —
                    the old #587 comment-only Save has no place here since
                    Commit now covers marks too, with no fresh signature. This
                    is independent of Complete & Sign below: a CONFIRMED sheet
                    that hasn't been signed yet still needs that action to
                    attach the judge's signature for the first time — that's
                    a different lifecycle step than correcting an in-place
                    value, so both can render together on a CONFIRMED sheet. */}
                {sheetState === "OPEN" ? (
                  <Button
                    variant="contained"
                    loading={savingSheet}
                    disabled={hasInvalidScore}
                    onClick={handleSave}
                    data-testid="save-sheet-button"
                  >
                    {t("rideEditor.save")}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    disabled={draftFieldsBusy || hasInvalidScore}
                    onClick={openCommitDialog}
                    data-testid="commit-changes-button"
                  >
                    {t("rideEditor.commitChanges")}
                  </Button>
                )}
                {!activeSheet.completedAt && (
                  <Button
                    variant="outlined"
                    // #1755 continuation — driver decision B: this is the
                    // irreversible path (freezes + signs the sheet), so it
                    // gets the same hasInvalidScore gate as Save/commit
                    // above rather than letting an admin sign over a cell
                    // still showing a strict-mode rejection.
                    disabled={hasMissingSignature || hasInvalidScore}
                    onClick={openSignDialog}
                    data-testid="complete-sign-button"
                  >
                    {t("rideEditor.completeSign")}
                  </Button>
                )}
              </Stack>

              {/* #850 — Complete & Sign adopts the class judge's show-wide
                  signature (#733); block it entirely, with a link to the
                  Judges page, while any judge assigned to this class has no
                  signature captured yet (mirrors scribe-fe ScoringForm's
                  hasMissingSignature alert, #733). */}
              {!activeSheet.completedAt && hasMissingSignature && (
                <Alert
                  severity="error"
                  sx={{ mt: 2 }}
                  action={
                    <Button color="inherit" size="small" onClick={() => navigate("/judges")}>
                      {t("rideEditor.goToJudges")}
                    </Button>
                  }
                >
                  {t("rideEditor.missingJudgeSignature")}
                </Alert>
              )}

              {/* #735 — signature box at the bottom of the sheet, once
                  signed. Shared with scribe-fe via common-fe so both surfaces
                  render identically. #2412 — a noSignatureNeeded sheet has no
                  e-signature to fetch or show (an empty box there would read
                  as a bug), so it gets the same "signed on paper original"
                  line as the printed sheet (competitor-score-sheet.template.js)
                  instead. */}
              {activeSheet.completedAt && !activeSheet.noSignatureNeeded && (
                <SignatureBox
                  judgeName={sheetSignature?.judgeName}
                  url={sheetSignature?.url}
                  loading={signatureLoading}
                  label={t("rideEditor.signatureBoxLabel")}
                />
              )}
              {activeSheet.completedAt && activeSheet.noSignatureNeeded && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  align="center"
                  data-testid="signed-on-paper-original"
                  sx={{ display: "block", mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}
                >
                  {t("rideEditor.signedOnPaperOriginal")}
                </Typography>
              )}
            </Box>
          )}
        </>
      )}

      <Dialog open={customReasonOpen} onClose={() => setCustomReasonOpen(false)}>
        <DialogTitle>{t("rideEditor.customReason")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            sx={{ mt: 1 }}
            label={t("rideEditor.customReasonPrompt")}
            value={customReasonValue}
            onChange={(e) => setCustomReasonValue(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setCustomReasonOpen(false)}>
            {t("rideEditor.customReasonCancel")}
          </Button>
          <Button
            variant="contained"
            disabled={!customReasonValue.trim()}
            onClick={() => {
              if (sheetState === "OPEN") addElimination(customReasonValue.trim());
              else addDraftElimination(customReasonValue.trim());
              setCustomReasonOpen(false);
            }}
          >
            {t("rideEditor.customReasonAdd")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* #1068 — errors/deductions are always a free-form entry (unlike
          eliminations' preset-reason menu): a label, a points-or-percent
          amount (score-common's errors[] carries exactly one of the two, per
          the test's error scheme), and an optional movement to attach it to
          — matching what a scribe can enter via ScoringForm's deduction
          dialog and error-tier picker (apps/scribe-fe), without reproducing
          the live catalog-tier auto-resolution (out of scope for correcting
          an already-scored sheet after the fact; the RESULT — a labeled
          points/percent deduction — is what scribe parity is about here). */}
      <Dialog open={errorDialogOpen} onClose={() => setErrorDialogOpen(false)}>
        <DialogTitle>{t("rideEditor.errorDialogTitle")}</DialogTitle>
        <DialogContent>
          {/* #1756 - the deduction resolves itself by default. The typed
              amount is still here, one radio away, because this surface is
              an admin escape hatch and the point of it is recording what
              the schedule would not produce (AC5) - but it is now the
              exception, and it stamps amountSource "manual" so the audit
              record says which one this was. */}
          <FormControl sx={{ mt: 1, mb: 2 }}>
            <FormLabel>{t("rideEditor.errorEntryModeLabel")}</FormLabel>
            <RadioGroup
              row
              value={errorEntryMode}
              onChange={(e) => setErrorEntryMode(e.target.value)}
            >
              <FormControlLabel
                value={ERROR_AMOUNT_SOURCES.RULE}
                disabled={!hasErrorConcept}
                control={<Radio color="warning" inputProps={{ "data-testid": "error-mode-rule" }} />}
                label={t("rideEditor.errorEntryModeRule")}
              />
              <FormControlLabel
                value={ERROR_AMOUNT_SOURCES.MANUAL}
                control={
                  <Radio color="warning" inputProps={{ "data-testid": "error-mode-manual" }} />
                }
                label={t("rideEditor.errorEntryModeManual")}
              />
            </RadioGroup>
          </FormControl>
          {errorEntryMode === ERROR_AMOUNT_SOURCES.RULE ? (
            <Typography
              variant="body2"
              color="text.primary"
              data-testid="error-rule-preview"
              sx={{ mb: 2 }}
            >
              {hasErrorConcept
                ? ruleOutcome.eliminate
                  ? t("rideEditor.errorRulePreviewEliminate", { label: ruleOutcome.label })
                  : t("rideEditor.errorRulePreview", {
                      label: ruleOutcome.label,
                      amount: formatDeductionAmount(ruleOutcome.points, ruleOutcome.percent),
                    })
                : t("rideEditor.errorRuleNoSchedule")}
            </Typography>
          ) : (
            <>
          <TextField
            autoFocus
            fullWidth
            sx={{ mb: 2 }}
            label={t("rideEditor.errorLabelPrompt")}
            value={errorLabelValue}
            onChange={(e) => setErrorLabelValue(e.target.value)}
            inputProps={{ "data-testid": "error-label-input" }}
          />
          <FormControl sx={{ mb: 2 }}>
            <FormLabel>{t("rideEditor.errorAmountTypeLabel")}</FormLabel>
            <RadioGroup
              row
              value={errorAmountType}
              onChange={(e) => setErrorAmountType(e.target.value)}
            >
              <FormControlLabel
                value="points"
                control={<Radio color="warning" />}
                label={t("rideEditor.errorAmountTypePoints")}
              />
              <FormControlLabel
                value="percent"
                control={<Radio color="warning" />}
                label={t("rideEditor.errorAmountTypePercent")}
              />
            </RadioGroup>
          </FormControl>
          <TextField
            fullWidth
            type="number"
            sx={{ mb: 2 }}
            label={t("rideEditor.errorAmountLabel")}
            value={errorAmountValue}
            onChange={(e) => setErrorAmountValue(e.target.value)}
            inputProps={{ "data-testid": "error-amount-input", min: 0, step: 0.1 }}
          />
            </>
          )}
          <Select
            fullWidth
            displayEmpty
            value={errorMovementValue}
            onChange={(e) => setErrorMovementValue(e.target.value)}
            inputProps={{ "data-testid": "error-movement-select" }}
          >
            <MenuItem value="">
              <em>{t("rideEditor.errorMovementNone")}</em>
            </MenuItem>
            {test &&
              movementsForSheet(test, klass, activeSheet, tests, sheets).map((m) => (
                <MenuItem key={m.movementNumber} value={String(m.movementNumber)}>
                  {m.movementNumber}. {formatSimpleMovementText(m.description)}
                </MenuItem>
              ))}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setErrorDialogOpen(false)}>
            {t("rideEditor.errorDialogCancel")}
          </Button>
          <Button
            variant="contained"
            disabled={
              // #1756 - nothing to validate in rule mode: there is nothing to
              // type. The only way it can't proceed is a sheet with no
              // error-of-course schedule at all, and the radio is already
              // disabled for that, so this just refuses the impossible.
              errorEntryMode === ERROR_AMOUNT_SOURCES.RULE
                ? !hasErrorConcept
                : !errorLabelValue.trim() ||
              errorAmountValue === "" ||
              Number.isNaN(Number(errorAmountValue)) ||
              // #1068 fix — score-common's errors[] is z.number().nonnegative()
              // for both points and percent (percent additionally capped at
              // 100 here as a sanity bound); a value outside that range used
              // to pass this check, then 400 on the draft PUT — common-fe's
              // interceptor rewrites that to a generic "Server Error" and the
              // chip stays in local draftFields regardless, so the entry
              // silently vanishes at commit (the server draft never got it).
              Number(errorAmountValue) < 0 ||
              (errorAmountType === "percent" && Number(errorAmountValue) > 100)
            }
            data-testid="error-dialog-add"
            onClick={() => {
              const movementNumber =
                errorMovementValue === "" ? null : Number(errorMovementValue);
              // #1756 - rule mode takes the label AND the amount from the
              // schedule (nextRuleError), so an admin cannot enter a
              // different deduction than the scribe would have for the same
              // occurrence. Manual mode is unchanged from #1068 except that
              // it now stamps itself as an override.
              // #1831 - rule mode at the eliminate tier adds an
              // eliminations[] entry (fromError:true), not an errors[] one,
              // matching scribe-fe's recordError.
              if (errorEntryMode === ERROR_AMOUNT_SOURCES.RULE && ruleOutcome.eliminate) {
                const extra = { fromError: true, movementNumber };
                if (sheetState === "OPEN") addElimination(ruleOutcome.label, extra);
                else addDraftElimination(ruleOutcome.label, extra);
                setErrorDialogOpen(false);
                return;
              }
              const entry =
                errorEntryMode === ERROR_AMOUNT_SOURCES.RULE
                  ? { ...ruleOutcome, movementNumber }
                  : {
                      label: errorLabelValue.trim(),
                      movementNumber,
                      amountSource: ERROR_AMOUNT_SOURCES.MANUAL,
                      ...(errorAmountType === "percent"
                        ? { percent: Number(errorAmountValue) }
                        : { points: Number(errorAmountValue) }),
                    };
              if (sheetState === "OPEN") addError(entry);
              else addDraftError(entry);
              setErrorDialogOpen(false);
            }}
          >
            {t("rideEditor.errorDialogAdd")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* #1068 — per-movement deduction dialog: reason + strictly-positive
          points (movementScoreSchema's deductions has no percent option,
          unlike the sheet-level errors dialog above). */}
      <Dialog open={deductionDialogMovement !== null} onClose={() => setDeductionDialogMovement(null)}>
        <DialogTitle>{t("rideEditor.movementDeductionDialogTitle")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            sx={{ mt: 1, mb: 2 }}
            label={t("rideEditor.movementDeductionReasonLabel")}
            value={deductionReasonValue}
            onChange={(e) => setDeductionReasonValue(e.target.value)}
            inputProps={{ "data-testid": "movement-deduction-reason-input" }}
          />
          <TextField
            fullWidth
            type="number"
            label={t("rideEditor.movementDeductionPointsLabel")}
            value={deductionPointsValue}
            onChange={(e) => setDeductionPointsValue(e.target.value)}
            inputProps={{ "data-testid": "movement-deduction-points-input", min: 0.1, step: 0.1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setDeductionDialogMovement(null)}>
            {t("rideEditor.errorDialogCancel")}
          </Button>
          <Button
            variant="contained"
            disabled={
              !deductionReasonValue.trim() ||
              deductionPointsValue === "" ||
              Number.isNaN(Number(deductionPointsValue)) ||
              // score-common's movementScoreSchema requires points strictly
              // positive (z.number().positive()) — 0 or negative 400s the
              // same way an out-of-range sheet-level error amount does.
              Number(deductionPointsValue) <= 0
            }
            data-testid="movement-deduction-dialog-add"
            onClick={() => {
              const entry = { reason: deductionReasonValue.trim(), points: Number(deductionPointsValue) };
              if (sheetState === "OPEN") addMovementDeduction(deductionDialogMovement, entry);
              else addDraftMovementDeduction(deductionDialogMovement, entry);
              setDeductionDialogMovement(null);
            }}
          >
            {t("rideEditor.errorDialogAdd")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* #2013 — one shared dialog for both a movement-scoped annotation
          (annotationDialogMovement is a number) and a sheet-general note
          (annotationDialogMovement is null); `undefined` is closed. Always
          enabled regardless of sheetState (AC6) -- this hits the dedicated
          POST /scoresheet/:id/annotations route directly, never the
          draft/commit ceremony the score cells above go through. */}
      <Dialog
        open={annotationDialogMovement !== undefined}
        onClose={() => !annotationSubmitting && setAnnotationDialogMovement(undefined)}
      >
        <DialogTitle>
          {annotationDialogMovement === null
            ? t("rideEditor.addGeneralAnnotationDialogTitle")
            : t("rideEditor.addAnnotationDialogTitle", { movementNumber: annotationDialogMovement })}
        </DialogTitle>
        <DialogContent>
          {annotationError && (
            <Alert severity="error" sx={{ mb: 2 }} data-testid="annotation-error">
              {annotationError}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            sx={{ mt: 1 }}
            label={t("rideEditor.annotationTextLabel")}
            value={annotationTextValue}
            onChange={(e) => setAnnotationTextValue(e.target.value)}
            disabled={annotationSubmitting}
            helperText={t("rideEditor.annotationCharsRemaining", {
              count: ANNOTATION_TEXT_MAX - annotationTextValue.length,
            })}
            inputProps={{ maxLength: ANNOTATION_TEXT_MAX, "data-testid": "annotation-text-input" }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            disabled={annotationSubmitting}
            onClick={() => setAnnotationDialogMovement(undefined)}
          >
            {t("rideEditor.errorDialogCancel")}
          </Button>
          <Button
            variant="contained"
            data-testid="annotation-dialog-add"
            disabled={!annotationTextValue.trim() || annotationSubmitting}
            onClick={submitAnnotation}
          >
            {t("rideEditor.addAnnotation")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={signOpen} onClose={() => !completing && setSignOpen(false)}>
        <DialogTitle>{t("rideEditor.signTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{t("rideEditor.signBody")}</DialogContentText>
          {/* #850 — no per-sheet signature pad anymore; the outer Complete &
              Sign button is already disabled while hasMissingSignature is
              true, so by the time this dialog is reachable the sheet's
              class judge(s) all have a show-wide signature on file. */}
          {completeError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {completeError.message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setSignOpen(false)} disabled={completing}>
            {t("rideEditor.signCancel")}
          </Button>
          <Button
            variant="contained"
            loading={completing}
            onClick={handleCompleteAndSign}
          >
            {t("rideEditor.signConfirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* #1067 — the single "Commit changes" confirm step (§4.4/§4.5): a
          short reason, the real repercussion warning (this changes a result
          competitors may have seen), and an opt-in resend checkbox defaulting
          off. Deliberately does NOT mention the signature — committing never
          requires or clears one (§4.2). */}
      <Dialog
        open={commitOpen}
        onClose={() => !committing && setCommitOpen(false)}
        data-testid="commit-dialog"
      >
        <DialogTitle>{t("rideEditor.commitDialogTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{t("rideEditor.commitDialogBody")}</DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label={t("rideEditor.commitReasonLabel")}
            placeholder={t("rideEditor.commitReasonPlaceholder")}
            value={commitReasonDraft}
            onChange={(e) => setCommitReasonDraft(e.target.value)}
            data-testid="commit-reason-input"
          />
          <FormControlLabel
            sx={{ mt: 1, display: "flex" }}
            control={
              <Checkbox
                checked={commitResend}
                disabled={!rider?.email}
                onChange={(e) => setCommitResend(e.target.checked)}
                data-testid="commit-resend-checkbox"
              />
            }
            label={t("rideEditor.commitResendLabel")}
          />
          {/* #1067 — ticking resend for a rider with no email on file would
              otherwise silently send nothing; the checkbox is disabled with
              an explanation instead. */}
          {!rider?.email && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              data-testid="commit-resend-no-email"
            >
              {t("rideEditor.commitResendNoEmail")}
            </Typography>
          )}
          {resendAfterCommitError && (
            <Alert severity="warning" sx={{ mt: 2 }} data-testid="commit-resend-error">
              {t("rideEditor.commitResendError")}
            </Alert>
          )}
          {commitConflict && (
            <Alert
              severity="warning"
              sx={{ mt: 2 }}
              data-testid="commit-conflict-alert"
              action={
                <Button color="inherit" size="small" onClick={reloadAfterConflict}>
                  {t("rideEditor.commitConflictReload")}
                </Button>
              }
            >
              {t("rideEditor.commitConflictMessage")}
            </Alert>
          )}
          {commitError && !commitConflict && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {apiErrorMessage(commitError, t, "rideEditor.commitError")}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setCommitOpen(false)} disabled={committing}>
            {t("rideEditor.commitCancel")}
          </Button>
          <Button
            variant="contained"
            loading={committing}
            disabled={!commitReasonDraft.trim() || committing}
            onClick={confirmCommit}
            data-testid="commit-confirm-button"
          >
            {t("rideEditor.commitConfirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AC5 — navigating away (the "Back to picker" button) while a draft
          has unsaved edits prompts this confirm dialog. */}
      <Dialog
        open={leaveConfirmOpen}
        onClose={() => setLeaveConfirmOpen(false)}
        data-testid="leave-confirm-dialog"
      >
        <DialogTitle>{t("rideEditor.leaveConfirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("rideEditor.leaveConfirmBody")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setLeaveConfirmOpen(false)}>
            {t("rideEditor.leaveConfirmStay")}
          </Button>
          <Button
            variant="contained"
            color="warning"
            data-testid="leave-confirm-button"
            onClick={() => {
              setLeaveConfirmOpen(false);
              goBackToPicker();
            }}
          >
            {t("rideEditor.leaveConfirmGo")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resendOpen} onClose={() => !resending && setResendOpen(false)}>
        <DialogTitle>{t("rideEditor.resendDialogTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{t("rideEditor.resendDialogBody")}</DialogContentText>
          <TextField
            autoFocus
            fullWidth
            type="email"
            label={t("rideEditor.resendEmailLabel")}
            data-testid="resend-email-input"
            value={resendEmail}
            onChange={(e) => {
              setResendEmail(e.target.value);
              setResendSuccess(false);
            }}
          />
          {resendError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {resendErrorMessage(resendError)}
            </Alert>
          )}
          {resendSuccess && (
            <Alert severity="success" sx={{ mt: 2 }} data-testid="resend-success">
              {t("rideEditor.resendSuccess")}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setResendOpen(false)} disabled={resending}>
            {t("rideEditor.resendCancel")}
          </Button>
          <Button
            variant="contained"
            loading={resending}
            disabled={!resendEmail.trim() || resending}
            data-testid="resend-confirm-button"
            onClick={confirmResend}
          >
            {t("rideEditor.resendConfirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* #928 — blocks the score-sheet report for a two-judge-section class
          missing a section's judge assignment. #2413 — "Print Anyway" now
          runs the same in-place print flow with overrideIncompleteJudges:true
          (usePrintScoreSheet.js) instead of resending a /reports deep link,
          so the report still stamps the rendered sheet as incomplete rather
          than silently omitting the missing judge. */}
      <Dialog open={judgesBlockOpen} onClose={() => setJudgesBlockOpen(false)} data-testid="incomplete-judges-dialog">
        <DialogTitle>{t("rideEditor.incompleteJudgesDialogTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("rideEditor.incompleteJudgesDialogBody", {
              positions: missingJudgeSections.map((s) => s.position).join(", "),
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setJudgesBlockOpen(false)}>
            {t("rideEditor.incompleteJudgesCancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            data-testid="incomplete-judges-override-button"
            onClick={() => {
              setJudgesBlockOpen(false);
              printScoreSheet({ showId: ride.showId, rideId, overrideIncompleteJudges: true });
            }}
          >
            {t("rideEditor.incompleteJudgesOverride")}
          </Button>
        </DialogActions>
      </Dialog>

      {activeSheet && (
        <SheetAuditDialog
          open={auditOpen}
          onClose={() => setAuditOpen(false)}
          rideId={rideId}
          sheetId={activeSheet.id}
          // #1081 — the audit trail response only carries testId (see
          // score-api's handlers/audit.js), not the test def itself, so the
          // dialog can't resolve scoringModel on its own; pass the already-
          // resolved test object down so its raw-score lines can format
          // through the same test-aware formatPoints as the readout above.
          test={test}
        />
      )}
    </Box>
  );
}
